import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  BarChart3,
  Bell,
  Bot,
  Building2,
  CalendarClock,
  Camera,
  Car,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Clock,
  CreditCard,
  Download,
  Eye,
  FileText,
  Globe2,
  Lock,
  LogOut,
  MapPin,
  Menu,
  MessageCircle,
  MessageSquare,
  Moon,
  Navigation,
  Package,
  Paintbrush,
  Phone,
  RefreshCw,
  ReceiptText,
  Search,
  Send,
  Settings,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Sparkles,
  Sun,
  Ticket,
  Upload,
  User,
  Users,
  Volume2,
  WalletCards,
  X,
  Zap,
} from 'lucide-react';
import Sales from './Sales';
import Inventory from './Inventory';
import Payments from './Payments';
import Renewals from './Renewals';
import Services from './Services';
import Reports from './Reports';
import MenuPermissions from './MenuPermissions';
import ActivityHistory from './ActivityHistory';
import DeleteHistory from './DeleteHistory';
import CorrectionRequests from './CorrectionRequests';
import DealerPanel from './DealerPanel';
import EmployeePanel from './EmployeePanel';
import CustomerPanel from './CustomerPanel';
import UserManagement from './UserManagement';
import NotificationManagement from './NotificationManagement';
import SuperAdminDashboard from './SuperAdminDashboard';
import MasterLogs from './MasterLogs';
import LicenseFooter from '../components/LicenseFooter';
import { AUTH_SESSION_CHANGED_EVENT, clearAuthSession, getCurrentUser, getImpersonationReturnUser, popImpersonationSession, setAuthSession } from '../utils/auth';
import { getBrandImageUrl, getProfileImageUrl, setBrowserBranding } from '../utils/browserBranding';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import './Dashboard.css';
import { API_URL } from '../config/api';
import { moduleCatalog } from '../config/menuCatalog';


const pageMotion = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 12 },
  transition: { duration: 0.24, ease: 'easeOut' },
};

const formatMoney = (value) => `Rs. ${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const getLocalDateKey = () => {
  const date = new Date();
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-');
};

const featureDetails = {
  'white-label': {
    title: 'White Label Builder',
    subtitle: 'Create branded CRM workspaces for every company and dealer network.',
    icon: Paintbrush,
    points: ['Upload logo', 'Theme colors and fonts', 'Custom domain and Auto SSL', 'Custom login and reports'],
  },
  support: {
    title: 'Ticket Support System',
    subtitle: 'AI assisted customer support, SLA queues and dealer issue management.',
    icon: Ticket,
    points: ['AI chatbot replies', 'Ticket priority', 'Dealer escalation', 'Support analytics'],
  },
};

const isCompanyAdmin = (role) => role === 'admin' || role === 'whitelabel_admin';
const isWorkspaceAdmin = (role) => role === 'superadmin' || role === 'admin' || role === 'whitelabel_admin';

const formatRole = (role = '') => role.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
const displayRole = (role = '', viewerRole = '') => (
  role === 'whitelabel_admin' && viewerRole !== 'superadmin' ? 'Admin' : formatRole(role)
);
const DATA_CHANGE_EVENT = 'biztrack:data-changed';
const isFormFieldFocused = () => {
  const activeElement = document.activeElement;
  return Boolean(activeElement && ['INPUT', 'TEXTAREA', 'SELECT'].includes(activeElement.tagName));
};
const presentModule = (item, viewerRole) => {
  if (viewerRole === 'customer' && item.key === 'dashboard') {
    return {
      ...item,
      title: 'Devices',
      subtitle: 'Device and service details'
    };
  }
  if (viewerRole !== 'whitelabel_admin' || item.key !== 'white-label') return item;
  return {
    ...item,
    title: 'Brand Management',
    subtitle: 'Brand profile and app generation',
    group: 'Admin Tools'
  };
};

const menuAccentPalette = [
  { keys: ['dashboard'], accent: '#2563eb', accent2: '#06b6d4', soft: 'rgba(37, 99, 235, 0.12)' },
  { keys: ['users', 'team-chat'], accent: '#0f766e', accent2: '#22c55e', soft: 'rgba(15, 118, 110, 0.12)' },
  { keys: ['inventory', 'purchase'], accent: '#0891b2', accent2: '#14b8a6', soft: 'rgba(8, 145, 178, 0.12)' },
  { keys: ['sales', 'payments', 'accounts'], accent: '#d97706', accent2: '#f97316', soft: 'rgba(217, 119, 6, 0.13)' },
  { keys: ['gps-tracking'], accent: '#16a34a', accent2: '#0ea5e9', soft: 'rgba(22, 163, 74, 0.12)' },
  { keys: ['reminders', 'renewals', 'notifications'], accent: '#7c3aed', accent2: '#ec4899', soft: 'rgba(124, 58, 237, 0.12)' },
  { keys: ['vehicle-services', 'support'], accent: '#dc2626', accent2: '#f59e0b', soft: 'rgba(220, 38, 38, 0.11)' },
  { keys: ['reports', 'activity-history', 'delete-history', 'correction-requests'], accent: '#475569', accent2: '#0ea5e9', soft: 'rgba(71, 85, 105, 0.12)' },
  { keys: ['whatsapp'], accent: '#128c7e', accent2: '#25d366', soft: 'rgba(18, 140, 126, 0.13)' },
  { keys: ['white-label', 'menu-permissions', 'user-chatbot'], accent: '#9333ea', accent2: '#2563eb', soft: 'rgba(147, 51, 234, 0.12)' }
];

const menuAccentStyle = (key) => {
  const palette = menuAccentPalette.find((item) => item.keys.includes(key)) || menuAccentPalette[0];
  return {
    '--menu-accent': palette.accent,
    '--menu-accent-2': palette.accent2,
    '--menu-soft': palette.soft
  };
};

const submenuAccentRules = [
  { tokens: ['service', 'support', 'ticket', 'expired', 'lost', 'defective'], accent: '#dc2626', accent2: '#f59e0b', soft: 'rgba(220, 38, 38, 0.11)' },
  { tokens: ['device', 'gps', 'tracking', 'map', 'route', 'imei', 'sim', 'protocol'], accent: '#16a34a', accent2: '#0ea5e9', soft: 'rgba(22, 163, 74, 0.12)' },
  { tokens: ['inventory', 'stock', 'product', 'oem', 'bulk', 'return', 'transfer', 'available', 'low'], accent: '#0891b2', accent2: '#14b8a6', soft: 'rgba(8, 145, 178, 0.12)' },
  { tokens: ['payment', 'finance', 'ledger', 'dues', 'credit', 'debit', 'wallet', 'account'], accent: '#d97706', accent2: '#f97316', soft: 'rgba(217, 119, 6, 0.13)' },
  { tokens: ['customer', 'dealer', 'employee', 'user', 'admin', 'office', 'location'], accent: '#0f766e', accent2: '#22c55e', soft: 'rgba(15, 118, 110, 0.12)' },
  { tokens: ['sales', 'sale', 'billing', 'invoice', 'revenue', 'purchase', 'vendor'], accent: '#d97706', accent2: '#f59e0b', soft: 'rgba(217, 119, 6, 0.13)' },
  { tokens: ['renewal', 'reminder', 'notification', 'alert', 'today', 'overdue'], accent: '#7c3aed', accent2: '#ec4899', soft: 'rgba(124, 58, 237, 0.12)' },
  { tokens: ['report', 'history', 'download', 'logs', 'summary', 'daily', 'monthly', 'activity'], accent: '#475569', accent2: '#0ea5e9', soft: 'rgba(71, 85, 105, 0.12)' },
  { tokens: ['whatsapp', 'chat', 'message', 'send', 'voice'], accent: '#128c7e', accent2: '#25d366', soft: 'rgba(18, 140, 126, 0.13)' },
  { tokens: ['white-label', 'brand', 'domain', 'ssl', 'android', 'ios', 'permission', 'theme'], accent: '#9333ea', accent2: '#2563eb', soft: 'rgba(147, 51, 234, 0.12)' }
];

const submenuFallbackPalette = [
  { accent: '#2563eb', accent2: '#06b6d4', soft: 'rgba(37, 99, 235, 0.12)' },
  { accent: '#0f766e', accent2: '#22c55e', soft: 'rgba(15, 118, 110, 0.12)' },
  { accent: '#d97706', accent2: '#f97316', soft: 'rgba(217, 119, 6, 0.13)' },
  { accent: '#7c3aed', accent2: '#ec4899', soft: 'rgba(124, 58, 237, 0.12)' },
  { accent: '#475569', accent2: '#0ea5e9', soft: 'rgba(71, 85, 105, 0.12)' }
];

const submenuAccentStyle = (item = {}, depth = 0) => {
  const haystack = `${item.key || ''} ${item.title || ''} ${item.subtitle || ''} ${item.group || ''}`.toLowerCase();
  const palette = submenuAccentRules.find((rule) => rule.tokens.some((token) => haystack.includes(token)))
    || submenuFallbackPalette[depth % submenuFallbackPalette.length];
  return {
    '--submenu-accent': palette.accent,
    '--submenu-accent-2': palette.accent2,
    '--submenu-soft': palette.soft
  };
};
const submenuChildren = (item) => (Array.isArray(item?.children) ? item.children : []);
const submenuHasChildren = (item) => submenuChildren(item).length > 0;
const presentSubmenuItem = (item, viewerRole) => (
  item?.key === 'users-grouped' && viewerRole === 'superadmin'
    ? { ...item, title: 'Super Admin ID' }
    : item
);
const revealPanel = (ref, focusSelector = 'input, select, textarea, button') => {
  const node = ref?.current;
  if (!node) return;
  node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const focusTarget = node.querySelector?.(focusSelector);
  if (focusTarget && !focusTarget.disabled) {
    window.setTimeout(() => focusTarget.focus({ preventScroll: true }), 180);
  }
};
const submenuMatchesSearch = (items = [], term = '', viewerRole = '') => (
  items.some((item) => {
    const displayItem = presentSubmenuItem(item, viewerRole);
    const haystack = `${displayItem.title || ''} ${displayItem.subtitle || ''} ${displayItem.key || ''}`.toLowerCase();
    return haystack.includes(term) || submenuMatchesSearch(submenuChildren(item), term, viewerRole);
  })
);
const moduleMatchesSearch = (item, viewerRole, term) => {
  if (!term) return true;
  const displayItem = presentModule(item, viewerRole);
  const haystack = `${displayItem.title || ''} ${displayItem.subtitle || ''} ${displayItem.group || ''} ${displayItem.key || ''}`.toLowerCase();
  return haystack.includes(term) || submenuMatchesSearch(displayItem.subItems || [], term, viewerRole);
};
const submenuContainsKey = (items = [], key = '') => {
  if (!key) return false;
  return items.some((item) => item.key === key || submenuContainsKey(submenuChildren(item), key));
};
const submenuPathToKey = (items = [], key = '', path = []) => {
  if (!key) return [];
  for (const item of items) {
    const nextPath = [...path, item.key];
    if (item.key === key) return nextPath;
    const childPath = submenuPathToKey(submenuChildren(item), key, nextPath);
    if (childPath.length > 0) return childPath;
  }
  return [];
};
const roleAllowsSubmenuItem = (item, role, parentRoles = []) => {
  if (role === 'superadmin') return true;
  const roles = item.strictRoles || item.roles || parentRoles;
  return !Array.isArray(roles) || roles.includes(role);
};
const filterSubmenuItems = (items = [], parentKey = '', role = '', hiddenSubmenuKeys = new Set(), parentRoles = []) => (
  items
    .map((item) => {
      const hidden = role !== 'superadmin' && hiddenSubmenuKeys.has(`${parentKey}:${item.key}`);
      if (hidden) return null;
      const roleAllowed = roleAllowsSubmenuItem(item, role, parentRoles);
      const hasChildren = submenuHasChildren(item);
      const filteredChildren = filterSubmenuItems(
        submenuChildren(item),
        parentKey,
        role,
        hiddenSubmenuKeys,
        item.roles || parentRoles
      );
      if (filteredChildren.length > 0) return { ...item, children: filteredChildren };
      if (hasChildren) return null;
      return roleAllowed ? { ...item, children: undefined } : null;
    })
    .filter(Boolean)
);
function ShellButton({ children, className = '', ...props }) {
  return <button className={`neo-button ${className}`} {...props}>{children}</button>;
}

function IconButton({ children, className = '', ...props }) {
  return <button className={`neo-icon-button ${className}`} {...props}>{children}</button>;
}

const NOTIFICATION_TOAST_AUTO_HIDE_MS = 5000;

function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [popupItem, setPopupItem] = useState(null);
  const [spokenId, setSpokenId] = useState(null);
  const [voiceReady, setVoiceReady] = useState(() => localStorage.getItem('credyou-notification-voice') === 'enabled');
  const [browserPopupReady, setBrowserPopupReady] = useState(() => (
    typeof window !== 'undefined'
    && 'Notification' in window
    && window.Notification.permission === 'granted'
  ));
  const autoShownIdRef = useRef(null);
  const popupTimerRef = useRef(null);

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications`, { headers: getHeaders() });
      setItems(res.data);
    } catch (err) {
      console.log(err);
    }
  }, [getHeaders]);

  useEffect(() => {
    fetchNotifications();
    const timer = setInterval(fetchNotifications, 60000);
    return () => clearInterval(timer);
  }, [fetchNotifications]);

  const speak = useCallback((item) => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(item.voice_text || item.message);
    utterance.lang = 'hi-IN';
    utterance.rate = 0.96;
    window.speechSynthesis.speak(utterance);
  }, []);

  const showInAppPopup = useCallback((item) => {
    setPopupItem(item);
    window.clearTimeout(popupTimerRef.current);
    popupTimerRef.current = window.setTimeout(() => setPopupItem(null), NOTIFICATION_TOAST_AUTO_HIDE_MS);
  }, []);

  const showBrowserNotification = useCallback((item) => {
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;

    const nativePopup = new Notification(item.title || 'Reminder', {
      body: item.message || 'New app reminder',
      tag: `credyou-${item.id}`,
      requireInteraction: false
    });
    window.setTimeout(() => nativePopup.close(), NOTIFICATION_TOAST_AUTO_HIDE_MS);
  }, []);

  const handleNotificationArrival = useCallback((item) => {
    setItems((current) => [item, ...current.filter((row) => Number(row.id) !== Number(item.id))].slice(0, 50));
    showInAppPopup(item);
    showBrowserNotification(item);
    if (item.voice_enabled && voiceReady) {
      speak(item);
      setSpokenId(item.id);
    }
  }, [showBrowserNotification, showInAppPopup, speak, voiceReady]);

  const enableVoiceAndPopups = async (itemToSpeak = null) => {
    localStorage.setItem('credyou-notification-voice', 'enabled');
    setVoiceReady(true);
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      setBrowserPopupReady(permission === 'granted');
    } else if ('Notification' in window) {
      setBrowserPopupReady(Notification.permission === 'granted');
    }

    const nextVoiceItem = itemToSpeak || items.find((item) => item.voice_enabled && !item.read_at);
    if (nextVoiceItem) {
      speak(nextVoiceItem);
      setSpokenId(nextVoiceItem.id);
    }
  };

  useEffect(() => {
    const socket = io(API_URL, {
      auth: { token: localStorage.getItem('token') },
      transports: ['websocket', 'polling']
    });

    socket.on('notification:new', (item) => {
      handleNotificationArrival(item);
    });

    return () => socket.disconnect();
  }, [handleNotificationArrival]);

  useEffect(() => {
    const unreadItem = items.find((item) => !item.read_at);
    if (!unreadItem || autoShownIdRef.current === unreadItem.id) return;

    autoShownIdRef.current = unreadItem.id;
    showInAppPopup(unreadItem);
    showBrowserNotification(unreadItem);
    if (unreadItem.voice_enabled && voiceReady && spokenId !== unreadItem.id) {
      speak(unreadItem);
      setSpokenId(unreadItem.id);
    }
  }, [items, showBrowserNotification, showInAppPopup, speak, spokenId, voiceReady]);

  useEffect(() => () => window.clearTimeout(popupTimerRef.current), []);

  const openPanel = () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (nextOpen) {
      fetchNotifications();
      const voiceItem = items.find((item) => item.voice_enabled && !item.read_at);
      if (voiceItem && voiceReady && spokenId !== voiceItem.id) {
        speak(voiceItem);
        setSpokenId(voiceItem.id);
      }
    }
  };

  const markRead = async (item) => {
    try {
      await axios.patch(`${API_URL}/api/notifications/${item.id}/read`, {}, { headers: getHeaders() });
      setItems((current) => current.filter((row) => Number(row.id) !== Number(item.id)));
    } catch (err) {
      console.log(err);
    }
  };

  const unreadCount = items.filter((item) => !item.read_at).length;
  const needsVoiceUnlock = items.some((item) => item.voice_enabled && !item.read_at) && !voiceReady;

  return (
    <div className="notify-root">
      <IconButton onClick={openPanel} aria-label="Notifications">
        <Bell size={18} />
        {unreadCount > 0 && <span className="live-dot" />}
      </IconButton>
      <AnimatePresence>
        {open && (
          <motion.div className="notification-panel glass-panel" initial={{ opacity: 0, y: 12, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.96 }}>
            <strong>Live notification center</strong>
            {(needsVoiceUnlock || !browserPopupReady) && (
              <div className="notification-permission-row">
                <button type="button" onClick={() => enableVoiceAndPopups()}><Volume2 size={15} /> Enable Voice & Popup</button>
              </div>
            )}
            {items.length === 0 ? (
              <div className="notification-empty">No notifications yet</div>
            ) : items.map((item) => {
              const Icon = item.type === 'renewal_daily' ? RefreshCw : Bell;
              return (
              <div className={`notification-item ${item.read_at ? '' : 'unread'}`} key={item.id} onClick={() => markRead(item)}>
                <Icon size={16} />
                <div className="notification-main">
                  <b>{item.title}</b>
                  <small>{item.message}</small>
                </div>
                {item.voice_enabled ? (
                  <button type="button" onClick={(event) => { event.stopPropagation(); speak(item); }} aria-label="Play voice notification">
                    <Volume2 size={15} />
                  </button>
                ) : null}
              </div>
            ); })}
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {popupItem && (
          <motion.div className="notification-toast glass-panel" initial={{ opacity: 0, y: 14, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.96 }}>
            <Bell size={17} />
            <div className="notification-main">
              <strong>{popupItem.title || 'Notification'}</strong>
              <small>{popupItem.message}</small>
            </div>
            <div className="notification-toast-actions">
              {popupItem.voice_enabled && (
                <button type="button" onClick={() => (voiceReady ? speak(popupItem) : enableVoiceAndPopups(popupItem))}>
                  <Volume2 size={15} />
                </button>
              )}
              <button type="button" onClick={() => setPopupItem(null)} aria-label="Close notification popup"><X size={15} /></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function RenewalDailyPopup({ open, items = [], onDismiss, onOpenDetails }) {
  const total = items.length;
  const priority = items.filter((row) => Number(row.days_left || 0) <= 15).length;
  const overdue = items.filter((row) => Number(row.days_left || 0) < 0).length;
  const today = items.filter((row) => Number(row.days_left || 0) === 0).length;
  const paymentFollowups = items.filter((row) => row.payment_status !== 'paid').length;

  const dayLabel = (row) => {
    const days = Number(row.days_left || 0);
    if (days < 0) return `${Math.abs(days)} days overdue`;
    if (days === 0) return 'Due today';
    return `${days} days left`;
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="renewal-popup-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <motion.section className="renewal-popup glass-panel" initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16, scale: 0.96 }}>
            <button type="button" className="renewal-popup-close" onClick={onDismiss} aria-label="Close renewal popup"><X size={18} /></button>
            <div className="renewal-popup-header">
              <span><CalendarClock size={22} /></span>
              <div>
                <small>Daily renewal notification</small>
                <h2>Renewal reminders due</h2>
              </div>
            </div>

            <div className="renewal-popup-stats">
              {[
                { label: '30 day', value: total },
                { label: '15 day', value: priority },
                { label: 'Today', value: today },
                { label: 'Overdue', value: overdue },
                { label: 'Payment', value: paymentFollowups },
              ].map((item) => (
                <div key={item.label}>
                  <strong>{item.value}</strong>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>

            <div className="renewal-popup-list">
              {items.slice(0, 6).map((row) => (
                <div className="renewal-popup-row" key={row.id || `${row.mobile}-${row.renewal_date}`}>
                  <div>
                    <strong>{row.name || 'Customer'}</strong>
                    <span>{row.vehicle_no || row.mobile || row.imei || '-'}</span>
                  </div>
                  <em>{dayLabel(row)}</em>
                  <b>{row.renewal_date ? formatDate(row.renewal_date) : '-'}</b>
                </div>
              ))}
            </div>

            <div className="renewal-popup-actions">
              <button type="button" onClick={onOpenDetails}><Eye size={16} /> View Details</button>
              <button type="button" onClick={onDismiss}>Close Today</button>
            </div>
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const AI_QUICK_PROMPTS = [
  'Live CRM overview',
  'Renewal priority',
  'Payment summary',
  'Sales forecast',
  'Inventory status'
];

function FloatingAssistant() {
  const [open, setOpen] = useState(false);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [cards, setCards] = useState([]);
  const [suggestions, setSuggestions] = useState(['Ask for live sales, renewals, payments, inventory, or team insight.']);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: 'BizTrack AI is active. I can read live CRM data according to your account access.'
    }
  ]);

  const addMessage = (message) => {
    setMessages((current) => [...current, message].slice(-8));
  };

  const askAssistant = async (question, options = {}) => {
    const prompt = String(question || input || '').trim();
    if (!prompt || busy) return;
    if (!options.silent) addMessage({ role: 'user', text: prompt });
    setInput('');
    setBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/workspace/ai/ask`,
        { message: prompt },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setCards(res.data.cards || []);
      setSuggestions(res.data.suggestions || []);
      addMessage({ role: 'assistant', text: res.data.reply || 'BizTrack AI is active.' });
    } catch (err) {
      addMessage({
        role: 'assistant',
        text: err.response?.data?.message || 'BizTrack AI is active, but live insight is temporarily unavailable.'
      });
    } finally {
      setBusy(false);
    }
  };

  const toggleAssistant = () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (nextOpen && !bootstrapped) {
      setBootstrapped(true);
      setTimeout(() => askAssistant('Live CRM overview', { silent: true }), 0);
    }
  };

  return (
    <div className="ai-assistant">
      <AnimatePresence>
        {open && (
          <motion.div className="ai-assistant-panel glass-panel" initial={{ opacity: 0, y: 18, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.96 }}>
            <div className="assistant-title">
              <Bot size={18} />
              <strong>BizTrack AI</strong>
              <span><i /> Active</span>
            </div>
            <div className="ai-message-list">
              {messages.map((message, index) => (
                <div className={`ai-message ${message.role}`} key={`${message.role}-${index}`}>
                  {message.text}
                </div>
              ))}
              {busy && <div className="ai-message assistant">Reading live CRM data...</div>}
            </div>
            {cards.length > 0 && (
              <div className="ai-card-grid">
                {cards.map((card) => (
                  <div className={`ai-metric-card ${card.tone || 'blue'}`} key={card.label}>
                    <span>{card.label}</span>
                    <strong>{card.value}</strong>
                  </div>
                ))}
              </div>
            )}
            <div className="ai-suggestion-list">
              {suggestions.map((item) => <span key={item}>{item}</span>)}
            </div>
            <div className="ai-quick-row">
              {AI_QUICK_PROMPTS.map((prompt) => (
                <button type="button" key={prompt} onClick={() => askAssistant(prompt)} disabled={busy}>{prompt}</button>
              ))}
            </div>
            <form className="ai-input-row" onSubmit={(event) => { event.preventDefault(); askAssistant(input); }}>
              <input value={input} onChange={(event) => setInput(event.target.value)} placeholder="Ask BizTrack AI..." />
              <button type="submit" disabled={busy || !input.trim()} aria-label="Ask BizTrack AI"><Send size={16} /></button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
      <button className="ai-fab" onClick={toggleAssistant} aria-label="AI Assistant">
        {open ? <X size={22} /> : <Bot size={24} />}
      </button>
    </div>
  );
}

function UserChatbotCenter() {
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [cards, setCards] = useState([]);
  const [suggestions, setSuggestions] = useState(['Ask about sales, renewals, payments, inventory, customers, or team status.']);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: 'User Chatbot is ready. Ask BizTrack AI for live CRM insight based on your account access.'
    }
  ]);

  const addMessage = (message) => {
    setMessages((current) => [...current, message].slice(-14));
  };

  const askAssistant = useCallback(async (question, options = {}) => {
    const prompt = String(question || input || '').trim();
    if (!prompt || busy) return;
    if (!options.silent) addMessage({ role: 'user', text: prompt });
    setInput('');
    setBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/workspace/ai/ask`,
        { message: prompt },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setCards(res.data.cards || []);
      setSuggestions(res.data.suggestions || []);
      addMessage({ role: 'assistant', text: res.data.reply || 'BizTrack AI is active.' });
    } catch (err) {
      addMessage({
        role: 'assistant',
        text: err.response?.data?.message || 'BizTrack AI is active, but live insight is temporarily unavailable.'
      });
    } finally {
      setBusy(false);
    }
  }, [busy, input]);

  useEffect(() => {
    askAssistant('Live CRM overview', { silent: true });
    // Load the first live answer once when the chatbot screen opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div className="user-chatbot-shell" {...pageMotion}>
      <section className="user-chatbot-hero glass-panel">
        <div className="user-chatbot-icon"><Bot size={28} /></div>
        <div>
          <span className="eyebrow"><Sparkles size={14} /> Active assistant</span>
          <h2>User Chatbot</h2>
          <p>Ask BizTrack AI for live CRM summaries, renewal priorities, payment follow-ups, sales status, inventory checks, and team insight.</p>
        </div>
      </section>

      <section className="user-chatbot-layout">
        <div className="user-chatbot-window glass-panel">
          <div className="user-chatbot-title">
            <strong>BizTrack AI Chat</strong>
            <span><i /> Live</span>
          </div>
          <div className="user-chatbot-messages">
            {messages.map((message, index) => (
              <div className={`user-chatbot-message ${message.role}`} key={`${message.role}-${index}`}>
                {message.text}
              </div>
            ))}
            {busy && <div className="user-chatbot-message assistant">Reading live CRM data...</div>}
          </div>
          <div className="user-chatbot-quick-row">
            {AI_QUICK_PROMPTS.map((prompt) => (
              <button type="button" key={prompt} onClick={() => askAssistant(prompt)} disabled={busy}>{prompt}</button>
            ))}
          </div>
          <form className="user-chatbot-input-row" onSubmit={(event) => { event.preventDefault(); askAssistant(input); }}>
            <input value={input} onChange={(event) => setInput(event.target.value)} placeholder="Type your question..." />
            <button type="submit" disabled={busy || !input.trim()}><Send size={17} /> Ask</button>
          </form>
        </div>

        <aside className="user-chatbot-side">
          <div className="user-chatbot-metric-grid">
            {cards.map((card) => (
              <div className={`ai-metric-card ${card.tone || 'blue'}`} key={card.label}>
                <span>{card.label}</span>
                <strong>{card.value}</strong>
              </div>
            ))}
          </div>
          <div className="user-chatbot-suggestions glass-panel">
            <strong>Suggested Actions</strong>
            {suggestions.map((item) => <span key={item}>{item}</span>)}
          </div>
        </aside>
      </section>
    </motion.div>
  );
}

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <motion.div className="stat-card glass-panel" style={{ '--accent': color }} whileHover={{ y: -4 }}>
      <div className="stat-icon"><Icon size={18} /></div>
      <p>{label}</p>
      <strong>{value}</strong>
      <span>Live counter</span>
    </motion.div>
  );
}

function SubmenuEntry({
  subItem,
  depth,
  parentPath,
  viewerRole,
  active,
  activeSubmenu,
  openSubmenuKeys,
  hoveredSubmenuKey,
  setHoveredSubmenuKey,
  handleSubmenuClick,
  submenuAccentStyle,
  renderSubmenuItems,
  showGroupLabel,
  groupLabel
}) {
  const displaySubItem = presentSubmenuItem(subItem, viewerRole);
  const children = submenuChildren(subItem);
  const hasChildren = children.length > 0;
  const childActive = active && submenuContainsKey(children, activeSubmenu);
  const subActive = active && (activeSubmenu === subItem.key || childActive);
  const nodeOpen = Boolean(openSubmenuKeys[subItem.key]);
  const isSubmenuVisible = hoveredSubmenuKey === subItem.key || (!hoveredSubmenuKey && nodeOpen);
  const SubIcon = displaySubItem.icon || ChevronRight;

  const entryRef = useRef(null);
  const [nestedStyle, setNestedStyle] = useState({});

  useLayoutEffect(() => {
    if (hasChildren && isSubmenuVisible && entryRef.current) {
      const updatePosition = () => {
        const shell = document.querySelector('.premium-crm-shell');
        const shellRect = shell ? shell.getBoundingClientRect() : { top: 0, left: 0, height: window.innerHeight };
        const scale = 0.9;
        
        const entryRect = entryRef.current.getBoundingClientRect();
        const internalEntryTop = (entryRect.top - shellRect.top) / scale;
        const internalShellHeight = shellRect.height / scale;

        const nestedEl = entryRef.current.querySelector('.module-submenu-nested');
        let level3Height = 0;
        if (nestedEl) {
          level3Height = nestedEl.getBoundingClientRect().height / scale;
        }
        if (!level3Height) {
          level3Height = 24 + (children.length * 42);
        }
        if (level3Height > 360) level3Height = 360;

        const level3Bottom = internalEntryTop + level3Height;
        if (level3Bottom > internalShellHeight - 16) {
          const overflow = level3Bottom - (internalShellHeight - 16);
          let topShift = -6 - overflow;
          if (internalEntryTop + topShift < 16) {
            topShift = 16 - internalEntryTop;
          }
          setNestedStyle({ top: `${topShift}px` });
        } else {
          setNestedStyle({ top: '-6px' });
        }
      };

      updatePosition();
      
      const sidebar = document.querySelector('.crm-sidebar');
      if (sidebar) {
        sidebar.addEventListener('scroll', updatePosition, { passive: true });
      }
      window.addEventListener('resize', updatePosition, { passive: true });
      return () => {
        if (sidebar) {
          sidebar.removeEventListener('scroll', updatePosition);
        }
        window.removeEventListener('resize', updatePosition);
      };
    } else {
      setNestedStyle({});
    }
  }, [hasChildren, isSubmenuVisible, children.length]);

  return (
    <div
      ref={entryRef}
      className="module-submenu-entry"
      onMouseEnter={() => {
        if (depth === 0) {
          setHoveredSubmenuKey(subItem.key);
        }
      }}
      onMouseLeave={() => {
        if (depth === 0) {
          setHoveredSubmenuKey((current) => (current === subItem.key ? null : current));
        }
      }}
    >
      {showGroupLabel && <div className="module-submenu-group-label">{groupLabel}</div>}
      <button
        type="button"
        className={`module-submenu-item ${subActive ? 'active' : ''} ${hasChildren ? 'has-children' : ''}`}
        style={{ '--submenu-depth': depth, ...submenuAccentStyle(displaySubItem, depth) }}
        onClick={() => handleSubmenuClick(subItem, hasChildren, parentPath)}
      >
        <SubIcon size={14} />
        <span>
          <strong>{displaySubItem.title}</strong>
          <small>{displaySubItem.subtitle}</small>
        </span>
        {hasChildren && <ChevronRight className={`module-submenu-chevron ${nodeOpen ? 'open' : ''}`} size={13} />}
      </button>
      {hasChildren && (
        <div
          className={`module-submenu-nested ${isSubmenuVisible ? 'open' : ''}`}
          style={nestedStyle}
        >
          {renderSubmenuItems(children, depth + 1, [...parentPath, subItem.key])}
        </div>
      )}
    </div>
  );
}

function ModuleTile({ item, onOpen, active, activeSubmenu, viewerRole, submenuOpen, onToggleSubmenu, isCollapsed, onToggleCollapse, onMouseEnterBlock, onMouseLeaveBlock }) {
  const displayItem = presentModule(item, viewerRole);
  const Icon = item.icon;
  const subItems = useMemo(() => item.subItems || [], [item.subItems]);
  const [openSubmenuKeys, setOpenSubmenuKeys] = useState({});
  const [hoveredSubmenuKey, setHoveredSubmenuKey] = useState(null);
  const defaultSubmenu = '';
  const containerRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!active || !activeSubmenu) return;
    const activePath = submenuPathToKey(subItems, activeSubmenu);
    if (activePath.length === 0) return;
    setOpenSubmenuKeys((current) => {
      const next = { ...current };
      activePath.slice(0, -1).forEach((key) => { next[key] = true; });
      return next;
    });
  }, [active, activeSubmenu, subItems]);

  useEffect(() => {
    if (!submenuOpen) setOpenSubmenuKeys({});
  }, [submenuOpen]);

  useEffect(() => {
    const updateCoords = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const shell = document.querySelector('.premium-crm-shell');
        const shellRect = shell ? shell.getBoundingClientRect() : { top: 0, left: 0, height: window.innerHeight };
        const scale = 0.9;
        
        const internalButtonTop = (rect.top - shellRect.top) / scale;
        const internalButtonLeft = (rect.right - shellRect.left) / scale;
        const internalShellHeight = shellRect.height / scale;

        // Smart vertical alignment: shift submenu up if it overflows the bottom of the screen.
        let estimatedHeight = 24 + (subItems.length * 42);
        if (estimatedHeight > 540) estimatedHeight = 540; // cap at CSS max-height limit

        let top = internalButtonTop;
        if (top + estimatedHeight > internalShellHeight - 16) {
          top = internalShellHeight - estimatedHeight - 16;
          if (top < 16) top = 16; // ensure it doesn't overflow the top
        }

        setCoords({
          top: top,
          left: internalButtonLeft
        });
      }
    };

    if (submenuOpen) {
      updateCoords();
      const sidebar = document.querySelector('.crm-sidebar');
      if (sidebar) {
        sidebar.addEventListener('scroll', updateCoords, { passive: true });
      }
      window.addEventListener('resize', updateCoords, { passive: true });
      return () => {
        if (sidebar) {
          sidebar.removeEventListener('scroll', updateCoords);
        }
        window.removeEventListener('resize', updateCoords);
      };
    }
  }, [submenuOpen, subItems.length]);

  const handleMenuClick = () => {
    if (isCollapsed && subItems.length > 0) {
      if (onToggleCollapse) onToggleCollapse();
    }
    onOpen(item.key, defaultSubmenu);
    if (subItems.length > 0) onToggleSubmenu(item.key);
  };
  const toggleSubmenuNode = (key, parentPath = []) => {
    setOpenSubmenuKeys((current) => {
      const next = {};
      parentPath.forEach((pathKey) => { next[pathKey] = true; });
      if (!current[key]) next[key] = true;
      return next;
    });
  };
  const handleSubmenuClick = (subItem, hasChildren, parentPath = []) => {
    if (hasChildren) {
      toggleSubmenuNode(subItem.key, parentPath);
      if (item.key === 'reports') onOpen(item.key, subItem.key);
      return;
    }
    setOpenSubmenuKeys({});
    onOpen(item.key, subItem.key);
  };
  const renderSubmenuItems = (items, depth = 0, parentPath = []) => {
    return items.map((subItem, index) => {
      const groupLabel = subItem.group || '';
      const showGroupLabel = groupLabel && items[index - 1]?.group !== groupLabel;

      return (
        <SubmenuEntry
          key={subItem.key}
          subItem={subItem}
          depth={depth}
          parentPath={parentPath}
          viewerRole={viewerRole}
          active={active}
          activeSubmenu={activeSubmenu}
          openSubmenuKeys={openSubmenuKeys}
          hoveredSubmenuKey={hoveredSubmenuKey}
          setHoveredSubmenuKey={setHoveredSubmenuKey}
          handleSubmenuClick={handleSubmenuClick}
          submenuAccentStyle={submenuAccentStyle}
          renderSubmenuItems={renderSubmenuItems}
          showGroupLabel={showGroupLabel}
          groupLabel={groupLabel}
        />
      );
    });
  };
  return (
    <div
      ref={containerRef}
      className="module-nav-block"
      style={menuAccentStyle(item.key)}
      onMouseEnter={() => onMouseEnterBlock?.(item.key)}
      onMouseLeave={() => onMouseLeaveBlock?.(item.key)}
    >
      <button className={`module-nav-item ${active ? 'active' : ''}`} onClick={handleMenuClick} title={isCollapsed ? displayItem.title : ''}>
        <Icon size={18} />
        <span>
          <strong>{displayItem.title}</strong>
          <small>{displayItem.subtitle}</small>
        </span>
        {subItems.length > 0 && !isCollapsed && (
          <ChevronDown className={`module-nav-chevron ${submenuOpen ? 'open' : ''}`} size={14} />
        )}
      </button>
      {subItems.length > 0 && createPortal(
        <div
          className={`module-submenu-list ${submenuOpen ? 'open' : ''}`}
          style={{
            position: 'fixed',
            top: `${coords.top}px`,
            left: `${coords.left}px`
          }}
        >
          {renderSubmenuItems(subItems)}
        </div>,
        document.querySelector('.premium-crm-shell') || document.body
      )}
    </div>
  );
}

function Sidebar({ modules, activePage, activeSubmenu, onOpen, open, onClose, user, brandLogoSrc, isCollapsed, onToggleCollapse, customerVehicles = [], onOpenCustomerProfile }) {
  const hasHostBrand = Boolean(user?.brand_context_active && user?.brand_logo_url);

  const sidebarModules = useMemo(() => {
    if (!['admin', 'whitelabel_admin'].includes(user?.role)) return modules;
    const notificationItems = modules
      .filter((item) => item.key === 'notifications')
      .map((item) => ({ ...item, group: 'Notification' }));
    if (notificationItems.length === 0) return modules;
    return [...modules.filter((item) => item.key !== 'notifications'), ...notificationItems];
  }, [modules, user?.role]);

  const groups = sidebarModules.reduce((acc, item) => {
    const displayItem = presentModule(item, user?.role);
    acc[displayItem.group] = [...(acc[displayItem.group] || []), item];
    return acc;
  }, {});
  const groupEntries = Object.entries(groups);
  const groupNames = groupEntries.map(([group]) => group).join('|');
  const activeGroup = groupEntries.find(([, items]) => (
    items.some((item) => activePage === item.key || (activePage === 'company-detail' && item.key === 'dashboard'))
  ))?.[0];
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedSubmenus, setExpandedSubmenus] = useState({});
  const [hoveredModule, setHoveredModule] = useState(null);

  const handleMouseEnterBlock = (key) => {
    setHoveredModule(key);
  };

  const handleMouseLeaveBlock = (key) => {
    setHoveredModule((current) => (current === key ? null : current));
  };

  useEffect(() => {
    const names = groupNames ? groupNames.split('|').filter(Boolean) : [];
    setExpandedGroups((current) => {
      const next = { ...current };
      names.forEach((group) => {
        if (next[group] === undefined) next[group] = true;
      });
      if (activeGroup) next[activeGroup] = true;
      return next;
    });
  }, [activeGroup, groupNames]);

  const toggleGroup = (group) => {
    setExpandedGroups((current) => ({ ...current, [group]: !current[group] }));
  };

  const toggleSubmenu = (key) => {
    setExpandedSubmenus((current) => (current[key] ? {} : { [key]: true }));
  };

  const openMenuItem = (key, submenu = '') => {
    onOpen(key, submenu);
    const clickedModule = modules.find((m) => m.key === key);
    if (!clickedModule || !clickedModule.subItems || clickedModule.subItems.length === 0 || submenu) {
      setExpandedSubmenus({});
    }
  };

  return (
    <>
      <aside
        className={`crm-sidebar ${isCollapsed ? 'collapsed' : ''} ${open ? 'open' : ''} ${user?.role === 'customer' ? 'sidebar-customer-view' : ''}`}
        style={user?.role === 'customer' ? { paddingBottom: '0px', display: 'flex', flexDirection: 'column' } : undefined}
      >
        <div className="sidebar-header-row" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'space-between',
          gap: '10px',
          marginBottom: '14px',
          borderBottom: '1px solid rgba(148, 163, 184, 0.12)',
          paddingBottom: '10px'
        }}>
          {!isCollapsed && (
            <div className="sidebar-brand" onClick={() => openMenuItem('dashboard')} style={{ minHeight: 'auto', marginBottom: '0', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '1' }}>
              {hasHostBrand ? (
                <div className="sidebar-brand-badge">
                  <img className="sidebar-brand-logo" src={brandLogoSrc} alt={user?.brand_name || 'Brand Logo'} style={{ maxHeight: '70px', maxWidth: '100%', objectFit: 'contain', width: 'auto' }} />
                </div>
              ) : (
                <img className="sidebar-brand-logo" src={brandLogoSrc || '/assets/BizTrack_Logo.png'} alt={user?.brand_name || 'BizTrack'} style={{ maxHeight: '62px', objectFit: 'contain', width: 'auto' }} />
              )}
            </div>
          )}
          <button
            type="button"
            className="sidebar-collapse-toggle-btn"
            onClick={onToggleCollapse}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              border: '1px solid var(--line)',
              background: 'var(--glass-strong)',
              color: 'var(--text)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 6px rgba(0,0,0,0.05)'
            }}
            title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          >
            {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown style={{ transform: 'rotate(90deg)' }} size={16} />}
          </button>
        </div>

        {/* CUSTOMER MULTI-VEHICLE FLEET SELECTION IN MENU SIDEBAR */}
        {user?.role === 'customer' ? (
          <div className="nav-group customer-sidebar-nav-group" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 'calc(100vh - 135px)' }}>
            <button type="button" className="nav-group-toggle open" style={{ cursor: 'default' }}>
              <span>MY VEHICLES</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <small>{customerVehicles.length}</small>
              </div>
            </button>
            <div className="nav-group-list-wrapper open">
              <div className="nav-group-list">
                {customerVehicles.map((v) => {
                  const isSelected = activeSubmenu
                    ? String(v.id) === String(activeSubmenu) || v.vehicle_no === activeSubmenu
                    : customerVehicles[0]?.id === v.id;
                  const isMoving = String(v.gps_motion_status).toLowerCase() === 'moving' || Number(v.gps_speed || 0) > 3;
                  const isTruck = String(v.vehicle_type || '').toLowerCase().includes('truck');
                  const Icon = isTruck ? Navigation : Navigation;

                  return (
                    <button
                      key={v.id}
                      type="button"
                      className={`module-nav-item ${isSelected ? 'active' : ''}`}
                      onClick={() => {
                        openMenuItem('dashboard', String(v.id));
                        window.dispatchEvent(new CustomEvent('biztrack:select-vehicle', { detail: v }));
                      }}
                      style={{ marginBottom: '6px', textAlign: 'left', width: '100%', cursor: 'pointer' }}
                      title={isCollapsed ? v.vehicle_no : ''}
                    >
                      <Icon size={18} color={isMoving ? '#16a34a' : '#0284c7'} />
                      {!isCollapsed && (
                        <span style={{ width: '100%' }}>
                          <strong style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '6px' }}>
                            <span>{v.vehicle_no}</span>
                            <span style={{
                              fontSize: '9px',
                              padding: '1px 5px',
                              borderRadius: '4px',
                              background: isMoving ? '#dcfce7' : '#fee2e2',
                              color: isMoving ? '#16a34a' : '#dc2626',
                              fontWeight: 900
                            }}>
                              {isMoving ? `${Number(v.gps_speed || 0).toFixed(0)} km/h` : 'Stopped'}
                            </span>
                          </strong>
                          <small style={{ fontSize: '10.5px', color: 'var(--text-muted)', display: 'block', marginTop: '2px' }}>
                            {v.driver_name ? v.driver_name.split(' ')[0] : 'Driver'} • {v.imei ? `...${String(v.imei).slice(-6)}` : 'GPS'}
                          </small>
                        </span>
                      )}
                    </button>
                  );
                })}

                {/* Driver History Log direct access in Sidebar */}
                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed var(--line, #e2e8f0)' }}>
                  <button
                    type="button"
                    onClick={() => onOpenCustomerProfile && onOpenCustomerProfile('logs')}
                    className="module-button"
                    style={{
                      width: '100%',
                      padding: isCollapsed ? '8px 0' : '8px 10px',
                      background: 'rgba(2, 132, 199, 0.07)',
                      borderRadius: '8px',
                      border: '1px solid rgba(2, 132, 199, 0.22)',
                      color: '#0284c7',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 800,
                      justifyContent: isCollapsed ? 'center' : 'flex-start',
                      transition: 'all 0.2s ease'
                    }}
                    title="View Driver Assignment History Log"
                  >
                    <Clock size={16} color="#0284c7" />
                    {!isCollapsed && <span>Driver History Log</span>}
                  </button>
                </div>
              </div>
            </div>

            {/* CUSTOMER PROFILE SECTION (ABOVE DEALER SUPPORT) */}
            {!isCollapsed && (
              <div
                onClick={onOpenCustomerProfile}
                title="Click to view & edit your profile details"
                style={{
                  marginTop: 'auto',
                  padding: '9px 12px',
                  background: 'var(--glass-strong, #f8fafc)',
                  borderRadius: '10px',
                  border: '1.5px solid var(--line, #e2e8f0)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)',
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                  userSelect: 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#0284c7';
                  e.currentTarget.style.background = '#f0f9ff';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--line, #e2e8f0)';
                  e.currentTarget.style.background = 'var(--glass-strong, #f8fafc)';
                  e.currentTarget.style.transform = 'none';
                }}
              >
                <div style={{
                  width: '34px',
                  height: '34px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #0284c7, #0369a1)',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  fontSize: '13px',
                  flexShrink: 0,
                  boxShadow: '0 2px 6px rgba(2, 132, 199, 0.35)',
                  overflow: 'hidden'
                }}>
                  {user?.profile_pic ? (
                    <img
                      src={`${API_URL}/api/profile/pic/${user.profile_pic}`}
                      alt="Profile"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <User size={17} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
                    <strong style={{ fontSize: '13px', fontWeight: 800, color: 'var(--text, #0f172a)' }}>
                      Profile
                    </strong>
                    <span style={{
                      fontSize: '9.5px',
                      fontWeight: 800,
                      color: '#0284c7',
                      background: '#e0f2fe',
                      padding: '1.5px 6px',
                      borderRadius: '4px'
                    }}>
                      Edit
                    </span>
                  </div>
                  <div style={{ fontSize: '10.5px', color: 'var(--text-muted, #64748b)' }}>
                    Account & Driver Settings
                  </div>
                </div>
              </div>
            )}

            {/* AUTHORIZED DEALER & SUPPORT CARD FIXED AT VERY BOTTOM */}
            {!isCollapsed && (
              <div style={{
                marginTop: '6px',
                marginBottom: '0px',
                padding: '10px 12px',
                background: 'var(--glass-strong, #ffffff)',
                borderRadius: '10px',
                border: '1.5px solid rgba(2, 132, 199, 0.25)',
                boxShadow: '0 3px 10px rgba(0, 0, 0, 0.04)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                  <span style={{ fontSize: '9.5px', fontWeight: 800, color: '#0284c7', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    Authorized Dealer & Support
                  </span>
                </div>
                <div style={{ fontWeight: 800, fontSize: '12.5px', color: 'var(--text, #0f172a)' }}>
                  {customerVehicles[0]?.seller_brand_name || customerVehicles[0]?.admin_name || user?.admin_name || 'Raju Motors'}
                </div>
                <div style={{ fontSize: '10.5px', color: 'var(--text-muted, #64748b)', margin: '1px 0 6px' }}>
                  {customerVehicles[0]?.admin_mobile || customerVehicles[0]?.admin_whatsapp || user?.admin_mobile || '7210435114'} | {customerVehicles[0]?.admin_email || user?.admin_email || 'info@info.com'}
                </div>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <a
                    href={`https://wa.me/${(customerVehicles[0]?.admin_whatsapp || customerVehicles[0]?.admin_mobile || '7210435114').replace(/\D/g, '')}?text=${encodeURIComponent(`Hello, I need support for my GPS account`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      flex: 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      padding: '6px 6px',
                      borderRadius: '6px',
                      background: '#16a34a',
                      color: '#ffffff',
                      fontSize: '11px',
                      fontWeight: 800,
                      textDecoration: 'none',
                      boxShadow: '0 2px 6px rgba(22, 163, 74, 0.25)'
                    }}
                  >
                    <Send size={11} /> WhatsApp
                  </a>
                  <a
                    href={`tel:${customerVehicles[0]?.admin_mobile || '7210435114'}`}
                    style={{
                      flex: 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      padding: '6px 6px',
                      borderRadius: '6px',
                      background: '#0f172a',
                      color: '#ffffff',
                      fontSize: '11px',
                      fontWeight: 800,
                      textDecoration: 'none',
                      boxShadow: '0 2px 6px rgba(15, 23, 42, 0.25)'
                    }}
                  >
                    <Phone size={11} /> Call Dealer
                  </a>
                </div>
              </div>
            )}
          </div>
        ) : (
          groupEntries.map(([group, items]) => (
            <div className="nav-group" key={group}>
              <button type="button" className={`nav-group-toggle ${expandedGroups[group] ? 'open' : ''}`} onClick={() => toggleGroup(group)}>
                <span>{group}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <small>{items.length}</small>
                  <ChevronDown className="nav-group-chevron" size={14} />
                </div>
              </button>
              <div className={`nav-group-list-wrapper ${expandedGroups[group] ? 'open' : ''}`}>
                <div className="nav-group-list">
                  {items.map((item) => (
                    <ModuleTile
                      key={item.key}
                      item={item}
                      viewerRole={user?.role}
                      active={activePage === item.key || (activePage === 'company-detail' && item.key === 'dashboard')}
                      activeSubmenu={activeSubmenu}
                      submenuOpen={hoveredModule === item.key || (!hoveredModule && !!expandedSubmenus[item.key])}
                      onToggleSubmenu={toggleSubmenu}
                      onOpen={openMenuItem}
                      isCollapsed={isCollapsed}
                      onToggleCollapse={onToggleCollapse}
                      onMouseEnterBlock={handleMouseEnterBlock}
                      onMouseLeaveBlock={handleMouseLeaveBlock}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))
        )}
      </aside>
      {open && <button className="sidebar-scrim" onClick={onClose} aria-label="Close sidebar" />}
    </>
  );
}

function FeaturePage({ pageKey }) {
  const detail = featureDetails[pageKey] || featureDetails.support;
  const Icon = detail.icon;

  return (
    <motion.div {...pageMotion}>
      <section className="feature-hero glass-panel">
        <div className="feature-icon"><Icon size={42} /></div>
        <div>
          <span className="eyebrow"><Zap size={14} /> Premium SaaS module</span>
          <h2>{detail.title}</h2>
          <p>{detail.subtitle}</p>
        </div>
      </section>
      <div className="feature-grid">
        {detail.points.map((point) => (
          <div className="feature-card glass-panel" key={point}>
            <CheckCircle2 size={18} />
            <strong>{point}</strong>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function SupportCenter({ user, supportView = '' }) {
  const appSupportLabel = user?.role === 'whitelabel_admin' ? 'Brand App' : 'White Label App';
  const [tickets, setTickets] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('crm_support_tickets') || '[]');
    } catch {
      return [];
    }
  });
  const [filter, setFilter] = useState('all');
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    category: 'GPS Tracking',
    priority: 'High',
    message: ''
  });

  useEffect(() => {
    localStorage.setItem('crm_support_tickets', JSON.stringify(tickets));
  }, [tickets]);

  useEffect(() => {
    const filterMap = {
      'support-ticket-all': 'all',
      'support-ticket-open': 'open',
      'support-ticket-progress': 'in_progress',
      'support-ticket-resolved': 'resolved'
    };
    if (filterMap[supportView]) setFilter(filterMap[supportView]);
  }, [supportView]);

  const visibleTickets = filter === 'all' ? tickets : tickets.filter((ticket) => ticket.status === filter);
  const ticketStats = [
    { label: 'Total Tickets', value: tickets.length, icon: Ticket, color: '#0ea5e9' },
    { label: 'Open', value: tickets.filter((ticket) => ticket.status === 'open').length, icon: Bell, color: '#f59e0b' },
    { label: 'In Progress', value: tickets.filter((ticket) => ticket.status === 'in_progress').length, icon: RefreshCw, color: '#6366f1' },
    { label: 'Resolved', value: tickets.filter((ticket) => ticket.status === 'resolved').length, icon: CheckCircle2, color: '#10b981' },
  ];

  const createTicket = (event) => {
    event.preventDefault();
    if (!ticketForm.subject.trim() || !ticketForm.message.trim()) return alert('Ticket subject and message required');
    const ticket = {
      id: Date.now(),
      ...ticketForm,
      status: 'open',
      requester: user?.name || 'CRM User',
      mobile: user?.mobile || '',
      created_at: new Date().toISOString()
    };
    setTickets((current) => [ticket, ...current]);
    setTicketForm({ subject: '', category: 'GPS Tracking', priority: 'High', message: '' });
    alert('Support ticket created');
  };

  const updateTicketStatus = (ticketId, status) => {
    setTickets((current) => current.map((ticket) => ticket.id === ticketId ? { ...ticket, status } : ticket));
  };

  const downloadTickets = () => {
    const header = ['ticket_id', 'subject', 'category', 'priority', 'status', 'requester', 'mobile', 'created_at', 'message'];
    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const rows = tickets.map((ticket) => {
      const formattedTicket = { ...ticket, created_at: formatDateTime(ticket.created_at, '') };
      return header.map((key) => escapeCsv(formattedTicket[key])).join(',');
    });
    const blob = new Blob([[header.join(','), ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'support-tickets.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <motion.div {...pageMotion}>
      <WorkspaceHero icon={Ticket} kicker="support desk" title="Ticket Support" desc="Create, track, and close customer, dealer, GPS, billing, and renewal issues inside CRM.">
        <ShellButton onClick={downloadTickets}><Download size={16} /> Download Tickets</ShellButton>
      </WorkspaceHero>
      <div className="ops-grid">
        {ticketStats.map((item) => (
          <div className="ops-metric glass-panel" style={{ '--accent': item.color }} key={item.label}>
            <item.icon size={22} />
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </div>
      <section className="white-label-management-grid">
        <div className="workspace-card glass-panel">
          <div className="section-title-row">
            <h3>Create support ticket</h3>
            <small>Every panel option now has a working support flow</small>
          </div>
          <form onSubmit={createTicket} className="form-grid compact">
            <label>Subject<input value={ticketForm.subject} onChange={(event) => setTicketForm((current) => ({ ...current, subject: event.target.value }))} placeholder="Example: Live map not showing" required /></label>
            <label>Category<select value={ticketForm.category} onChange={(event) => setTicketForm((current) => ({ ...current, category: event.target.value }))}>
              <option>GPS Tracking</option>
              <option>Inventory</option>
              <option>Sales</option>
              <option>Payments</option>
              <option>Renewal</option>
              <option>{appSupportLabel}</option>
              <option>WhatsApp Panel</option>
            </select></label>
            <label>Priority<select value={ticketForm.priority} onChange={(event) => setTicketForm((current) => ({ ...current, priority: event.target.value }))}>
              <option>High</option>
              <option>Medium</option>
              <option>Low</option>
            </select></label>
            <label style={{ gridColumn: '1 / -1' }}>Message<textarea value={ticketForm.message} onChange={(event) => setTicketForm((current) => ({ ...current, message: event.target.value }))} placeholder="Issue details..." required /></label>
            <ShellButton type="submit"><Ticket size={16} /> Create Ticket</ShellButton>
          </form>
        </div>
        <div className="workspace-card glass-panel">
          <div className="section-title-row">
            <h3>Ticket filters</h3>
            <small>{visibleTickets.length} records</small>
          </div>
          <div className="wa-filter-row">
            {[
              { key: 'all', label: `All ${tickets.length}` },
              { key: 'open', label: `Open ${tickets.filter((ticket) => ticket.status === 'open').length}` },
              { key: 'in_progress', label: `Progress ${tickets.filter((ticket) => ticket.status === 'in_progress').length}` },
              { key: 'resolved', label: `Resolved ${tickets.filter((ticket) => ticket.status === 'resolved').length}` },
            ].map((item) => (
              <button type="button" key={item.key} className={filter === item.key ? 'active' : ''} onClick={() => setFilter(item.key)}>{item.label}</button>
            ))}
          </div>
          <div className="mini-table">
            {visibleTickets.map((ticket) => (
              <div className="mini-table-row" key={ticket.id}>
                <strong>{ticket.subject}</strong>
                <span>{ticket.category} | {ticket.priority}</span>
                <b>{formatRole(ticket.status)}</b>
                <small>{ticket.requester} | {formatDateTime(ticket.created_at)}</small>
                <div className="support-ticket-actions">
                  <button type="button" onClick={() => updateTicketStatus(ticket.id, 'open')}>Open</button>
                  <button type="button" onClick={() => updateTicketStatus(ticket.id, 'in_progress')}>Progress</button>
                  <button type="button" onClick={() => updateTicketStatus(ticket.id, 'resolved')}>Resolved</button>
                </div>
              </div>
            ))}
            {visibleTickets.length === 0 && <div className="empty-state">No support tickets found.</div>}
          </div>
        </div>
      </section>
    </motion.div>
  );
}

function SystemDeploymentCenter({ user }) {
  const [deploying, setDeploying] = useState(false);
  const [deployStatus, setDeployStatus] = useState('Ready');
  const [logs, setLogs] = useState('System deployment engine initialized. Ready to build...\n');
  const consoleRef = useRef(null);

  useEffect(() => {
    if (consoleRef.current) {
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }
  }, [logs]);

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const runDeployment = async () => {
    setDeploying(true);
    setDeployStatus('Deploying...');
    setLogs((prev) => prev + `[${new Date().toLocaleTimeString()}] Triggering system build and deployment...\n`);
    try {
      const res = await axios.post(`${API_URL}/api/workspace/deploy`, {}, {
        headers: getHeaders()
      });
      setDeployStatus('Success');
      setLogs((prev) => prev + `[${new Date().toLocaleTimeString()}] Deployment completed successfully!\n\n--- BUILD LOGS ---\n${res.data.logs || 'No logs returned.'}\n`);
    } catch (err) {
      console.error(err);
      const errMsg = err.response?.data?.message || err.message || 'Deployment failed';
      const errDetails = err.response?.data?.error || '';
      const stdoutLogs = err.response?.data?.stdout || '';
      const stderrLogs = err.response?.data?.stderr || '';
      setDeployStatus('Failed');
      setLogs((prev) => prev + `[${new Date().toLocaleTimeString()}] ERROR: ${errMsg}\n${errDetails ? `Details: ${errDetails}\n` : ''}${stdoutLogs ? `\n--- BUILD STDOUT ---\n${stdoutLogs}\n` : ''}${stderrLogs ? `\n--- BUILD STDERR ---\n${stderrLogs}\n` : ''}`);
    } finally {
      setDeploying(false);
    }
  };

  const clearLogs = () => {
    setLogs(`[${new Date().toLocaleTimeString()}] Logs cleared. System ready.\n`);
  };

  const getStatusColor = () => {
    if (deployStatus === 'Deploying...') return '#f59e0b';
    if (deployStatus === 'Success') return '#10b981';
    if (deployStatus === 'Failed') return '#ef4444';
    return '#3b82f6';
  };

  return (
    <motion.div {...pageMotion} className="system-deploy-page">
      <section className="feature-hero glass-panel" style={{ background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.06), rgba(239, 68, 68, 0.03))', border: '1px solid rgba(220, 38, 38, 0.15)' }}>
        <div className="feature-icon" style={{ background: 'linear-gradient(135deg, #dc2626, #be123c)' }}><RefreshCw size={28} className={deploying ? 'spin-animation' : ''} /></div>
        <div>
          <span className="eyebrow" style={{ color: '#be123c' }}><Zap size={14} /> Super Admin Control</span>
          <h2>System Deployment Center</h2>
          <p>Rebuild the React frontend static pages, compile styles, clean client caches, and redeploy services.</p>
        </div>
      </section>

      <section className="deployment-dashboard-layout" style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px', marginTop: '20px' }}>
        <div className="deployment-console-card glass-panel" style={{ padding: '20px', display: 'flex', flexDirection: 'column', minHeight: '450px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <h3 style={{ margin: '0', fontSize: '15px', fontWeight: '800' }}>Live Console Logs</h3>
            <button type="button" className="neo-button soft" onClick={clearLogs} style={{ minHeight: '32px', padding: '4px 12px', fontSize: '11px' }}>
              Clear Terminal
            </button>
          </div>

          <div ref={consoleRef} style={{
            flex: '1',
            background: '#0a0f1d',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px',
            padding: '16px',
            fontFamily: 'Consolas, Monaco, monospace',
            fontSize: '12px',
            color: '#4af626',
            overflowY: 'auto',
            maxHeight: '380px',
            whiteSpace: 'pre-wrap',
            boxShadow: 'inset 0 4px 16px rgba(0,0,0,0.5)',
            textShadow: '0 0 3px rgba(74,246,38,0.3)'
          }}>
            {logs}
          </div>
        </div>

        <div className="deployment-control-side" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div className="glass-panel" style={{ padding: '20px' }}>
            <h3 style={{ margin: '0 0 14px', fontSize: '15px', fontWeight: '800' }}>Engine Status</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <span className="live-dot" style={{
                width: '12px',
                height: '12px',
                backgroundColor: getStatusColor(),
                boxShadow: `0 0 10px ${getStatusColor()}`,
                animation: deploying ? 'livePulse 1.0s infinite' : 'none'
              }} />
              <strong style={{ fontSize: '14px', color: 'var(--text)' }}>{deployStatus}</strong>
            </div>

            <button
              type="button"
              onClick={runDeployment}
              disabled={deploying}
              style={{
                width: '100%',
                minHeight: '44px',
                borderRadius: '8px',
                border: 'none',
                color: '#ffffff',
                background: deploying ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'linear-gradient(135deg, #dc2626, #be123c)',
                fontSize: '13px',
                fontWeight: '900',
                cursor: deploying ? 'wait' : 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                boxShadow: deploying ? '0 4px 12px rgba(245, 158, 11, 0.2)' : '0 6px 16px rgba(220, 38, 38, 0.25)'
              }}
            >
              <RefreshCw size={16} className={deploying ? 'spin-animation' : ''} />
              <span>{deploying ? 'Deploying Services...' : 'Trigger Deploy Now'}</span>
            </button>
          </div>

          <div className="glass-panel" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <h4 style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', color: 'var(--muted)' }}>Environment</h4>
            <div style={{ fontSize: '11px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div><strong>OS Platform:</strong> {navigator.platform}</div>
              <div><strong>Dev Host:</strong> localhost:3000</div>
              <div><strong>API Port:</strong> 5000</div>
              <div><strong>DB Name:</strong> credyou_db</div>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
}

const whiteLabelProfileDefaults = {
  theme_mode: 'light',
  custom_domain: '',
  portal_url: '',
  auto_ssl_enabled: 1,
  ssl_status: 'not_configured',
  domain_status: 'not_configured',
  android_package_name: '',
  ios_bundle_id: '',
  brand_report_enabled: 1,
  support_email: '',
  invoice_address: '',
  login_eyebrow: 'CRM Access',
  login_title: 'Welcome back',
  login_subtitle: 'Sign in to manage customers, stock, renewals, payments, and team workflows.',
  login_primary_action: 'Enter Dashboard',
  login_secure_label: 'Encrypted session',
  login_access_label: 'Role-based access'
};

const makeWhiteLabelSlug = (value) => String(value || 'white-label').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'white-label';
const packageFromBrand = (value, type = 'web') => `com.biztrack.${makeWhiteLabelSlug(value).replace(/-/g, '.')}.${type}`;
const parseAppMetadata = (metadata) => {
  if (!metadata) return {};
  if (typeof metadata === 'object') return metadata;
  try {
    return JSON.parse(metadata);
  } catch {
    return {};
  }
};

function WhiteLabelBuilder({ user, onBrandUpdate, whiteLabelView = '' }) {
  const activeUser = user || getCurrentUser();
  const isSuperAdmin = activeUser?.role === 'superadmin';
  const [apps, setApps] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [appForm, setAppForm] = useState({ app_type: 'android', app_name: 'BizTrack CRM', package_name: '' });
  const [accountForm, setAccountForm] = useState({ name: '', mobile: '', email: '', username: '', password: '' });
  const [profileForm, setProfileForm] = useState(whiteLabelProfileDefaults);
  const [activeBuilderOption, setActiveBuilderOption] = useState('');
  const [creating, setCreating] = useState(false);
  const [publishingUpdate, setPublishingUpdate] = useState(false);
  const [buildProgress, setBuildProgress] = useState('');
  const [lastDownloadLabel, setLastDownloadLabel] = useState('');
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [domainInput, setDomainInput] = useState('');
  const [domainStatus, setDomainStatus] = useState(null);
  const [domainBusy, setDomainBusy] = useState('');
  const builderOptionsRef = useRef(null);
  const activeOptionRef = useRef(null);
  const domainSetupRef = useRef(null);
  const appBuildRef = useRef(null);
  const createAccountRef = useRef(null);
  const workspaceAccountsRef = useRef(null);
  const personaliseRef = useRef(null);
  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);
  const activeAdminId = isSuperAdmin ? selectedAdminId : activeUser?.id;
  const options = [
    { label: 'Upload logo', icon: Upload, tone: '#0ea5e9', soft: '#e0f2fe', tone2: '#22c55e' },
    { label: 'Change theme color', icon: Paintbrush, tone: '#a855f7', soft: '#f3e8ff', tone2: '#f97316' },
    { label: 'Custom domain setup', icon: Globe2, tone: '#14b8a6', soft: '#ccfbf1', tone2: '#3b82f6' },
    { label: 'Auto SSL', icon: Lock, tone: '#f59e0b', soft: '#fef3c7', tone2: '#10b981' },
    { label: 'App generation', icon: Smartphone, tone: '#ef4444', soft: '#fee2e2', tone2: '#06b6d4' },
    { label: 'Brand-specific reports', icon: FileText, tone: '#6366f1', soft: '#e0e7ff', tone2: '#84cc16' },
  ];
  const fetchApps = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/workspace/apps`, { headers: getHeaders() });
      setApps(res.data || []);
    } catch (err) {
      console.log(err);
    }
  }, [getHeaders]);

  const fetchAccounts = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await axios.get(`${API_URL}/api/users`, { headers: getHeaders() });
      const workspaceAccounts = (res.data || []).filter((item) => ['admin', 'whitelabel_admin'].includes(item.role));
      setAccounts(workspaceAccounts);
      setSelectedAdminId((current) => (
        workspaceAccounts.some((account) => String(account.id) === String(current))
          ? current
          : (workspaceAccounts[0]?.id ? String(workspaceAccounts[0].id) : '')
      ));
    } catch (err) {
      console.log(err);
    }
  }, [getHeaders, isSuperAdmin]);

  const fetchProfile = useCallback(async () => {
    if (isSuperAdmin && !selectedAdminId) return;
    try {
      const query = activeAdminId ? `?admin_id=${activeAdminId}` : '';
      const res = await axios.get(`${API_URL}/api/workspace/whitelabel/profile${query}`, { headers: getHeaders() });
      const profile = { ...whiteLabelProfileDefaults, ...(res.data || {}) };
      setProfileForm(profile);
      if (!isSuperAdmin) onBrandUpdate?.({ brand_logo_url: profile.logo_url, brand_name: profile.brand_name });
      setDomainInput(profile.custom_domain || '');
      setAppForm((current) => ({
        ...current,
        app_name: current.app_name && current.app_name !== 'BizTrack CRM' ? current.app_name : profile.brand_name,
        package_name: current.package_name
          || (current.app_type === 'ios' ? profile.ios_bundle_id : profile.android_package_name)
          || packageFromBrand(profile.brand_name, current.app_type)
      }));
    } catch (err) {
      console.log(err);
    }
  }, [activeAdminId, getHeaders, isSuperAdmin, onBrandUpdate, selectedAdminId]);

  const fetchDomainStatus = useCallback(async () => {
    if (!isSuperAdmin || !activeAdminId) {
      setDomainStatus(null);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/workspace/whitelabel/domain/status?admin_id=${activeAdminId}`, { headers: getHeaders() });
      setDomainStatus(res.data || null);
      if (res.data?.profile) {
        setProfileForm((current) => ({ ...current, ...res.data.profile }));
        setDomainInput(res.data.profile.custom_domain || '');
      }
    } catch (err) {
      console.log(err);
    }
  }, [activeAdminId, getHeaders, isSuperAdmin]);

  useEffect(() => { fetchApps(); fetchAccounts(); }, [fetchApps, fetchAccounts]);
  useEffect(() => { fetchProfile(); }, [fetchProfile]);
  useEffect(() => { fetchDomainStatus(); }, [fetchDomainStatus]);

  const mobileAppTypes = ['android', 'ios'];
  const visibleApps = (isSuperAdmin && selectedAdminId ? apps.filter((app) => String(app.admin_id) === String(selectedAdminId)) : apps)
    .filter((app) => mobileAppTypes.includes(app.app_type))
    .slice()
    .sort((a, b) => {
      const byDate = new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
      return byDate || Number(b.id || 0) - Number(a.id || 0);
    });
  const bizTrackAndroidApp = isSuperAdmin
    ? apps
        .filter((app) => app.app_type === 'android' && (Number(app.admin_id || 0) === 0 || app.package_name === 'com.biztrack.crm.android'))
        .slice()
        .sort((a, b) => {
          const byDate = new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
          return byDate || Number(b.id || 0) - Number(a.id || 0);
        })[0]
    : null;
  const latestAndroidApp = visibleApps.find((app) => app.app_type === 'android');
  const latestIosApp = visibleApps.find((app) => app.app_type === 'ios');
  const latestAndroidMetadata = parseAppMetadata(latestAndroidApp?.metadata);
  const latestAndroidVersion = latestAndroidMetadata.version_code || 'latest';
  const latestAndroidDownloadUrl = latestAndroidApp?.direct_download_url || latestAndroidApp?.download_url || '';
  const bizTrackAndroidMetadata = parseAppMetadata(bizTrackAndroidApp?.metadata);
  const bizTrackAndroidVersion = bizTrackAndroidMetadata.version_code || 'latest';
  const bizTrackAndroidDownloadUrl = bizTrackAndroidApp?.direct_download_url || bizTrackAndroidApp?.download_url || '';
  const accountStats = [
    { label: isSuperAdmin ? 'Workspace Accounts' : 'Admin Account', value: isSuperAdmin ? accounts.length : 1, icon: Building2, color: '#0891b2' },
    { label: 'App Download', value: lastDownloadLabel || 'Direct', icon: Download, color: '#7c3aed', text: true },
    { label: 'Domain', value: profileForm.custom_domain || 'Not Set', icon: Globe2, color: '#0ea5e9', text: true },
    { label: 'Auto SSL', value: formatRole(profileForm.ssl_status || 'not_configured'), icon: Lock, color: '#22c55e', text: true },
  ];
  const dnsReady = Boolean(domainStatus?.dns?.dns_ready) || ['dns_ready', 'nginx_ready', 'live'].includes(profileForm.domain_status);
  const nginxReady = ['nginx_ready', 'live'].includes(profileForm.domain_status);
  const sslActive = profileForm.ssl_status === 'active';
  const loginLive = profileForm.domain_status === 'live' || profileForm.app_status === 'branded_login_live';
  const domainSteps = [
    { title: '1. Save Custom Domain', value: profileForm.custom_domain || domainInput || 'Not Set', tone: 'blue' },
    { title: '2. DNS A Record', value: `${domainInput || profileForm.custom_domain || 'domain'} -> ${domainStatus?.dns?.expected_ip || 'server IP'}`, tone: 'cyan' },
    { title: '3. Nginx Mapping', value: 'Route domain to the same frontend and API', tone: 'violet' },
    { title: '4. SSL Certificate', value: profileForm.custom_domain ? `certbot --nginx -d ${profileForm.custom_domain}` : 'Save domain first', tone: 'green' },
  ];
  const domainServices = [
    { action: 'check-dns', title: 'Check DNS', desc: 'Verify domain points to server IP.', icon: Globe2 },
    { action: 'generate-nginx', title: 'Generate Nginx', desc: 'Create server block for this domain.', icon: Package },
    { action: 'issue-ssl', title: 'Issue SSL', desc: 'Run Certbot and enable HTTPS.', icon: Lock },
    { action: 'verify', title: 'Verify Domain', desc: 'Confirm branded login page is live.', icon: RefreshCw },
  ];
  const domainBadges = [
    { label: 'Pending DNS', active: !dnsReady },
    { label: 'DNS Ready', active: dnsReady },
    { label: 'Nginx Ready', active: nginxReady },
    { label: 'SSL Active', active: sslActive },
    { label: 'Branded Login Live', active: loginLive },
  ];

  const selectAccount = (account) => {
    setSelectedAdminId(String(account.id));
    setAppForm((current) => ({
      ...current,
      app_name: current.app_name && current.app_name !== 'BizTrack CRM' ? current.app_name : `${account.name} CRM`,
      package_name: packageFromBrand(account.name, current.app_type)
    }));
  };

  const saveProfile = async (event) => {
    event?.preventDefault();
    if (isSuperAdmin && !selectedAdminId) return alert('Select a workspace account first');
    setSavingProfile(true);
    try {
      const res = await axios.post(`${API_URL}/api/workspace/whitelabel/profile`, {
        ...profileForm,
        admin_id: activeAdminId
      }, { headers: getHeaders() });
      const nextProfile = { ...whiteLabelProfileDefaults, ...(res.data.profile || {}) };
      setProfileForm(nextProfile);
      if (!isSuperAdmin) onBrandUpdate?.({ brand_logo_url: nextProfile.logo_url, brand_name: nextProfile.brand_name });
      alert(isSuperAdmin ? 'White label profile saved' : 'Brand profile saved');
    } catch (err) {
      alert(err.response?.data?.message || (isSuperAdmin ? 'Could not save white label profile' : 'Could not save brand profile'));
    } finally {
      setSavingProfile(false);
    }
  };

  const applyDomainResponse = (data) => {
    if (data?.profile) {
      setProfileForm({ ...whiteLabelProfileDefaults, ...data.profile });
      setDomainInput(data.profile.custom_domain || '');
    }
    setDomainStatus((current) => ({ ...(current || {}), ...(data || {}) }));
  };

  const saveCustomDomain = async () => {
    if (!activeAdminId) return alert('Select a workspace account first');
    if (!domainInput.trim()) return alert('Enter custom domain first');
    setDomainBusy('save');
    try {
      const res = await axios.post(`${API_URL}/api/workspace/whitelabel/domain/save`, {
        admin_id: activeAdminId,
        custom_domain: domainInput
      }, { headers: getHeaders() });
      applyDomainResponse(res.data);
      alert(res.data.message || 'Custom domain saved');
    } catch (err) {
      alert(err.response?.data?.message || 'Could not save custom domain');
    } finally {
      setDomainBusy('');
    }
  };

  const runDomainService = async (action) => {
    if (!activeAdminId) return alert('Select a workspace account first');
    if (!domainInput.trim() && !profileForm.custom_domain) return alert('Save custom domain first');
    setDomainBusy(action);
    try {
      const res = await axios.post(`${API_URL}/api/workspace/whitelabel/domain/${action}`, {
        admin_id: activeAdminId,
        custom_domain: domainInput || profileForm.custom_domain
      }, { headers: getHeaders() });
      applyDomainResponse(res.data);
      alert(res.data.message || 'Domain service completed');
    } catch (err) {
      alert(err.response?.data?.message || 'Domain service failed');
    } finally {
      setDomainBusy('');
    }
  };

  const uploadLogo = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (isSuperAdmin && !selectedAdminId) return alert('Select a workspace account first');
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      if (activeAdminId) formData.append('admin_id', activeAdminId);
      const res = await axios.post(`${API_URL}/api/workspace/whitelabel/logo`, formData, { headers: getHeaders() });
      const nextProfile = { ...whiteLabelProfileDefaults, ...(res.data.profile || {}) };
      setProfileForm(nextProfile);
      if (!isSuperAdmin) onBrandUpdate?.({ brand_logo_url: nextProfile.logo_url, brand_name: nextProfile.brand_name });
      alert('Logo uploaded');
    } catch (err) {
      alert(err.response?.data?.message || 'Logo upload failed');
    } finally {
      setUploadingLogo(false);
      event.target.value = '';
    }
  };

  const createWhiteLabelAccount = async (event) => {
    event.preventDefault();
    if (!accountForm.name.trim() || !accountForm.mobile.trim()) return alert('Account name and mobile number are required');
    setCreatingAccount(true);
    try {
      const res = await axios.post(`${API_URL}/api/users`, {
        name: accountForm.name.trim(),
        mobile: accountForm.mobile.trim(),
        email: accountForm.email.trim(),
        username: accountForm.username.trim() || accountForm.mobile.trim(),
        password: accountForm.password.trim() || accountForm.mobile.trim(),
        role: 'whitelabel_admin'
      }, { headers: getHeaders() });
      await fetchAccounts();
      setSelectedAdminId(String(res.data.id));
      setAppForm((current) => ({ ...current, app_name: `${accountForm.name.trim()} CRM`, package_name: packageFromBrand(accountForm.name.trim(), current.app_type) }));
      setAccountForm({ name: '', mobile: '', email: '', username: '', password: '' });
      alert('White label account created successfully');
    } catch (err) {
      alert(err.response?.data?.message || 'Could not create white label account');
    } finally {
      setCreatingAccount(false);
    }
  };

  const getPackageNameForType = (type, name) => {
    const typedPackage = type === 'android'
      ? profileForm.android_package_name
      : type === 'ios'
        ? profileForm.ios_bundle_id
        : '';
    return String(typedPackage || '').trim() || packageFromBrand(name, type);
  };

  const generateAppRequest = async (type, name) => {
    const packageName = getPackageNameForType(type, name);
    const res = await axios.post(`${API_URL}/api/workspace/apps`, {
      ...appForm,
      ...profileForm,
      app_type: type,
      app_name: name,
      package_name: packageName,
      admin_id: isSuperAdmin ? selectedAdminId : undefined
    }, { headers: getHeaders() });
    return res.data.app;
  };

  const generateBizTrackAppRequest = async () => {
    const res = await axios.post(`${API_URL}/api/workspace/apps`, {
      app_type: 'android',
      app_name: 'BizTrack CRM',
      package_name: 'com.biztrack.crm.android',
      brand_name: 'BizTrack CRM',
      logo_url: '/assets/BizTrack_Logo.png',
      primary_color: '#0b66ae',
      secondary_color: '#8cc63f',
      theme_mode: 'light',
      custom_domain: 'biztrack.credyou.in',
      auto_ssl_enabled: 1,
      brand_report_enabled: 1,
      login_eyebrow: 'CRM Access',
      login_title: 'Welcome back',
      login_subtitle: 'Sign in to manage customers, stock, renewals, payments, and team workflows.',
      login_primary_action: 'Enter Dashboard'
    }, { headers: getHeaders() });
    return res.data.app;
  };

  const createApp = async (type = appForm.app_type) => {
    const name = appForm.app_name.trim();
    if (!name) return alert('App name is required');
    if (!['android', 'ios'].includes(type)) return alert('Only Android and iOS app generation is available here');
    if (isSuperAdmin && !selectedAdminId) return alert('Select a workspace account first');
    setCreating(true);
    setBuildProgress(type === 'android' ? 'Generating Android app package' : 'Generating iOS app package');
    try {
      const app = await generateAppRequest(type, name);
      setApps((current) => [app, ...current]);
      setAppForm((current) => ({ ...current, app_type: type, package_name: '' }));
      setBuildProgress(type === 'android' ? 'Downloading updated Android APK' : 'Downloading updated iOS package');
      await downloadApp(app);
      setLastDownloadLabel(type === 'android' ? 'Android APK' : 'iOS Package');
      alert(type === 'android' ? 'Updated Android app downloaded' : 'Updated iOS package downloaded');
    } catch (err) {
      alert(err.message || err.response?.data?.message || 'Could not generate app');
    } finally {
      setCreating(false);
      setBuildProgress('');
    }
  };

  const buildBizTrackApp = async () => {
    if (!isSuperAdmin) return alert('Only super admin can build BizTrack app');
    setCreating(true);
    setBuildProgress('Generating BizTrack Android APK');
    try {
      const app = await generateBizTrackAppRequest();
      await fetchApps();
      setBuildProgress('Downloading BizTrack APK');
      await downloadApp(app);
      setLastDownloadLabel('BizTrack APK');
      alert('BizTrack APK generated and downloaded');
    } catch (err) {
      alert(err.message || err.response?.data?.message || 'Could not generate BizTrack APK');
    } finally {
      setCreating(false);
      setBuildProgress('');
    }
  };

  const publishAllAppUpdates = async () => {
    if (!isSuperAdmin) return alert('Only super admin can publish APK updates');
    setPublishingUpdate(true);
    setBuildProgress('Publishing APK update to all white label apps');
    try {
      const res = await axios.post(`${API_URL}/api/workspace/apps/publish-update`, {}, { headers: getHeaders() });
      await fetchApps();
      alert(res.data?.message || 'APK update published to all white label apps');
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Could not publish APK update');
    } finally {
      setPublishingUpdate(false);
      setBuildProgress('');
    }
  };

  const getDownloadErrorMessage = async (err) => {
    const data = err.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      if (text) {
        try {
          return JSON.parse(text).message || text;
        } catch {
          return text;
        }
      }
    }
    return err.response?.data?.message || err.message || 'Download failed';
  };

  const downloadBlob = async (url, fileName) => {
    try {
      const res = await axios.get(url, { headers: getHeaders(), responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      throw new Error(await getDownloadErrorMessage(err));
    }
  };

  const downloadDirectFile = (url, fileName) => {
    const link = document.createElement('a');
    const separator = String(url).includes('?') ? '&' : '?';
    link.href = `${url}${separator}ts=${Date.now()}`;
    link.download = fileName;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const downloadApp = async (app) => {
    const extension = app.app_type === 'android' ? 'apk' : 'zip';
    const versionCode = app.metadata?.version_code || 'latest';
    const fileName = app.app_type === 'android'
      ? `${makeWhiteLabelSlug(app.app_name)}-v${versionCode}.apk`
      : `${makeWhiteLabelSlug(app.app_name)}-${app.app_type}.${extension}`;
    if (app.app_type === 'android' && (app.direct_download_url || app.download_url)) {
      downloadDirectFile(app.direct_download_url || app.download_url, fileName);
      return;
    }
    return downloadBlob(`${API_URL}/api/workspace/apps/${app.id}/download`, fileName);
  };

  const activateBuilderOption = (label) => {
    setActiveBuilderOption(label);
    if (label === 'App generation') {
      setAppForm((current) => ({ ...current, app_type: 'android', package_name: current.package_name || packageFromBrand(current.app_name, 'android') }));
    }
  };

  useEffect(() => {
    const optionMap = {
      'white-label-upload-logo': 'Upload logo',
      'white-label-theme-color': 'Change theme color',
      'white-label-brand-reports': 'Brand-specific reports',
      'white-label-save-domain': 'Custom domain setup',
      'white-label-check-dns': 'Custom domain setup',
      'white-label-generate-nginx': 'Custom domain setup',
      'white-label-issue-ssl': 'Auto SSL',
      'white-label-verify-domain': 'Custom domain setup',
      'white-label-android': 'App generation',
      'white-label-ios': 'App generation',
      'white-label-app-downloads': 'App generation'
    };
    const directPanelMap = {
      'white-label-create-account': { ref: createAccountRef, focus: 'input' },
      'white-label-workspace-accounts': { ref: workspaceAccountsRef, focus: 'button' }
    };
    const directPanel = directPanelMap[whiteLabelView];
    if (directPanel) {
      setActiveBuilderOption('');
      window.setTimeout(() => revealPanel(directPanel.ref, directPanel.focus), 0);
      return;
    }
    const option = optionMap[whiteLabelView];
    if (!option) return;
    activateBuilderOption(option);
    const targetRef = whiteLabelView.includes('domain') || whiteLabelView.includes('dns') || whiteLabelView.includes('ssl')
      ? domainSetupRef
      : whiteLabelView.includes('app') || whiteLabelView.includes('android') || whiteLabelView.includes('ios')
        ? appBuildRef
        : whiteLabelView === 'white-label-upload-logo' || whiteLabelView === 'white-label-theme-color' || whiteLabelView === 'white-label-brand-reports'
          ? personaliseRef
          : activeOptionRef;
    window.setTimeout(() => revealPanel(targetRef), 0);
    if (whiteLabelView === 'white-label-ios') {
      setAppForm((current) => ({ ...current, app_type: 'ios', package_name: current.package_name || packageFromBrand(current.app_name, 'ios') }));
    }
    if (whiteLabelView === 'white-label-android') {
      setAppForm((current) => ({ ...current, app_type: 'android', package_name: current.package_name || packageFromBrand(current.app_name, 'android') }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whiteLabelView]);

  return (
    <motion.div {...pageMotion}>
      <section className="builder-shell" ref={builderOptionsRef}>
        <div className="builder-options">
          {options.map(({ label, icon: Icon, tone, soft, tone2 }) => (
            <button type="button" className={`builder-option glass-panel ${activeBuilderOption === label ? 'active' : ''}`} key={label} style={{ '--tone': tone, '--soft': soft, '--tone-2': tone2 }} onClick={() => activateBuilderOption(label)}>
              <Icon size={18} />
              <span>{label}</span>
            </button>
          ))}
        </div>
      </section>
      {activeBuilderOption && (
        <section className="workspace-card glass-panel active-option-panel" ref={activeOptionRef}>
          <div className="section-title-row">
            <h3>{activeBuilderOption} active</h3>
          <small>{formatRole(profileForm.domain_status || 'not_configured')}</small>
          </div>
          <p>{activeBuilderOption} is ready in this {isSuperAdmin ? 'white label' : 'brand'} profile.</p>
        </section>
      )}
      {isSuperAdmin && (
        <section className="domain-ssl-setup glass-panel" ref={domainSetupRef}>
          <div className="domain-setup-left">
            <div className="domain-setup-heading">
              <span><Globe2 size={24} /></span>
              <div>
                <h3>Domain & SSL Setup</h3>
                <p>Select a white-label brand, check DNS, generate Nginx, issue SSL, then verify branded login.</p>
              </div>
            </div>
            <div className="domain-input-row">
              <select value={selectedAdminId} onChange={(event) => setSelectedAdminId(event.target.value)} disabled={accounts.length === 0}>
                <option value="">Select workspace account</option>
                {accounts.map((account) => <option value={account.id} key={account.id}>{account.name}</option>)}
              </select>
              <input value={domainInput} onChange={(event) => setDomainInput(event.target.value)} placeholder="crm.company.com" disabled={!selectedAdminId} />
              <ShellButton type="button" onClick={saveCustomDomain} disabled={!selectedAdminId || domainBusy === 'save'}>
                <Settings size={16} /> {domainBusy === 'save' ? 'Saving' : 'Save Domain'}
              </ShellButton>
            </div>
            <div className="domain-step-grid">
              {domainSteps.map((step) => (
                <div className={`domain-step-card ${step.tone}`} key={step.title}>
                  <strong>{step.title}</strong>
                  <span>{step.value}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="domain-automation-card">
            <div className="section-title-row">
              <h3>Automation Buttons</h3>
              <small>{loginLive ? 'Branded Login Live' : 'Ready for setup'}</small>
            </div>
            <div className="domain-action-grid">
              {domainServices.map(({ action, title, desc, icon: Icon }) => (
                <button type="button" key={action} onClick={() => runDomainService(action)} disabled={!selectedAdminId || Boolean(domainBusy)}>
                  <span><Icon size={18} /></span>
                  <strong>{domainBusy === action ? 'Running...' : title}</strong>
                  <small>{desc}</small>
                </button>
              ))}
            </div>
            <div className="domain-status-badges">
              {domainBadges.map((badge) => <span className={badge.active ? 'active' : ''} key={badge.label}>{badge.label}</span>)}
            </div>
            <div className="domain-log-list">
              <span>DNS A: {domainStatus?.dns?.a_records?.length ? domainStatus.dns.a_records.join(', ') : 'None'} | AAAA: {domainStatus?.dns?.aaaa_records?.length ? domainStatus.dns.aaaa_records.join(', ') : 'None'}</span>
              <span>Nginx site: {domainStatus?.nginx_site || (profileForm.custom_domain ? `/etc/nginx/conf.d/biztrack-wl-${makeWhiteLabelSlug(profileForm.custom_domain)}.conf` : 'Pending')}</span>
              <span>SSL active: {sslActive ? profileForm.custom_domain : 'Pending'}</span>
              <span>Live: {profileForm.portal_url || 'Pending'}</span>
            </div>
          </div>
        </section>
      )}
      <section className="ops-grid white-label-stats">
        {accountStats.map(({ label, value, icon: Icon, color }) => (
          <div className="ops-metric glass-panel" style={{ '--accent': color }} key={label}>
            <Icon size={18} />
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </section>
      {isSuperAdmin && (
        <section className="white-label-management-grid">
          <div className="workspace-card glass-panel" ref={createAccountRef}>
            <div className="section-title-row">
              <h3>Create white label account</h3>
              <small>Login is created from mobile or username</small>
            </div>
            <form onSubmit={createWhiteLabelAccount} className="form-grid compact">
              <label>Account / Company Name<input value={accountForm.name} onChange={(event) => setAccountForm((current) => ({ ...current, name: event.target.value }))} placeholder="Company name" required /></label>
              <label>Mobile Number<input value={accountForm.mobile} onChange={(event) => setAccountForm((current) => ({ ...current, mobile: event.target.value }))} placeholder="919999999999" required /></label>
              <label>Email<input value={accountForm.email} onChange={(event) => setAccountForm((current) => ({ ...current, email: event.target.value }))} placeholder="support@company.com" /></label>
              <label>Username<input value={accountForm.username} onChange={(event) => setAccountForm((current) => ({ ...current, username: event.target.value }))} placeholder="Optional; mobile used if blank" /></label>
              <label>Password<input type="password" value={accountForm.password} onChange={(event) => setAccountForm((current) => ({ ...current, password: event.target.value }))} placeholder="Optional; mobile used if blank" /></label>
              <ShellButton type="submit" disabled={creatingAccount}><Building2 size={16} /> Create Account</ShellButton>
            </form>
          </div>
          <div className="workspace-card glass-panel" ref={workspaceAccountsRef}>
            <div className="section-title-row">
              <h3>Workspace accounts</h3>
              <small>{accounts.length} accounts</small>
            </div>
            <div className="white-label-account-list">
              {accounts.map((account) => (
                <button type="button" className={`white-label-account-card ${String(selectedAdminId) === String(account.id) ? 'active' : ''}`} key={account.id} onClick={() => selectAccount(account)}>
                  <strong>{account.name}</strong>
                  <span>{account.mobile || account.username}</span>
                  <small>{formatRole(account.role)} | {account.is_active ? 'Active' : 'Inactive'}</small>
                </button>
              ))}
              {accounts.length === 0 && <div className="empty-state">No workspace account found. Create one to generate apps.</div>}
            </div>
          </div>
        </section>
      )}
      <section className="white-label-management-grid">
        <div className="workspace-card glass-panel whitelabel-personalise-card" ref={personaliseRef}>
          <div className="section-title-row">
            <h3>Personalise portal</h3>
            <small>{profileForm.custom_domain ? `${formatRole(profileForm.domain_status || 'pending_dns')} | ${profileForm.portal_url || profileForm.custom_domain}` : 'Domain not configured'}</small>
          </div>
          <form onSubmit={saveProfile} className="form-grid compact">
            <label>Brand / Portal Name<input value={profileForm.brand_name} onChange={(event) => setProfileForm((current) => ({ ...current, brand_name: event.target.value }))} placeholder="Brand CRM" /></label>
            <label>Logo Upload<input type="file" accept="image/*" onChange={uploadLogo} disabled={uploadingLogo || (isSuperAdmin && !selectedAdminId)} /></label>
            {isSuperAdmin && <label>Logo URL<input value={profileForm.logo_url} onChange={(event) => setProfileForm((current) => ({ ...current, logo_url: event.target.value }))} placeholder="/uploads/brand/logo.png" /></label>}
            <label>Primary Colour<input type="color" value={profileForm.primary_color} onChange={(event) => setProfileForm((current) => ({ ...current, primary_color: event.target.value }))} /></label>
            <label>Secondary Colour<input type="color" value={profileForm.secondary_color} onChange={(event) => setProfileForm((current) => ({ ...current, secondary_color: event.target.value }))} /></label>
            <label>Theme<select value={profileForm.theme_mode} onChange={(event) => setProfileForm((current) => ({ ...current, theme_mode: event.target.value }))}><option value="light">Light</option><option value="dark">Dark</option></select></label>
            <label>Custom Domain<input value={profileForm.custom_domain} onChange={(event) => setProfileForm((current) => ({ ...current, custom_domain: event.target.value }))} placeholder="crm.company.com" /></label>
            <label>Auto SSL<select value={String(profileForm.auto_ssl_enabled)} onChange={(event) => setProfileForm((current) => ({ ...current, auto_ssl_enabled: event.target.value }))}><option value="1">Active</option><option value="0">Manual</option></select></label>
            <label>Brand Reports<select value={String(profileForm.brand_report_enabled)} onChange={(event) => setProfileForm((current) => ({ ...current, brand_report_enabled: event.target.value }))}><option value="1">Active</option><option value="0">Off</option></select></label>
            <label>Support Email<input value={profileForm.support_email} onChange={(event) => setProfileForm((current) => ({ ...current, support_email: event.target.value }))} placeholder="support@company.com" /></label>
            <label className="wide-field">Invoice Address<textarea value={profileForm.invoice_address} onChange={(event) => setProfileForm((current) => ({ ...current, invoice_address: event.target.value }))} placeholder="Company address shown on invoice" /></label>
            <label>Login Badge<input value={profileForm.login_eyebrow} onChange={(event) => setProfileForm((current) => ({ ...current, login_eyebrow: event.target.value }))} placeholder="CRM Access" /></label>
            <label>Login Title<input value={profileForm.login_title} onChange={(event) => setProfileForm((current) => ({ ...current, login_title: event.target.value }))} placeholder="Welcome back" /></label>
            <label className="wide-field">Login Subtitle<textarea value={profileForm.login_subtitle} onChange={(event) => setProfileForm((current) => ({ ...current, login_subtitle: event.target.value }))} placeholder="Sign in copy shown on the login page" /></label>
            <label>Login Button<input value={profileForm.login_primary_action} onChange={(event) => setProfileForm((current) => ({ ...current, login_primary_action: event.target.value }))} placeholder="Enter Dashboard" /></label>
            <label>Security Label<input value={profileForm.login_secure_label} onChange={(event) => setProfileForm((current) => ({ ...current, login_secure_label: event.target.value }))} placeholder="Encrypted session" /></label>
            <label>Access Label<input value={profileForm.login_access_label} onChange={(event) => setProfileForm((current) => ({ ...current, login_access_label: event.target.value }))} placeholder="Role-based access" /></label>
            <ShellButton type="submit" disabled={savingProfile || (isSuperAdmin && !selectedAdminId)}><Settings size={16} /> Save Personalisation</ShellButton>
            <ShellButton type="button" disabled={!profileForm.portal_url} onClick={() => window.open(profileForm.portal_url, '_blank', 'noreferrer')}><Globe2 size={16} /> Open Portal</ShellButton>
          </form>
        </div>
        <div className="workspace-card glass-panel whitelabel-status-card">
          <div className="section-title-row">
            <h3>Activation status</h3>
            <small>{formatRole(profileForm.app_status || 'draft')}</small>
          </div>
          {[
            { label: 'Logo', value: profileForm.logo_url ? 'Active' : 'Pending', icon: Upload },
            { label: 'Theme Colour', value: `${profileForm.primary_color} / ${profileForm.secondary_color}`, icon: Paintbrush },
            { label: 'Custom Domain', value: profileForm.custom_domain || 'Not Set', icon: Globe2 },
            { label: 'AutoSSL', value: formatRole(profileForm.ssl_status), icon: Lock },
            { label: 'Brand Reports', value: Number(profileForm.brand_report_enabled) === 1 ? 'Active' : 'Off', icon: FileText },
          ].map(({ label, value, icon: Icon }) => (
            <div className="whitelabel-status-row" key={label}>
              <Icon size={16} />
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
      </section>
      <section className="app-build-panel glass-panel" ref={appBuildRef}>
        <div className="section-title-row">
          <h3>App Generation Process</h3>
          <small>{buildProgress || (latestAndroidApp || latestIosApp ? 'Mobile app package ready' : 'Ready')}</small>
        </div>
        <div className="app-build-action-row">
          {isSuperAdmin && (
            <>
              <ShellButton type="button" onClick={buildBizTrackApp} disabled={creating || publishingUpdate}>
                <Sparkles size={16} /> Build BizTrack APK
              </ShellButton>
              <ShellButton type="button" onClick={() => downloadApp(bizTrackAndroidApp)} disabled={creating || publishingUpdate || !bizTrackAndroidDownloadUrl}>
                <Download size={16} /> Download BizTrack APK
              </ShellButton>
            </>
          )}
          <ShellButton type="button" onClick={() => createApp('android')} disabled={creating || publishingUpdate || (isSuperAdmin && !selectedAdminId)}>
            <Smartphone size={16} /> Build Android APK
          </ShellButton>
          <ShellButton type="button" onClick={() => createApp('ios')} disabled={creating || publishingUpdate || (isSuperAdmin && !selectedAdminId)}>
            <Smartphone size={16} /> Build iOS Package
          </ShellButton>
          {isSuperAdmin && (
            <ShellButton type="button" onClick={publishAllAppUpdates} disabled={creating || publishingUpdate || apps.filter((app) => app.app_type === 'android').length === 0}>
              <RefreshCw size={16} /> Push Update to All APKs
            </ShellButton>
          )}
        </div>
        {isSuperAdmin && (
          <div className="app-latest-build-card">
            <div className="app-latest-build-main">
              <span><Sparkles size={18} /></span>
              <div>
                <strong>BizTrack CRM APK</strong>
                <small>
                  {bizTrackAndroidApp
                    ? `Updated APK v${bizTrackAndroidVersion} | ${bizTrackAndroidApp.package_name || 'com.biztrack.crm.android'}`
                    : 'Build the main BizTrack mobile app'}
                </small>
              </div>
            </div>
            <div className="app-latest-build-actions">
              <ShellButton type="button" onClick={buildBizTrackApp} disabled={creating || publishingUpdate}>
                <Smartphone size={16} /> Build BizTrack
              </ShellButton>
              <ShellButton type="button" onClick={() => downloadApp(bizTrackAndroidApp)} disabled={!bizTrackAndroidDownloadUrl || creating || publishingUpdate}>
                <Download size={16} /> Download
              </ShellButton>
            </div>
            <small className="app-latest-build-url">{bizTrackAndroidDownloadUrl || 'BizTrack APK not built yet'}</small>
          </div>
        )}
        {latestAndroidApp && (
          <div className="app-latest-build-card">
            <div className="app-latest-build-main">
              <span><Smartphone size={18} /></span>
              <div>
                <strong>{latestAndroidApp.app_name || 'Android APK'}</strong>
                <small>Updated APK v{latestAndroidVersion} | {latestAndroidApp.package_name || 'Package ready'}</small>
              </div>
            </div>
            <div className="app-latest-build-actions">
              <ShellButton type="button" onClick={() => downloadApp(latestAndroidApp)} disabled={!latestAndroidDownloadUrl}>
                <Download size={16} /> Download Updated APK
              </ShellButton>
              <ShellButton type="button" onClick={() => window.open(latestAndroidApp.app_url || profileForm.portal_url, '_blank', 'noreferrer')} disabled={!latestAndroidApp.app_url && !profileForm.portal_url}>
                <Globe2 size={16} /> Open App
              </ShellButton>
            </div>
            <small className="app-latest-build-url">{latestAndroidDownloadUrl || 'Download link pending'}</small>
          </div>
        )}
        <div className="app-build-step-grid">
          {[
            { title: 'Brand Profile', value: profileForm.brand_name || 'Pending', icon: Paintbrush, ready: Boolean(profileForm.brand_name) },
            { title: 'Android App', value: latestAndroidApp?.status ? formatRole(latestAndroidApp.status) : 'Pending', icon: Smartphone, ready: Boolean(latestAndroidApp) },
            { title: 'iOS App', value: latestIosApp?.status ? formatRole(latestIosApp.status) : 'Pending', icon: Smartphone, ready: Boolean(latestIosApp) }
          ].map(({ title, value, icon: Icon, ready }) => (
            <div className={`app-build-step-card ${ready ? 'ready' : ''}`} key={title}>
              <Icon size={18} />
              <span>{title}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
      </section>
    </motion.div>
  );
}

const workspaceDefaultSettings = {
  whatsapp_number: '',
  whatsapp_message: 'Hello {name}, you have an important update from BizTrack CRM.',
  android_app_link: '',
  ios_app_link: '',
  pwa_link: '',
  team_chat_enabled: '1'
};

function useWorkspaceSettings() {
  const [settings, setSettings] = useState(workspaceDefaultSettings);
  const [users, setUsers] = useState([]);
  const [allowedUserIds, setAllowedUserIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/workspace/settings`, { headers: getHeaders() });
      setSettings({ ...workspaceDefaultSettings, ...(res.data.settings || {}) });
      setUsers(res.data.users || []);
      setAllowedUserIds(res.data.allowed_user_ids || []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const saveSettings = async (payload) => {
    const res = await axios.post(`${API_URL}/api/workspace/settings`, payload, { headers: getHeaders() });
    setSettings({ ...workspaceDefaultSettings, ...(res.data.settings || {}) });
    setAllowedUserIds(res.data.allowed_user_ids || []);
    return res.data;
  };

  return { settings, setSettings, users, allowedUserIds, setAllowedUserIds, loading, fetchSettings, saveSettings };
}

function WorkspaceHero({ icon: Icon, kicker, title, desc, children }) {
  return (
    <section className="workspace-hero glass-panel">
      <div className="workspace-hero-icon"><Icon size={30} /></div>
      <div>
        <span className="eyebrow"><Sparkles size={14} /> {kicker}</span>
        <h2>{title}</h2>
        <p>{desc}</p>
      </div>
      {children ? <div className="workspace-hero-actions">{children}</div> : null}
    </section>
  );
}

function PurchaseCenter({ purchaseView = '' }) {
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [summary, setSummary] = useState({ purchase_count: 0, inward_devices: 0, vendor_credit: 0 });
  const [vendorForm, setVendorForm] = useState({ name: '', mobile: '', email: '', gst_no: '', address: '', opening_balance: '', balance_type: 'credit' });
  const [purchaseForm, setPurchaseForm] = useState({
    vendor_id: '',
    product_id: '',
    quantity: 1,
    unit_price: '',
    paid_amount: '',
    payment_mode: 'credit',
    invoice_no: '',
    purchase_date: '',
    warehouse_id: '',
    warehouse: 'Main',
    note: ''
  });
  const [deviceRows, setDeviceRows] = useState([{ imei: '', sim_no: '', sim_no_2: '', serial_no: '' }]);
  const [saving, setSaving] = useState(false);
  const vendorSectionRef = useRef(null);
  const purchaseSectionRef = useRef(null);
  const deviceRowsRef = useRef(null);
  const purchaseLedgerRef = useRef(null);
  const vendorLedgerRef = useRef(null);
  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const fetchPurchaseData = useCallback(async () => {
    try {
      const [vendorRes, productRes, warehouseRes, purchaseRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/purchases/vendors`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/inventory/products`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/inventory/warehouses`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/purchases/inventory-ledger`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/purchases/summary`, { headers: getHeaders() }),
      ]);
      setVendors(vendorRes.data || []);
      setProducts(productRes.data || []);
      setWarehouses(warehouseRes.data || []);
      setPurchases(purchaseRes.data || []);
      setSummary(summaryRes.data || {});
    } catch (err) {
      console.log(err);
    }
  }, [getHeaders]);

  useEffect(() => { fetchPurchaseData(); }, [fetchPurchaseData]);

  useEffect(() => {
    if (!purchaseView) return;
    const targetMap = {
      'purchase-create-vendor': { ref: vendorSectionRef, focus: 'input' },
      'purchase-entry-form': { ref: purchaseSectionRef, focus: 'select, input' },
      'purchase-inward-devices': { ref: deviceRowsRef, focus: 'input' },
      'purchase-ledger': { ref: purchaseLedgerRef },
      'purchase-vendor-ledger': { ref: vendorLedgerRef }
    };
    const target = targetMap[purchaseView];
    if (target) window.setTimeout(() => revealPanel(target.ref, target.focus), 0);
  }, [purchaseView]);

  const updatePurchaseField = (key, value) => {
    setPurchaseForm((current) => {
      if (key === 'warehouse_id') {
        const warehouse = warehouses.find((item) => String(item.id) === String(value));
        return { ...current, warehouse_id: value, warehouse: warehouse?.name || 'Main' };
      }
      return { ...current, [key]: value };
    });
    if (key === 'quantity') {
      const count = Math.min(Math.max(1, Number(value || 1)), 25);
      setDeviceRows((current) => Array.from({ length: count }, (_, index) => current[index] || { imei: '', sim_no: '', sim_no_2: '', serial_no: '' }));
    }
  };

  const updateDeviceRow = (index, key, value) => {
    setDeviceRows((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row));
  };

  const addVendor = async () => {
    if (!vendorForm.name.trim()) return alert('Vendor name is required');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/purchases/vendors`, vendorForm, { headers: getHeaders() });
      setVendorForm({ name: '', mobile: '', email: '', gst_no: '', address: '', opening_balance: '', balance_type: 'credit' });
      await fetchPurchaseData();
      alert('Vendor created successfully');
    } catch (err) {
      alert(err.response?.data?.message || 'Could not create vendor');
    } finally {
      setSaving(false);
    }
  };

  const addPurchase = async () => {
    if (!purchaseForm.vendor_id || !purchaseForm.product_id) return alert('Vendor and product are required');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/purchases`, {
        ...purchaseForm,
        quantity: Number(purchaseForm.quantity || 1),
        devices: deviceRows
      }, { headers: getHeaders() });
      setPurchaseForm({
        vendor_id: '',
        product_id: '',
        quantity: 1,
        unit_price: '',
        paid_amount: '',
        payment_mode: 'credit',
        invoice_no: '',
        purchase_date: '',
        warehouse_id: '',
        warehouse: 'Main',
        note: ''
      });
      setDeviceRows([{ imei: '', sim_no: '', sim_no_2: '', serial_no: '' }]);
      await fetchPurchaseData();
      alert('Purchase and inward inventory saved successfully');
    } catch (err) {
      alert(err.response?.data?.message || 'Could not save purchase');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div {...pageMotion}>
      <WorkspaceHero icon={ShoppingBag} kicker="purchase command" title="Purchase Management" desc="Vendor purchases, stock inward, office location mapping, and payment tracking in one panel.">
        <ShellButton onClick={fetchPurchaseData}><RefreshCw size={16} /> Refresh</ShellButton>
      </WorkspaceHero>
      <div className="ops-grid">
        {[
          { label: 'Purchase Entries', value: summary.purchase_count || 0, icon: ReceiptText, color: '#0b66ae' },
          { label: 'Inward Devices', value: summary.inward_devices || 0, icon: Package, color: '#d97706' },
          { label: 'Vendor Balance', value: formatMoney(summary.vendor_credit), icon: WalletCards, color: '#7c3aed' },
        ].map((item) => (
          <div className="ops-metric glass-panel" style={{ '--accent': item.color }} key={item.label}>
            <item.icon size={22} />
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </div>

      <section className="purchase-layout">
        <div className="workspace-card glass-panel" ref={vendorSectionRef}>
          <div className="section-title-row">
            <h3>Vendor details</h3>
            <small>Admin vendor creation</small>
          </div>
          <div className="form-grid compact">
            <label>
              Vendor Name
              <input value={vendorForm.name} onChange={(event) => setVendorForm((current) => ({ ...current, name: event.target.value }))} placeholder="Vendor name" />
            </label>
            <label>
              Mobile
              <input value={vendorForm.mobile} onChange={(event) => setVendorForm((current) => ({ ...current, mobile: event.target.value }))} placeholder="Mobile number" />
            </label>
            <label>
              Email
              <input value={vendorForm.email} onChange={(event) => setVendorForm((current) => ({ ...current, email: event.target.value }))} placeholder="Email address" />
            </label>
            <label>
              GST Number
              <input value={vendorForm.gst_no} onChange={(event) => setVendorForm((current) => ({ ...current, gst_no: event.target.value }))} placeholder="GST number" />
            </label>
            <label>
              Opening Balance
              <input type="number" value={vendorForm.opening_balance} onChange={(event) => setVendorForm((current) => ({ ...current, opening_balance: event.target.value }))} placeholder="0" />
            </label>
            <label>
              Balance Type
              <select value={vendorForm.balance_type} onChange={(event) => setVendorForm((current) => ({ ...current, balance_type: event.target.value }))}>
                <option value="credit">Credit</option>
                <option value="debit">Debit</option>
              </select>
            </label>
          </div>
          <label>
            Address
            <textarea value={vendorForm.address} onChange={(event) => setVendorForm((current) => ({ ...current, address: event.target.value }))} placeholder="Vendor address" />
          </label>
          <ShellButton onClick={addVendor} disabled={saving}><Building2 size={16} /> Create Vendor</ShellButton>
        </div>

        <div className="workspace-card glass-panel" ref={purchaseSectionRef}>
          <div className="section-title-row">
            <h3>Purchase details</h3>
            <small>Inward devices move into inventory</small>
          </div>
          <div className="form-grid">
            <label>
              Vendor
              <select value={purchaseForm.vendor_id} onChange={(event) => updatePurchaseField('vendor_id', event.target.value)}>
                <option value="">Select vendor</option>
                {vendors.map((vendor) => <option key={vendor.id} value={vendor.id}>{vendor.name}</option>)}
              </select>
            </label>
            <label>
              Product
              <select value={purchaseForm.product_id} onChange={(event) => updatePurchaseField('product_id', event.target.value)}>
                <option value="">Select product</option>
                {products.map((product) => <option key={product.id} value={product.id}>{product.name} {product.model ? `- ${product.model}` : ''}</option>)}
              </select>
            </label>
            <label>
              Quantity
              <input type="number" min="1" value={purchaseForm.quantity} onChange={(event) => updatePurchaseField('quantity', event.target.value)} />
            </label>
            <label>
              Unit Price
              <input type="number" value={purchaseForm.unit_price} onChange={(event) => updatePurchaseField('unit_price', event.target.value)} placeholder="0" />
            </label>
            <label>
              Paid Amount
              <input type="number" value={purchaseForm.paid_amount} onChange={(event) => updatePurchaseField('paid_amount', event.target.value)} placeholder="0" />
            </label>
            <label>
              Payment Mode
              <select value={purchaseForm.payment_mode} onChange={(event) => updatePurchaseField('payment_mode', event.target.value)}>
                <option value="cash">Cash</option>
                <option value="credit">Credit</option>
                <option value="online">Online</option>
                <option value="upi">UPI</option>
                <option value="gpay">GPay</option>
                <option value="paytm">Paytm</option>
                <option value="phonepe">PhonePe</option>
              </select>
            </label>
            <label>
              Invoice Number
              <input value={purchaseForm.invoice_no} onChange={(event) => updatePurchaseField('invoice_no', event.target.value)} placeholder="Invoice number" />
            </label>
            <label>
              Purchase Date
              <input type="date" value={purchaseForm.purchase_date} onChange={(event) => updatePurchaseField('purchase_date', event.target.value)} />
            </label>
            <label>
              Office Location
              <select value={purchaseForm.warehouse_id} onChange={(event) => updatePurchaseField('warehouse_id', event.target.value)}>
                <option value="">Main / auto office location</option>
                {warehouses.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
                ))}
              </select>
            </label>
          </div>
          <label>
            Note
            <textarea value={purchaseForm.note} onChange={(event) => updatePurchaseField('note', event.target.value)} placeholder="Purchase note" />
          </label>
          <div className="device-entry-list" ref={deviceRowsRef}>
            {deviceRows.map((row, index) => (
              <div className="device-entry-row" key={index}>
                <input value={row.imei} onChange={(event) => updateDeviceRow(index, 'imei', event.target.value)} placeholder={`IMEI ${index + 1}`} />
                <input value={row.sim_no} onChange={(event) => updateDeviceRow(index, 'sim_no', event.target.value)} placeholder="SIM No." />
                <input value={row.sim_no_2} onChange={(event) => updateDeviceRow(index, 'sim_no_2', event.target.value)} placeholder="SIM No. 2" />
                <input value={row.serial_no} onChange={(event) => updateDeviceRow(index, 'serial_no', event.target.value)} placeholder="Serial No." />
              </div>
            ))}
          </div>
          <ShellButton onClick={addPurchase} disabled={saving}><ClipboardList size={16} /> Save Purchase</ShellButton>
        </div>
      </section>

      <section className="workspace-card glass-panel" ref={purchaseLedgerRef}>
        <div className="section-title-row">
          <h3>Purchase ledger</h3>
          <small>Vendor debit, credit and inward device status</small>
        </div>
        <div className="mini-table purchase-table">
          {purchases.map((row) => (
            <div className="mini-table-row" key={row.id}>
              <strong>{row.vendor_name}</strong>
              <span>{row.product_name} | Invoice {row.invoice_no || 'No invoice'}</span>
              <b>Qty {row.quantity}</b>
              <em>{formatMoney(row.total_amount)} / Paid {formatMoney(row.paid_amount)}</em>
              <small>
                {(row.devices || []).length > 0
                  ? `${row.devices.length} inventory rows: ${(row.devices || []).slice(0, 5).map((device) => [device.imei, device.sim_no].filter(Boolean).join(' / ')).filter(Boolean).join(', ') || 'numbers pending'}`
                  : 'Inventory rows pending'}
              </small>
            </div>
          ))}
          {purchases.length === 0 && <div className="empty-state">No purchase records found.</div>}
        </div>
      </section>

      <section className="workspace-card glass-panel" ref={vendorLedgerRef}>
        <div className="section-title-row">
          <h3>Vendor debit credit details</h3>
          <small>{vendors.length} vendors</small>
        </div>
        <div className="mini-table vendor-table">
          {vendors.map((vendor) => (
            <div className="mini-table-row" key={vendor.id}>
              <strong>{vendor.name}</strong>
              <span>{vendor.mobile || vendor.email || 'No contact'}</span>
              <b>Debit {formatMoney(vendor.debit_amount)}</b>
              <em>Credit {formatMoney(vendor.credit_amount)}</em>
            </div>
          ))}
          {vendors.length === 0 && <div className="empty-state">No vendors created yet.</div>}
        </div>
      </section>
    </motion.div>
  );
}

function ReminderCenter({ onOpenWhatsAppPanel, reminderView = '' }) {
  const currentUser = getCurrentUser();
  const [expiring, setExpiring] = useState([]);
  const [reminderFilter, setReminderFilter] = useState('30');
  const [recipients, setRecipients] = useState({ users: [], customers: [] });
  const [notifyMode, setNotifyMode] = useState('single');
  const [notifyTarget, setNotifyTarget] = useState('');
  const [notifyCategory, setNotifyCategory] = useState('');
  const [notifyTitle, setNotifyTitle] = useState('Renewal Reminder');
  const [notifyMessage, setNotifyMessage] = useState('Hello {name}, your renewal is due soon. Please complete it on time.');
  const [notifyVoice, setNotifyVoice] = useState(true);
  const [notifyVoiceText, setNotifyVoiceText] = useState('Hello {name}, your renewal is due soon. Please complete it on time.');
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderCategories, setReminderCategories] = useState([]);
  const [categorySaving, setCategorySaving] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    category_key: 'gps_service_renewal',
    name: 'GPS Service Renewal',
    reminder_before_days: '30',
    reminder_channel: 'app_notification',
    auto_reminder: true,
    repeat_reminder: 'daily',
    custom_repeat_days: '2',
    payment_link: '',
    assigned_employee_id: '',
    followup_status: 'pending',
    is_active: true
  });
  const { settings } = useWorkspaceSettings();
  const canSendAppReminders = isWorkspaceAdmin(currentUser?.role);
  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const renewalReminderTypeOptions = [
    { value: 'gps_service_renewal', label: 'GPS Service Renewal' },
    { value: 'sim_renewal', label: 'SIM Renewal' },
    { value: 'ais_140_certificate_renewal', label: 'AIS-140 Certificate Renewal' },
    { value: 'insurance_renewal', label: 'Insurance Renewal' },
    { value: 'amc_renewal', label: 'AMC Renewal' },
    { value: 'software_subscription_renewal', label: 'Software Subscription Renewal' },
    { value: 'device_warranty_renewal', label: 'Device Warranty Renewal' }
  ];
  const reminderBeforeOptions = [30, 15, 7, 3, 1];
  const repeatReminderOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'custom', label: 'Custom' }
  ];
  const followupStatusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'called', label: 'Called' },
    { value: 'paid', label: 'Paid' },
    { value: 'expired', label: 'Expired' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const fetchExpiring = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customers/expiring`, { headers: getHeaders() });
      setExpiring(res.data || []);
    } catch (err) {
      console.log(err);
    }
  }, [getHeaders]);

  const fetchReminderRecipients = useCallback(async () => {
    if (!canSendAppReminders) {
      setRecipients({ users: [], customers: [] });
      return;
    }

    try {
      const res = await axios.get(`${API_URL}/api/notifications/recipients`, { headers: getHeaders() });
      setRecipients(res.data || { users: [], customers: [] });
    } catch (err) {
      console.log(err);
    }
  }, [canSendAppReminders, getHeaders]);

  const fetchReminderCategories = useCallback(async () => {
    if (!canSendAppReminders) {
      setReminderCategories([]);
      return;
    }

    try {
      const res = await axios.get(`${API_URL}/api/notifications/reminder-categories`, { headers: getHeaders() });
      setReminderCategories(res.data || []);
    } catch (err) {
      console.log(err);
    }
  }, [canSendAppReminders, getHeaders]);

  useEffect(() => {
    fetchExpiring();
    fetchReminderRecipients();
    fetchReminderCategories();
  }, [fetchExpiring, fetchReminderRecipients, fetchReminderCategories]);

  const reminderServices = [
    { label: 'Daily renewal scan', value: '9 AM', icon: Bell, color: '#0b66ae' },
    { label: 'Voice notification', value: 'Enabled', icon: Volume2, color: '#8cc63f' },
    { label: 'WhatsApp panel', value: settings.whatsapp_number ? 'Ready' : 'Configure', icon: MessageCircle, color: '#128c7e' },
  ];

  const reminderMessage = (row) => {
    const template = settings.whatsapp_message || 'Hello {name}, your renewal is due soon.';
    return template
      .replace(/\{\{name\}\}/g, row.name || 'Customer')
      .replace(/\{name\}/g, row.name || 'Customer');
  };

  const recipientOptions = [
    ...recipients.users.map((row) => ({
      value: `user:${row.id}`,
      label: `${row.name} - ${formatRole(row.role)} - ${row.mobile || 'No mobile'}`
    })),
    ...recipients.customers.map((row) => ({
      value: `customer:${row.id}`,
      label: `${row.name} - CRM Customer - ${row.mobile || 'No mobile'}${row.vehicle_no ? ` - ${row.vehicle_no}` : ''}`
    }))
  ];
  const roleCount = (roleName) => recipients.users.filter((row) => row.role === roleName).length;
  const reminderCategoryOptions = [
    { value: 'employee', label: 'Employees', count: roleCount('employee') },
    { value: 'dealer', label: 'Dealers', count: roleCount('dealer') },
    { value: 'customer', label: 'Customer App Users', count: roleCount('customer') },
    { value: 'crm_customer', label: 'All CRM Customers', count: recipients.customers.length },
  ];
  const reminderTemplates = [
    {
      key: 'renewal',
      label: 'Renewal',
      title: 'Renewal Reminder',
      message: 'Hello {name}, your renewal is due soon. Please complete it on time.',
      voice: 'Hello {name}, your renewal is due soon. Please complete it on time.'
    },
    {
      key: 'payment',
      label: 'Payment',
      title: 'Payment Reminder',
      message: 'Hello {name}, your payment follow-up is pending. Please check your app.',
      voice: 'Hello {name}, your payment follow-up is pending. Please check your app.'
    },
    {
      key: 'service',
      label: 'Service',
      title: 'Service Reminder',
      message: 'Hello {name}, this is an important service reminder. Please check your app.',
      voice: 'Hello {name}, this is an important service reminder. Please check your app.'
    }
  ];

  const employeeOptions = recipients.users.filter((row) => row.role === 'employee');
  const resetCategoryForm = () => {
    setEditingCategoryId(null);
    setCategoryForm({
      category_key: 'gps_service_renewal',
      name: 'GPS Service Renewal',
      reminder_before_days: '30',
      reminder_channel: 'app_notification',
      auto_reminder: true,
      repeat_reminder: 'daily',
      custom_repeat_days: '2',
      payment_link: '',
      assigned_employee_id: '',
      followup_status: 'pending',
      is_active: true
    });
  };

  const updateCategoryForm = (field, value) => {
    setCategoryForm((prev) => ({ ...prev, [field]: value }));
  };

  const selectReminderType = (value) => {
    const option = renewalReminderTypeOptions.find((item) => item.value === value);
    setCategoryForm((prev) => ({
      ...prev,
      category_key: value,
      name: option?.label || prev.name
    }));
  };

  const editReminderCategory = (category) => {
    setEditingCategoryId(category.id);
    setCategoryForm({
      category_key: category.category_key || 'gps_service_renewal',
      name: category.name || 'GPS Service Renewal',
      reminder_before_days: String(category.reminder_before_days || 30),
      reminder_channel: category.reminder_channel || 'app_notification',
      auto_reminder: Boolean(Number(category.auto_reminder)),
      repeat_reminder: category.repeat_reminder || 'daily',
      custom_repeat_days: String(category.custom_repeat_days || 2),
      payment_link: category.payment_link || '',
      assigned_employee_id: category.assigned_employee_id ? String(category.assigned_employee_id) : '',
      followup_status: category.followup_status || 'pending',
      is_active: Boolean(Number(category.is_active))
    });
  };

  const saveReminderCategory = async (event) => {
    event.preventDefault();
    if (!canSendAppReminders) return alert('Only admin can manage reminder categories');
    if (!categoryForm.name.trim()) return alert('Reminder category name is required');

    setCategorySaving(true);
    try {
      const payload = {
        ...categoryForm,
        reminder_before_days: Number(categoryForm.reminder_before_days),
        custom_repeat_days: categoryForm.repeat_reminder === 'custom' ? Number(categoryForm.custom_repeat_days || 1) : null,
        assigned_employee_id: categoryForm.assigned_employee_id || null
      };
      const request = editingCategoryId
        ? axios.put(`${API_URL}/api/notifications/reminder-categories/${editingCategoryId}`, payload, { headers: getHeaders() })
        : axios.post(`${API_URL}/api/notifications/reminder-categories`, payload, { headers: getHeaders() });
      const res = await request;
      alert(res.data?.message || 'Reminder category saved successfully');
      resetCategoryForm();
      fetchReminderCategories();
    } catch (err) {
      alert(err.response?.data?.message || 'Reminder category save failed');
    } finally {
      setCategorySaving(false);
    }
  };

  const applyReminderTemplate = (template) => {
    setNotifyTitle(template.title);
    setNotifyMessage(template.message);
    setNotifyVoice(true);
    setNotifyVoiceText(template.voice);
  };

  useEffect(() => {
    const reminderTypeMap = {
      'reminder-gps-renewal': 'gps_service_renewal',
      'reminder-insurance': 'insurance_renewal',
      'reminder-amc': 'amc_renewal'
    };
    if (reminderView === 'reminder-single') {
      setNotifyMode('single');
      return;
    }
    if (reminderView === 'reminder-category') {
      setNotifyMode('category');
      return;
    }
    if (reminderTypeMap[reminderView]) selectReminderType(reminderTypeMap[reminderView]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminderView]);

  const prefillAppReminder = (row) => {
    setNotifyMode('single');
    setNotifyTarget(`customer:${row.id}`);
    setNotifyCategory('');
    setNotifyTitle('Renewal Reminder');
    setNotifyMessage(`Hello {name}, your vehicle ${row.vehicle_no || ''} renewal is due on ${row.renewal_date ? formatDate(row.renewal_date) : 'upcoming date'}. Please complete it on time.`);
    setNotifyVoice(true);
    setNotifyVoiceText(`Hello {name}, your renewal is due. Please complete it on time.`);
  };

  const sendAppReminder = async (event) => {
    event.preventDefault();
    if (!canSendAppReminders) return alert('Only admin can send app reminders');
    if (notifyMode === 'single' && !notifyTarget) return alert('Please select user or customer');
    if (notifyMode === 'category' && !notifyCategory) return alert('Please select category');
    if (!notifyTitle.trim() || !notifyMessage.trim()) return alert('Title and message required');

    const [targetType, targetId] = notifyMode === 'single' ? notifyTarget.split(':') : ['category', notifyCategory];
    setSendingReminder(true);
    try {
      const res = await axios.post(`${API_URL}/api/notifications/send`, {
        target_type: targetType,
        target_id: targetId,
        target_category: notifyMode === 'category' ? notifyCategory : undefined,
        title: notifyTitle,
        message: notifyMessage,
        voice_enabled: notifyVoice,
        voice_text: notifyVoiceText || notifyMessage
      }, { headers: getHeaders() });
      alert(res.data?.message || 'Reminder sent successfully');
      setNotifyTarget('');
      setNotifyCategory('');
    } catch (err) {
      alert(err.response?.data?.message || 'Reminder send failed');
    } finally {
      setSendingReminder(false);
    }
  };

  const visibleReminders = expiring.filter((row) => {
    const daysLeft = Number(row.days_left || 0);
    if (reminderFilter === '15') return daysLeft <= 15;
    if (reminderFilter === 'payment') return row.payment_status !== 'paid';
    return true;
  });

  const downloadRenewalReport = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reports/renewals`, {
        headers: getHeaders(),
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'renewal-reminders-report.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      alert(err.response?.data?.message || 'Could not download renewal report');
    }
  };

  const addWhatsAppReminderToPanel = async (row = visibleReminders[0]) => {
    if (!row?.mobile) {
      alert('No customer mobile number available for reminder');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/whatsapp-work`, {
        contact_name: row.name || 'Customer',
        mobile: row.mobile,
        message: reminderMessage(row),
        source: 'renewal_reminder'
      }, { headers: getHeaders() });
      alert('WhatsApp reminder added to the CRM Work Panel');
      if (onOpenWhatsAppPanel) onOpenWhatsAppPanel();
    } catch (err) {
      alert(err.response?.data?.message || 'Could not add WhatsApp reminder');
    }
  };

  const playVoiceSummary = () => {
    if (!window.speechSynthesis) {
      alert('Voice alert is not supported in this browser');
      return;
    }
    const urgent = expiring.filter((row) => Number(row.days_left || 0) <= 15).length;
    const pending = expiring.filter((row) => row.payment_status !== 'paid').length;
    const message = `Reminder alert. ${expiring.length} renewals are due within 30 days. ${urgent} are priority renewals within 15 days. ${pending} payment follow ups are pending.`;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(new SpeechSynthesisUtterance(message));
  };

  const runReminderService = async (item) => {
    if (item === 'Daily dealer notification') {
      await fetchExpiring();
      alert('Daily dealer renewal scan refreshed');
    } else if (item === 'Customer WhatsApp reminder') {
      addWhatsAppReminderToPanel();
    } else if (item === 'Voice alert playback') {
      playVoiceSummary();
    } else if (item === '30 day renewal window') {
      setReminderFilter('30');
    } else if (item === '15 day priority window') {
      setReminderFilter('15');
    } else if (item === 'Payment follow-up') {
      setReminderFilter('payment');
    }
  };

  return (
    <motion.div {...pageMotion}>
      <WorkspaceHero icon={CalendarClock} kicker="renewal reminder" title="Reminder Center" desc="Daily renewal alerts, dealer morning notifications, and customer reminder workflows.">
        <ShellButton onClick={fetchExpiring}><RefreshCw size={16} /> Refresh</ShellButton>
        <ShellButton onClick={downloadRenewalReport}><Download size={16} /> Download Report</ShellButton>
      </WorkspaceHero>
      <div className="ops-grid">
        {[
          { label: '30 day renewals', value: expiring.length, icon: RefreshCw, color: '#dc2626' },
          { label: '15 day priority', value: expiring.filter((row) => Number(row.days_left || 0) <= 15).length, icon: Bell, color: '#f59e0b' },
          { label: 'Payment follow-up', value: expiring.filter((row) => row.payment_status !== 'paid').length, icon: CreditCard, color: '#8b5cf6' },
          ...reminderServices,
        ].map((item) => (
          <div className="ops-metric glass-panel" style={{ '--accent': item.color }} key={item.label}>
            <item.icon size={22} />
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </div>
      {canSendAppReminders && (
        <section className="workspace-card glass-panel reminder-category-card">
          <div className="section-title-row">
            <h3>Reminder Category</h3>
            <small>{reminderCategories.length} renewal types configured</small>
          </div>
          <form onSubmit={saveReminderCategory}>
            <div className="form-grid reminder-category-form">
              <label>
                Renewal Type
                <select value={categoryForm.category_key} onChange={(event) => selectReminderType(event.target.value)}>
                  {renewalReminderTypeOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </label>
              <label>
                Category Name
                <input value={categoryForm.name} onChange={(event) => updateCategoryForm('name', event.target.value)} placeholder="Reminder category name" required />
              </label>
              <label>
                Reminder Before
                <select value={categoryForm.reminder_before_days} onChange={(event) => updateCategoryForm('reminder_before_days', event.target.value)}>
                  {reminderBeforeOptions.map((day) => <option key={day} value={day}>{day} days</option>)}
                </select>
              </label>
              <label>
                Reminder Type
                <select value={categoryForm.reminder_channel} onChange={(event) => updateCategoryForm('reminder_channel', event.target.value)}>
                  <option value="app_notification">App Notification</option>
                </select>
              </label>
              <label>
                Repeat Reminder
                <select value={categoryForm.repeat_reminder} onChange={(event) => updateCategoryForm('repeat_reminder', event.target.value)}>
                  {repeatReminderOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </label>
              {categoryForm.repeat_reminder === 'custom' && (
                <label>
                  Custom Repeat Days
                  <input type="number" min="1" max="365" value={categoryForm.custom_repeat_days} onChange={(event) => updateCategoryForm('custom_repeat_days', event.target.value)} />
                </label>
              )}
              <label>
                Assigned Employee
                <select value={categoryForm.assigned_employee_id} onChange={(event) => updateCategoryForm('assigned_employee_id', event.target.value)}>
                  <option value="">No employee assigned</option>
                  {employeeOptions.map((item) => <option key={item.id} value={item.id}>{item.name} - {item.mobile || 'No mobile'}</option>)}
                </select>
              </label>
              <label>
                Follow-up Status
                <select value={categoryForm.followup_status} onChange={(event) => updateCategoryForm('followup_status', event.target.value)}>
                  {followupStatusOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </label>
              <label className="wide-field">
                Payment Link
                <input value={categoryForm.payment_link} onChange={(event) => updateCategoryForm('payment_link', event.target.value)} placeholder="https://payment-link.example" />
              </label>
            </div>
            <div className="reminder-category-actions">
              <label className="inline-check">
                <input type="checkbox" checked={categoryForm.auto_reminder} onChange={(event) => updateCategoryForm('auto_reminder', event.target.checked)} />
                Auto Reminder {categoryForm.auto_reminder ? 'ON' : 'OFF'}
              </label>
              <label className="inline-check">
                <input type="checkbox" checked={categoryForm.is_active} onChange={(event) => updateCategoryForm('is_active', event.target.checked)} />
                Active
              </label>
              <ShellButton type="submit" disabled={categorySaving}>
                <CheckCircle2 size={16} /> {categorySaving ? 'Saving...' : editingCategoryId ? 'Update Category' : 'Create Category'}
              </ShellButton>
              {editingCategoryId && <button type="button" className="table-link button-link" onClick={resetCategoryForm}>Cancel Edit</button>}
            </div>
          </form>
          <div className="reminder-category-grid">
            {reminderCategories.map((category) => {
              const statusText = followupStatusOptions.find((item) => item.value === category.followup_status)?.label || category.followup_status || 'Pending';
              const repeatText = repeatReminderOptions.find((item) => item.value === category.repeat_reminder)?.label || category.repeat_reminder || 'Daily';
              return (
                <button type="button" className={`reminder-category-tile ${Number(category.is_active) ? 'active' : 'inactive'}`} key={category.id} onClick={() => editReminderCategory(category)}>
                  <span>{Number(category.auto_reminder) ? 'Auto ON' : 'Auto OFF'}</span>
                  <strong>{category.name}</strong>
                  <small>{category.reminder_before_days} days before - App Notification - {repeatText}</small>
                  <em>{category.assigned_employee_name ? `Assigned to ${category.assigned_employee_name}` : 'No employee assigned'}</em>
                  <b>{statusText}</b>
                </button>
              );
            })}
            {reminderCategories.length === 0 && <div className="empty-state">No reminder category configured yet.</div>}
          </div>
        </section>
      )}
      {canSendAppReminders && (
        <section className="workspace-card glass-panel">
          <div className="section-title-row">
            <h3>User wise app reminder</h3>
            <small>{recipientOptions.length} recipients ready</small>
          </div>
          <div className="template-chip-row">
            {reminderTemplates.map((template) => (
              <button type="button" key={template.key} onClick={() => applyReminderTemplate(template)}>{template.label}</button>
            ))}
          </div>
          <form onSubmit={sendAppReminder}>
            <div className="form-grid">
              <label>
                Send To
                <select value={notifyMode} onChange={(event) => { setNotifyMode(event.target.value); setNotifyTarget(''); setNotifyCategory(''); }}>
                  <option value="single">Single User / Customer</option>
                  <option value="category">User Category</option>
                </select>
              </label>
              <label>
                Recipient
                {notifyMode === 'single' ? (
                  <select value={notifyTarget} onChange={(event) => setNotifyTarget(event.target.value)} required>
                    <option value="">Select user or customer</option>
                    {recipientOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                ) : (
                  <select value={notifyCategory} onChange={(event) => setNotifyCategory(event.target.value)} required>
                    <option value="">Select category</option>
                    {reminderCategoryOptions.map((item) => <option key={item.value} value={item.value}>{item.label} ({item.count})</option>)}
                  </select>
                )}
              </label>
              <label>
                Title
                <input value={notifyTitle} onChange={(event) => setNotifyTitle(event.target.value)} placeholder="Reminder title" required />
              </label>
            </div>
            <div className="form-grid compact reminder-compose-grid">
              <label>
                Text Message
                <textarea value={notifyMessage} onChange={(event) => setNotifyMessage(event.target.value)} placeholder="Message shown in app" required />
              </label>
              <label>
                <span className="inline-check">
                  <input type="checkbox" checked={notifyVoice} onChange={(event) => setNotifyVoice(event.target.checked)} />
                  Voice reminder
                </span>
                <textarea value={notifyVoiceText} onChange={(event) => setNotifyVoiceText(event.target.value)} placeholder="Voice text" disabled={!notifyVoice} />
              </label>
            </div>
            <ShellButton type="submit" disabled={sendingReminder}>
              <Send size={16} /> {sendingReminder ? 'Sending...' : 'Send App Reminder'}
            </ShellButton>
          </form>
        </section>
      )}
      <section className="workspace-card glass-panel">
        <div className="section-title-row">
          <h3>Upcoming renewal reminders</h3>
          <small>{reminderFilter === '15' ? 'Showing 15 day priority renewals' : reminderFilter === 'payment' ? 'Showing payment follow-up reminders' : 'Showing 30 day renewal window'}</small>
        </div>
        <div className="mini-table">
          {visibleReminders.slice(0, 8).map((row) => (
            <div className="mini-table-row" key={row.id}>
              <strong>{row.name}</strong>
              <span>{row.vehicle_no || row.mobile}</span>
              <b>{row.renewal_date ? formatDate(row.renewal_date) : '-'}</b>
              <em>{row.payment_status || 'pending'}</em>
              <small>
                {canSendAppReminders && (
                  <button type="button" className="table-link button-link" onClick={() => prefillAppReminder(row)}>Prepare app reminder</button>
                )}
                <button type="button" className="table-link button-link" onClick={() => addWhatsAppReminderToPanel(row)}>Add to WhatsApp panel</button>
              </small>
            </div>
          ))}
          {visibleReminders.length === 0 && <div className="empty-state">No renewal reminders pending right now.</div>}
        </div>
      </section>
      <section className="workspace-card glass-panel">
        <div className="section-title-row">
          <h3>Reminder services</h3>
          <small>Service status</small>
        </div>
        <div className="action-tile-grid">
          {['Daily dealer notification', 'Customer WhatsApp reminder', 'Voice alert playback', '30 day renewal window', '15 day priority window', 'Payment follow-up'].map((item) => (
            <button type="button" className="action-tile" onClick={() => runReminderService(item)} key={item}>{item}</button>
          ))}
        </div>
      </section>
    </motion.div>
  );
}

const toDateTimeInput = (value) => {
  if (!value) return '';
  return String(value).replace(' ', 'T').slice(0, 16);
};

const getTrackingForm = (customer) => ({
  gps_tracking_enabled: Boolean(customer.gps_tracking_enabled),
  last_data_time: toDateTimeInput(customer.last_data_time),
  gps_online_status: customer.gps_online_status || 'offline',
  gps_speed: customer.gps_speed || 0,
  gps_ignition: Boolean(customer.gps_ignition),
  gps_motion_status: customer.gps_motion_status || 'Stop',
  gps_address: customer.gps_address || '',
  gps_direction: customer.gps_direction || '',
  gps_signal_status: customer.gps_signal_status || '',
  gsm_signal: customer.gsm_signal || '',
  battery_voltage: customer.battery_voltage || ''
});

const hasGpsLocation = (item) => item?.gps_latitude !== undefined && item?.gps_latitude !== null && item?.gps_latitude !== '' && item?.gps_longitude !== undefined && item?.gps_longitude !== null && item?.gps_longitude !== '';
const MAX_MERCATOR_LATITUDE = 85.05112878;

const clampMapLatitude = (latitude) => Math.max(Math.min(Number(latitude), MAX_MERCATOR_LATITUDE), -MAX_MERCATOR_LATITUDE);
const getTileX = (longitude, zoom) => ((Number(longitude) + 180) / 360) * (2 ** zoom);
const getTileY = (latitude, zoom) => {
  const latRad = (clampMapLatitude(latitude) * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(latRad) + (1 / Math.cos(latRad))) / Math.PI) / 2) * (2 ** zoom);
};

const toLiveGpsPoint = (item) => {
  const latitude = Number(item?.latitude ?? item?.gps_latitude ?? item?.last_latitude);
  const longitude = Number(item?.longitude ?? item?.gps_longitude ?? item?.last_longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null;
  return {
    latitude,
    longitude,
    event_time: item?.event_time || item?.last_data_time || item?.gps_last_packet_at || item?.last_packet_time,
    speed: item?.speed ?? item?.gps_speed ?? item?.last_speed,
    ignition: item?.ignition ?? item?.gps_ignition,
    motion_status: item?.motion_status || item?.gps_motion_status,
    address: item?.address || item?.gps_address || item?.current_address,
    protocol: item?.protocol || item?.gps_protocol
  };
};

const buildGpsDeviceFromEvent = (event, existing = {}) => ({
  ...existing,
  id: existing.id || event.device_id || `live-${event.imei || 'device'}`,
  customer_id: existing.customer_id || event.customer_id || null,
  customer_name: existing.customer_name || event.customer_name || null,
  imei: existing.imei || event.imei || '',
  device_name: existing.device_name || event.customer_name || event.imei || 'GPS Device',
  device_type: existing.device_type || 'GPS',
  live_status: 'active',
  status: 'active',
  last_latitude: event.latitude,
  last_longitude: event.longitude,
  last_speed: event.speed,
  ignition: event.ignition,
  motion_status: event.motion_status,
  current_address: event.address,
  gps_signal_status: event.gps_signal_status,
  battery_voltage: event.battery_voltage,
  protocol: event.protocol,
  source_ip: event.source_ip,
  source_port: event.source_port,
  last_packet_time: event.event_time
});

const upsertGpsDeviceFromEvent = (current, event) => {
  if (!event?.imei) return current;
  const imei = String(event.imei || '');
  const exists = current.some((item) => String(item.imei || '') === imei);
  if (!exists) return [buildGpsDeviceFromEvent(event), ...current];
  return current.map((item) => (
    String(item.imei || '') === imei ? buildGpsDeviceFromEvent(event, item) : item
  ));
};

const chooseLiveMapZoom = (points) => {
  if (!points || points.length < 2) return 15;
  const latitudes = points.map((point) => point.latitude);
  const longitudes = points.map((point) => point.longitude);
  const spread = Math.max(
    Math.max(...latitudes) - Math.min(...latitudes),
    Math.max(...longitudes) - Math.min(...longitudes)
  );
  if (spread > 1) return 9;
  if (spread > 0.5) return 10;
  if (spread > 0.2) return 11;
  if (spread > 0.08) return 12;
  if (spread > 0.03) return 13;
  if (spread > 0.01) return 14;
  return 15;
};

function LiveTrackingMap({ vehicle, points = [], mode = 'live', fleetVehicles = [], mapTheme = 'light', fullscreen = false }) {
  const validPoints = points.map(toLiveGpsPoint).filter(Boolean);
  const routePoints = validPoints.filter((point, index, list) => (
    index === 0 || point.latitude !== list[index - 1].latitude || point.longitude !== list[index - 1].longitude
  ));
  const fleetPoints = fleetVehicles.map(toLiveGpsPoint).filter(Boolean);
  const fallbackCenter = fleetPoints[0] || {
    latitude: 22.9734,
    longitude: 78.6569,
    event_time: null,
    speed: 0,
    motion_status: 'Waiting',
    address: 'India overview'
  };
  const latest = routePoints[routePoints.length - 1] || fallbackCenter;
  const hasPrimaryPoint = routePoints.length > 0;

  const zoom = hasPrimaryPoint
    ? chooseLiveMapZoom(routePoints.slice(-40))
    : (fleetPoints.length ? chooseLiveMapZoom(fleetPoints) : 5);
  const centerX = getTileX(latest.longitude, zoom);
  const centerY = getTileY(latest.latitude, zoom);
  const startX = centerX - 1.5;
  const startY = centerY - 1.5;
  const minTileX = Math.floor(startX);
  const maxTileX = Math.floor(startX + 3);
  const minTileY = Math.floor(startY);
  const maxTileY = Math.floor(startY + 3);
  const tileCount = 2 ** zoom;
  const tiles = [];

  for (let x = minTileX; x <= maxTileX; x += 1) {
    for (let y = minTileY; y <= maxTileY; y += 1) {
      if (y < 0 || y >= tileCount) continue;
      const wrappedX = ((x % tileCount) + tileCount) % tileCount;
      tiles.push({
        key: `${x}-${y}`,
        src: `https://tile.openstreetmap.org/${zoom}/${wrappedX}/${y}.png`,
        left: `${((x - startX) / 3) * 100}%`,
        top: `${((y - startY) / 3) * 100}%`
      });
    }
  }

  const projectPoint = (point) => ({
    x: ((getTileX(point.longitude, zoom) - startX) / 3) * 100,
    y: ((getTileY(point.latitude, zoom) - startY) / 3) * 100
  });
  const projectedRoute = routePoints.slice(-80).map(projectPoint);
  const latestProjection = projectPoint(latest);
  const routePath = projectedRoute.map((point) => `${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(' ');
  const speed = Number(latest.speed ?? vehicle?.gps_speed ?? vehicle?.last_speed ?? 0);
  const status = latest.motion_status || vehicle?.gps_motion_status || vehicle?.motion_status || (hasPrimaryPoint ? 'Stop' : 'Waiting');
  const isMoving = status.toLowerCase() === 'moving' || speed > 0;
  const vehicleLabel = vehicle?.vehicle_no || vehicle?.device_name || vehicle?.name || 'GPS Vehicle';
  const lastUpdate = latest.event_time ? formatDateTime(latest.event_time) : 'Waiting for first GPS packet';
  const getStatusClass = (item, point) => {
    const liveStatus = String(item?.live_status || item?.status || item?.gps_online_status || '').toLowerCase();
    if (liveStatus.includes('expired')) return 'expired';
    if (liveStatus.includes('never')) return 'never-connected';
    if (liveStatus.includes('offline')) return 'offline';
    const itemSpeed = Number(point?.speed ?? item?.gps_speed ?? item?.last_speed ?? 0);
    return itemSpeed > 0 ? 'moving' : 'active';
  };
  const fleetMarkers = fleetVehicles
    .map((item) => {
      const point = toLiveGpsPoint(item);
      if (!point) return null;
      const projected = projectPoint(point);
      if (projected.x < -20 || projected.x > 120 || projected.y < -20 || projected.y > 120) return null;
      return {
        key: item.id || item.imei || item.customer_id,
        item,
        point,
        x: projected.x,
        y: projected.y,
        label: item.vehicle_no || item.device_name || item.name || item.customer_name || 'Vehicle',
        statusClass: getStatusClass(item, point)
      };
    })
    .filter(Boolean);
  const clusteredMarkers = Object.values(fleetMarkers.reduce((groups, marker) => {
    const key = `${Math.round(marker.x / 5)}-${Math.round(marker.y / 5)}`;
    groups[key] = groups[key] || [];
    groups[key].push(marker);
    return groups;
  }, {}));

  return (
    <div className={`live-map-panel ${mapTheme === 'dark' ? 'dark' : ''} ${fullscreen ? 'fullscreen' : ''} ${hasPrimaryPoint ? '' : 'no-data'}`}>
      <div className="live-map-tiles" aria-hidden="true">
        {tiles.map((tile) => (
          <img
            alt=""
            className="live-map-tile"
            draggable="false"
            key={tile.key}
            src={tile.src}
            style={{ left: tile.left, top: tile.top }}
          />
        ))}
      </div>
      <div className="live-map-statusbar">
        <span>{mode === 'playback' ? 'Playback' : mode === 'history' ? 'History' : 'Live Map'}</span>
        <b>{fleetVehicles.length || 0} vehicles</b>
        <em>Auto refresh 15 sec</em>
      </div>
      <div className="live-map-controls" aria-hidden="true">
        <span>+</span>
        <span>-</span>
        <span>⌖</span>
      </div>
      <svg className="live-map-route" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        {projectedRoute.length > 1 && <polyline points={routePath} />}
      </svg>
      {hasPrimaryPoint && (
        <div className={`live-map-marker ${isMoving ? 'moving' : ''}`} style={{ left: `${latestProjection.x}%`, top: `${latestProjection.y}%` }}>
          <span className="live-map-marker-card">
            <strong>{vehicleLabel}</strong>
            <small>{speed.toFixed(0)} km/h | {status}</small>
          </span>
          <span className="live-map-marker-dot" />
        </div>
      )}
      {clusteredMarkers.map((group) => {
        const marker = group[0];
        if (group.length > 1) {
          return (
            <div className="fleet-map-cluster" key={`cluster-${marker.x}-${marker.y}`} style={{ left: `${marker.x}%`, top: `${marker.y}%` }}>
              <strong>{group.length}</strong>
              <span>vehicles</span>
            </div>
          );
        }
        return (
          <div className={`fleet-map-marker ${marker.statusClass}`} key={marker.key} style={{ left: `${marker.x}%`, top: `${marker.y}%` }}>
            <span className="fleet-map-dot" />
            <span className="fleet-map-popup">
              <strong>{marker.label}</strong>
              <small>{Number(marker.point.speed || 0).toFixed(0)} km/h | {marker.point.address || marker.item.current_address || 'Address pending'}</small>
              <small>{marker.point.event_time ? formatDateTime(marker.point.event_time) : 'No update time'}</small>
            </span>
          </div>
        );
      })}
      {!hasPrimaryPoint && (
        <div className="live-map-waiting-card">
          <strong>Map ready</strong>
          <span>Once the device GPS packet is received, marker, route, and speed will appear live.</span>
          <small>{vehicle?.imei || vehicle?.sim_no || 'IMEI/SIM waiting'}</small>
        </div>
      )}
      <div className="live-map-legend">
        <span className="active">Online</span>
        <span className="moving">Moving</span>
        <span className="offline">Offline</span>
        <span className="expired">Expired</span>
      </div>
      <div className="live-map-info">
        <span>{mode === 'playback' ? 'Playback route' : mode === 'history' ? 'History route' : 'Live device location'}</span>
        <strong>{latest.latitude.toFixed(6)}, {latest.longitude.toFixed(6)}</strong>
        <small>{lastUpdate}</small>
        <small>{hasPrimaryPoint ? (latest.address || latest.protocol || vehicle?.gps_protocol || 'GPS device packet received') : 'Waiting for device live GPS data'}</small>
      </div>
      <a className="live-map-credit" href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>
    </div>
  );
}

function GPSTrackingCenter({ user, gpsView = '' }) {
  const [customers, setCustomers] = useState([]);
  const [gpsDevices, setGpsDevices] = useState([]);
  const [inventoryStock, setInventoryStock] = useState([]);
  const [gpsReports, setGpsReports] = useState([]);
  const [commandHistory, setCommandHistory] = useState([]);
  const [selected, setSelected] = useState(null);
  const [trackingForm, setTrackingForm] = useState({ gps_tracking_enabled: true });
  const [deviceForm, setDeviceForm] = useState({ inventory_id: '', imei: '', sim_no: '', device_name: '', device_type: 'GPS', device_model: '', status: 'never_connected', expiry_date: '' });
  const [gpsHistory, setGpsHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [trackingView, setTrackingView] = useState('live');
  const [trackingSideTab, setTrackingSideTab] = useState('commands');
  const [mapTheme, setMapTheme] = useState('light');
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [actionLog, setActionLog] = useState([]);
  const [selectedGpsSidebarOption, setSelectedGpsSidebarOption] = useState(null);
  const [reportPreview, setReportPreview] = useState(null);
  const [reportPreviewLoading, setReportPreviewLoading] = useState(false);
  const [commandSending, setCommandSending] = useState('');
  const [protocolRows, setProtocolRows] = useState([]);
  const [protocolSummary, setProtocolSummary] = useState({});
  const [protocolSearch, setProtocolSearch] = useState('');
  const [protocolLoading, setProtocolLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const selectedFormCustomerId = useRef(null);
  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);
  const canManage = ['superadmin', 'admin', 'whitelabel_admin', 'employee', 'dealer'].includes(user?.role);
  const canModifyGps = isWorkspaceAdmin(user?.role);
  const canFetchInventory = ['superadmin', 'admin', 'whitelabel_admin', 'employee'].includes(user?.role);

  const fetchTracking = useCallback(async () => {
    try {
      const [trackingRes, deviceRes, reportRes, commandRes, stockRes] = await Promise.all([
        axios.get(`${API_URL}/api/customers/tracking`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/gps/devices`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/gps/reports`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/gps/commands/history`, { headers: getHeaders() }),
        canFetchInventory ? axios.get(`${API_URL}/api/inventory/stock`, { headers: getHeaders() }) : Promise.resolve({ data: [] }),
      ]);
      const devices = (deviceRes.data || []).filter((item) => (
        item.sale_id || item.inventory_id || item.last_packet_time || toLiveGpsPoint(item)
      ));
      const salesCustomerIds = new Set(devices.map((item) => Number(item.customer_id)).filter(Boolean));
      const rows = (trackingRes.data || []).filter((item) => item.tracking_sale_id || item.sale_imei || salesCustomerIds.has(Number(item.id)));
      setCustomers(rows);
      setGpsDevices(devices);
      setInventoryStock(stockRes.data || []);
      setGpsReports(reportRes.data || []);
      setCommandHistory(commandRes.data || []);
      setSelected((current) => {
        const firstDeviceCustomer = devices[0]?.customer_id;
        if (!current) return rows.find((row) => Number(row.id) === Number(firstDeviceCustomer)) || rows[0] || null;
        return rows.find((row) => Number(row.id) === Number(current.id)) || rows.find((row) => Number(row.id) === Number(firstDeviceCustomer)) || rows[0] || null;
      });
    } catch (err) {
      console.log(err);
    }
  }, [canFetchInventory, getHeaders]);

  useEffect(() => { fetchTracking(); }, [fetchTracking]);
  useEffect(() => {
    const timer = setInterval(fetchTracking, 5000);
    return () => clearInterval(timer);
  }, [fetchTracking]);

  const fetchProtocolMonitor = useCallback(async () => {
    if (user?.role !== 'superadmin') return;
    setProtocolLoading((current) => current || protocolRows.length === 0);
    try {
      const res = await axios.get(`${API_URL}/api/gps/protocol-monitor`, {
        headers: getHeaders(),
        params: protocolSearch ? { search: protocolSearch } : {}
      });
      setProtocolRows(res.data.rows || []);
      setProtocolSummary(res.data.summary || {});
    } catch (err) {
      console.log(err);
    } finally {
      setProtocolLoading(false);
    }
  }, [getHeaders, protocolRows.length, protocolSearch, user?.role]);

  useEffect(() => {
    if (gpsView !== 'gps-protocol-monitor' || user?.role !== 'superadmin') return undefined;
    fetchProtocolMonitor();
    const timer = setInterval(fetchProtocolMonitor, 5000);
    return () => clearInterval(timer);
  }, [fetchProtocolMonitor, gpsView, user?.role]);

  const fetchGpsHistory = useCallback(async (customerId) => {
    if (!customerId) {
      setGpsHistory([]);
      return;
    }
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/gps/history/${customerId}`, { headers: getHeaders() });
      setGpsHistory(res.data || []);
    } catch (err) {
      console.log(err);
      setGpsHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    const socket = io(API_URL, {
      auth: { token: localStorage.getItem('token') },
      transports: ['websocket', 'polling']
    });
    socket.on('gps:location', (event) => {
      setCustomers((current) => current.map((item) => (
        Number(item.id) === Number(event.customer_id)
          ? {
            ...item,
            gps_tracking_enabled: true,
            gps_latitude: event.latitude,
            gps_longitude: event.longitude,
            gps_speed: event.speed,
            gps_ignition: event.ignition,
            gps_motion_status: event.motion_status,
            gps_address: event.address,
            gps_signal_status: event.gps_signal_status,
            battery_voltage: event.battery_voltage,
            gps_protocol: event.protocol,
            gps_device_ip: event.source_ip,
            last_data_time: event.event_time,
            gps_online_status: 'online'
          }
          : item
      )));
      setGpsDevices((current) => upsertGpsDeviceFromEvent(current, event));
      setSelected((current) => (Number(current?.id) === Number(event.customer_id) ? {
        ...current,
        gps_latitude: event.latitude,
        gps_longitude: event.longitude,
        gps_speed: event.speed,
        gps_ignition: event.ignition,
        gps_motion_status: event.motion_status,
        gps_address: event.address,
        gps_signal_status: event.gps_signal_status,
        battery_voltage: event.battery_voltage,
        gps_protocol: event.protocol,
        gps_device_ip: event.source_ip,
        last_data_time: event.event_time,
        gps_online_status: 'online'
      } : current));
      if (Number(selectedFormCustomerId.current) === Number(event.customer_id)) {
        fetchGpsHistory(event.customer_id);
      }
    });
    socket.on('gps:command', (event) => {
      setCommandHistory((current) => [event, ...current].slice(0, 200));
    });
    return () => socket.disconnect();
  }, [fetchGpsHistory]);

  useEffect(() => {
    if (!selected) {
      setGpsHistory([]);
      selectedFormCustomerId.current = null;
      return;
    }
    if (Number(selectedFormCustomerId.current) !== Number(selected.id)) {
      setTrackingForm(getTrackingForm(selected));
      setTrackingView('live');
      setTrackingSideTab('commands');
      setReportPreview(null);
      selectedFormCustomerId.current = selected.id;
    }
    fetchGpsHistory(selected.id);
  }, [fetchGpsHistory, selected]);

  useEffect(() => {
    const customerId = selected?.id;
    if (!customerId) return undefined;
    const timer = setInterval(() => fetchGpsHistory(customerId), 5000);
    return () => clearInterval(timer);
  }, [fetchGpsHistory, selected?.id]);

  useEffect(() => {
    if (!gpsView) return;
    const sidebarActionMap = {
      'gps-command-lock': { type: 'command', label: 'Engine Lock / Immobilizer' },
      'gps-command-location': { type: 'command', label: 'Get Current Location' },
      'gps-command-reboot': { type: 'command', label: 'Reboot Device' },
      'gps-command-apn': { type: 'command', label: 'Change APN' },
      'gps-command-geofence': { type: 'command', label: 'Set Geo-fence' },
      'gps-command-sos': { type: 'command', label: 'Enable SOS' },
      'gps-report-trip': { type: 'report', label: 'Trip Report' },
      'gps-report-km': { type: 'report', label: 'Distance / KM Report' },
      'gps-report-stop-idle': { type: 'report', label: 'Stop Report' },
      'gps-report-overspeed': { type: 'report', label: 'Overspeed Report' },
      'gps-report-daily-summary': { type: 'report', label: 'Daily Summary Report' },
      'gps-report-offline': { type: 'report', label: 'Device Offline Report' },
      'gps-alert-overspeed': { type: 'alert', label: 'Overspeed Alert' },
      'gps-alert-ignition': { type: 'alert', label: 'Ignition ON/OFF Alert' },
      'gps-alert-power': { type: 'alert', label: 'Power Cut Alert' },
      'gps-alert-low-battery': { type: 'alert', label: 'Low Battery Alert' },
      'gps-alert-geofence': { type: 'alert', label: 'Geo-fence Entry/Exit Alert' },
      'gps-alert-sos': { type: 'alert', label: 'SOS Alert' }
    };
    if (gpsView === 'gps-live-map') {
      setSelectedGpsSidebarOption(null);
      setTrackingView('live');
      return;
    }
    if (gpsView === 'gps-playback') {
      setSelectedGpsSidebarOption(null);
      setTrackingView('playback');
      setTrackingSideTab('history');
      return;
    }
    if (gpsView === 'gps-history') {
      setSelectedGpsSidebarOption(null);
      setTrackingView('history');
      setTrackingSideTab('history');
      return;
    }
    if (gpsView === 'gps-protocol-monitor') {
      setSelectedGpsSidebarOption(null);
      return;
    }
    const sidebarAction = sidebarActionMap[gpsView];
    if (sidebarAction) {
      setSelectedGpsSidebarOption({ key: gpsView, ...sidebarAction });
      setTrackingSideTab(sidebarAction.type === 'command' ? 'commands' : 'reports');
      if (sidebarAction.type !== 'report') setReportPreview(null);
    }
  }, [gpsView]);

  const startEdit = (customer) => {
    setSelected(customer);
    setTrackingForm(getTrackingForm(customer));
    selectedFormCustomerId.current = customer.id;
  };

  const saveTracking = async () => {
    if (!canModifyGps) return alert('Only admin can update tracking details');
    if (!selected) return alert('Please select a customer');
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/customers/${selected.id}/tracking`, trackingForm, { headers: getHeaders() });
      await fetchTracking();
      alert('GPS tracking updated successfully');
    } catch (err) {
      alert(err.response?.data?.message || 'Could not update GPS tracking');
    } finally {
      setSaving(false);
    }
  };

  const resetDeviceForm = () => setDeviceForm({ inventory_id: '', imei: '', sim_no: '', device_name: '', device_type: 'GPS', device_model: '', status: 'never_connected', expiry_date: '' });

  const editDevice = (device) => {
    setDeviceForm({
      id: device.id,
      customer_id: device.customer_id || '',
      inventory_id: device.inventory_id || '',
      imei: device.imei || '',
      sim_no: device.sim_no || '',
      device_name: device.device_name || '',
      device_type: device.device_type || 'GPS',
      device_model: device.device_model || '',
      protocol: device.protocol || '',
      status: device.status || device.live_status || 'never_connected',
      expiry_date: device.expiry_date ? String(device.expiry_date).slice(0, 10) : ''
    });
  };

  const inventoryDeviceLabel = (item) => {
    const name = item.device_name || item.product_name || item.name || 'Inventory device';
    const imeiText = item.imei ? `IMEI ${item.imei}` : 'No IMEI';
    const simText = item.sim_no || item.sim_no_2 || item.serial_no || 'No SIM';
    return `${name} | ${imeiText} | ${simText} | ${item.status}`;
  };

  const applyInventoryToDeviceForm = (inventoryId) => {
    const item = inventoryStock.find((row) => String(row.id) === String(inventoryId));
    setDeviceForm((current) => {
      if (!item) return { ...current, inventory_id: inventoryId || '' };
      return {
        ...current,
        inventory_id: inventoryId,
        customer_id: current.customer_id || selected?.id || '',
        imei: item.imei || current.imei || '',
        sim_no: item.sim_no || item.sim_no_2 || item.serial_no || current.sim_no || '',
        device_name: item.device_name || item.product_name || item.name || current.device_name || '',
        device_type: item.device_type || item.product_type || item.type || current.device_type || 'GPS',
        device_model: item.model || item.device_oem_name || current.device_model || '',
        status: item.status === 'sold' ? 'active' : current.status || 'never_connected'
      };
    });
  };

  const saveGpsDevice = async () => {
    if (deviceForm.id && !canModifyGps) return alert('Only admin can update GPS devices');
    if (!deviceForm.imei) return alert('IMEI is required');
    setSaving(true);
    try {
      const url = deviceForm.id ? `${API_URL}/api/gps/devices/${deviceForm.id}` : `${API_URL}/api/gps/devices`;
      const method = deviceForm.id ? 'patch' : 'post';
      await axios[method](url, deviceForm, { headers: getHeaders() });
      await fetchTracking();
      resetDeviceForm();
      alert('GPS device saved successfully');
    } catch (err) {
      alert(err.response?.data?.message || 'Could not save GPS device');
    } finally {
      setSaving(false);
    }
  };

  const deleteGpsDevice = async (deviceId) => {
    if (!canModifyGps) return alert('Only admin can delete GPS devices');
    if (!deviceId) return;
    try {
      await axios.delete(`${API_URL}/api/gps/devices/${deviceId}`, { headers: getHeaders() });
      await fetchTracking();
    } catch (err) {
      alert(err.response?.data?.message || 'Could not delete GPS device');
    }
  };

  const sendDeviceCommand = async (label, deviceOverride = null) => {
    const commandDevice = deviceOverride || selectedDevice;
    if (!commandDevice?.id || !Number.isFinite(Number(commandDevice.id))) return alert('Device mapping is not ready for command');
    if (/engine lock|engine unlock|factory reset/i.test(label) && !window.confirm(`Send ${label} command to ${commandDevice.imei || 'selected device'}?`)) return;
    setTrackingSideTab('commands');
    const commandKey = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    setCommandSending(label);
    try {
      const res = await axios.post(`${API_URL}/api/gps/devices/${commandDevice.id}/commands`, {
        command_key: commandKey,
        command_name: label,
        command_payload: label
      }, { headers: getHeaders() });
      setCommandHistory((current) => [res.data.command, ...current].filter(Boolean).slice(0, 200));
      pushActionLog('Command', `${label} - ${res.data.command?.status || 'queued'}`);
    } catch (err) {
      alert(err.response?.data?.message || 'Could not send GPS command');
    } finally {
      setCommandSending('');
    }
  };

  const getOpenStreetMapUrl = (item, embed = false) => {
    const point = toLiveGpsPoint(item);
    if (!point) return '';
    const lat = point.latitude;
    const lng = point.longitude;
    if (embed) {
      const pad = 0.01;
      return `https://www.openstreetmap.org/export/embed.html?bbox=${lng - pad}%2C${lat - pad}%2C${lng + pad}%2C${lat + pad}&layer=mapnik&marker=${lat}%2C${lng}`;
    }
    return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=16/${lat}/${lng}`;
  };
  const getHistoryOpenStreetMapUrl = (point, embed = false) => getOpenStreetMapUrl({ gps_latitude: point?.latitude, gps_longitude: point?.longitude }, embed);
  const historyPoints = useMemo(
    () => gpsHistory
      .filter((point) => point.latitude !== undefined && point.latitude !== null && point.longitude !== undefined && point.longitude !== null)
      .slice()
      .reverse(),
    [gpsHistory]
  );
  const distanceBetween = (start, end) => {
    const lat1 = Number(start.latitude);
    const lon1 = Number(start.longitude);
    const lat2 = Number(end.latitude);
    const lon2 = Number(end.longitude);
    if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return 0;
    const toRad = (value) => (value * Math.PI) / 180;
    const earthKm = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2
      + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return earthKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };
  const routeDistance = historyPoints.reduce((sum, point, index) => (
    index === 0 ? 0 : sum + distanceBetween(historyPoints[index - 1], point)
  ), 0);
  const maxSpeed = gpsHistory.reduce((max, point) => Math.max(max, Number(point.speed || 0)), 0);
  const latestHistoryPoint = historyPoints[historyPoints.length - 1];
  const selectedMapPoint = toLiveGpsPoint(selected);
  const historyMapPoints = historyPoints.map(toLiveGpsPoint).filter(Boolean);
  const salesMappedDevices = gpsDevices.filter((item) => (
    item.sale_id || item.inventory_id || item.last_packet_time || toLiveGpsPoint(item)
  ));
  const salesMappedCustomers = customers.filter((customer) => customer.tracking_sale_id || customer.sale_imei);
  const selectedDevice = salesMappedDevices.find((item) => (
    (selected?.imei && String(item.imei || '') === String(selected.imei))
    || Number(item.customer_id) === Number(selected?.id)
  ));
  const fleetMapVehicles = [
    ...salesMappedCustomers.filter((item) => hasGpsLocation(item)),
    ...salesMappedDevices.filter((item) => toLiveGpsPoint(item)),
  ];
  const liveMapPoints = trackingView === 'live'
    ? [...historyMapPoints.slice(-24), selectedMapPoint].filter(Boolean)
    : (historyMapPoints.length ? historyMapPoints : [selectedMapPoint].filter(Boolean));
  const activeCount = customers.filter((item) => item.gps_tracking_enabled || hasGpsLocation(item) || item.imei).length;
  const deviceStatusCounts = salesMappedDevices.reduce((counts, item) => {
    const key = item.live_status || item.status || 'never_connected';
    counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, {});
  const pendingCommandCount = commandHistory.filter((item) => String(item.status || '').toLowerCase() === 'pending').length;
  const sentCommandCount = commandHistory.filter((item) => ['sent', 'success'].includes(String(item.status || '').toLowerCase())).length;
  const todayDistance = routeDistance;
  const openTrackingUrl = trackingView !== 'live' && latestHistoryPoint
    ? getHistoryOpenStreetMapUrl(latestHistoryPoint)
    : (getOpenStreetMapUrl(selected) || (latestHistoryPoint ? getHistoryOpenStreetMapUrl(latestHistoryPoint) : ''));
  const liveTrackingItems = [
    ['Vehicle live location', selectedMapPoint ? `${selectedMapPoint.latitude}, ${selectedMapPoint.longitude}` : (selected?.gps_address || 'Waiting for device data')],
    ['Speed', `${Number(selected?.gps_speed || 0)} km/h`],
    ['Ignition', selected?.gps_ignition ? 'ON' : 'OFF'],
    ['Vehicle status', selected?.gps_motion_status || 'Stop'],
    ['Last update time', selected?.last_data_time ? formatDateTime(selected.last_data_time) : '-'],
    ['Today distance', `${todayDistance.toFixed(2)} km`],
    ['Parking status', Number(selected?.gps_speed || 0) > 0 ? 'Running' : 'Parked'],
    ['Direction', selected?.gps_direction || '-'],
    ['GPS signal', selected?.gps_signal_status || '-'],
    ['Satellite status', selected?.gps_satellites ? `${selected.gps_satellites} satellites` : '-'],
    ['GSM signal', selected?.gsm_signal || '-'],
    ['Battery voltage', selected?.battery_voltage || '-'],
    ['Protocol', selected?.gps_protocol || '-'],
    ['Device IP', selected?.gps_device_ip || '-'],
    ['Device status', selectedDevice?.live_status || selectedDevice?.status || selected?.gps_online_status || '-'],
    ['Last packet', selectedDevice?.last_packet_time ? formatDateTime(selectedDevice.last_packet_time) : (selected?.gps_last_packet_at ? formatDateTime(selected.gps_last_packet_at) : '-')],
    ['Expiry date', selectedDevice?.expiry_date ? formatDate(selectedDevice.expiry_date) : (selected?.renewal_date ? formatDate(selected.renewal_date) : '-')],
  ];
  const historySummaryItems = [
    ['History points', historyLoading ? 'Loading...' : gpsHistory.length],
    ['Route distance', `${routeDistance.toFixed(2)} km`],
    ['Max speed', `${maxSpeed.toFixed(0)} km/h`],
    ['Last event', latestHistoryPoint?.event_time ? formatDateTime(latestHistoryPoint.event_time) : '-'],
  ];
  const reportOptions = ['Trip Report', 'Distance / KM Report', 'Stop Report', 'Idle Report', 'Parking Report', 'Overspeed Report', 'Ignition Report', 'Playback / Route History', 'Geo-fence Entry/Exit Report', 'Daily Summary Report', 'Device Offline Report', 'AC Report', 'Fuel Report', 'Driver Behaviour Report'];
  const alertOptions = ['Overspeed Alert', 'Ignition ON/OFF Alert', 'Power Cut Alert', 'Low Battery Alert', 'Geo-fence Entry/Exit Alert', 'Device Offline Alert', 'Towing Alert', 'Vibration Alert', 'GPS Signal Lost Alert', 'Main Battery Disconnect Alert', 'SOS Alert', 'Route Deviation Alert'];
  const commandOptions = ['Engine Lock / Immobilizer', 'Engine Unlock', 'Get Current Location', 'Reboot Device', 'Change Server IP/Port', 'Change APN', 'Set Tracking Interval', 'Set Overspeed Limit', 'Enable/Disable Sleep Mode', 'Factory Reset', 'Check Device Status', 'Set Geo-fence', 'Remove Geo-fence', 'Enable SOS', 'Disable SOS'];
  const historyOptions = ['Live Tracking', 'Route Playback', 'Today History', 'Yesterday History', 'Last 7 Days History', 'Stop History', 'Idle History', 'Parking History', 'Overspeed History', 'Ignition ON/OFF History', 'Geo-fence Entry/Exit History', 'Download CSV History', 'Share Live Location'];
  const customerAppOptions = ['Live map', 'Route playback', 'Reports download', 'Alerts notification', 'Share live location', 'Lock/unlock vehicle', 'Multiple vehicles'];
  const trackingSideTabs = [
    { key: 'commands', label: 'Command', count: commandOptions.length },
    { key: 'history', label: 'History', count: gpsHistory.length },
    { key: 'reports', label: 'Reports', count: reportOptions.length },
  ];
  const deviceModuleRows = salesMappedDevices.length
    ? salesMappedDevices
    : salesMappedCustomers.map((customer) => ({
      id: `customer-${customer.id}`,
      customer_id: customer.id,
      customer_name: customer.name,
      customer_mobile: customer.mobile,
      vehicle_no: customer.vehicle_no,
      device_name: customer.vehicle_no || customer.name,
      imei: customer.imei || customer.sale_imei,
      sim_no: customer.sim_no || customer.sale_sim_no || customer.sale_serial_no,
      sale_id: customer.tracking_sale_id,
      device_type: customer.device_type || 'GPS',
      device_model: customer.device_model,
      protocol: customer.gps_protocol,
      live_status: customer.gps_online_status || (hasGpsLocation(customer) ? 'active' : 'never_connected'),
      last_packet_time: customer.gps_last_packet_at || customer.last_data_time,
      last_latitude: customer.gps_latitude,
      last_longitude: customer.gps_longitude,
      last_speed: customer.gps_speed,
      ignition: customer.gps_ignition,
      motion_status: customer.gps_motion_status,
      current_address: customer.gps_address,
      gps_signal_status: customer.gps_signal_status,
      battery_voltage: customer.battery_voltage,
      expiry_date: customer.renewal_date
    }));
  const selectedModuleDevice = selectedDevice
    || deviceModuleRows.find((device) => Number(device.customer_id) === Number(selected?.id))
    || deviceModuleRows.find((device) => String(device.imei || '') === String(selected?.imei || ''))
    || deviceModuleRows[0]
    || null;
  const selectedModulePoints = liveMapPoints.length ? liveMapPoints : [selectedModuleDevice].filter(Boolean);
  const selectTrackingDevice = (device) => {
    const customer = customers.find((item) => (
      Number(item.id) === Number(device.customer_id)
      || (device.imei && String(item.imei || '') === String(device.imei))
    ));
    const fallbackCustomer = customer || {
      id: device.customer_id,
      name: device.customer_name || device.device_name || 'GPS Customer',
      mobile: device.customer_mobile || '',
      vehicle_no: device.vehicle_no || device.device_name || '',
      imei: device.imei || '',
      sim_no: device.sim_no || '',
      device_type: device.device_type || 'GPS',
      device_model: device.device_model || '',
      gps_protocol: device.protocol || '',
      gps_device_ip: device.source_ip || '',
      gps_last_packet_at: device.last_packet_time,
      gps_latitude: device.last_latitude,
      gps_longitude: device.last_longitude,
      gps_speed: device.last_speed,
      gps_ignition: device.ignition,
      gps_motion_status: device.motion_status,
      gps_address: device.current_address,
      gps_signal_status: device.gps_signal_status,
      battery_voltage: device.battery_voltage,
      renewal_date: device.expiry_date,
      gps_online_status: device.live_status || device.status || 'never_connected'
    };
    setSelected(fallbackCustomer);
    setTrackingForm(getTrackingForm(fallbackCustomer));
    selectedFormCustomerId.current = fallbackCustomer.id || null;
    if (fallbackCustomer.id) fetchGpsHistory(fallbackCustomer.id);
    if (!String(device.id || '').startsWith('customer-')) editDevice(device);
  };
  const deviceModuleStats = [
    ['Live Location', selectedMapPoint ? `${selectedMapPoint.latitude.toFixed(6)}, ${selectedMapPoint.longitude.toFixed(6)}` : 'Waiting'],
    ['Current Status', selected?.gps_motion_status || selectedModuleDevice?.motion_status || selectedModuleDevice?.live_status || '-'],
    ['Ignition', (selected?.gps_ignition || selectedModuleDevice?.ignition) ? 'ON' : 'OFF'],
    ['Speed', `${Number(selected?.gps_speed ?? selectedModuleDevice?.last_speed ?? 0)} km/h`],
    ['Last Update', selected?.last_data_time ? formatDateTime(selected.last_data_time) : (selectedModuleDevice?.last_packet_time ? formatDateTime(selectedModuleDevice.last_packet_time) : '-')],
    ['Today Distance', `${todayDistance.toFixed(2)} km`],
    ['Parking Status', Number(selected?.gps_speed ?? selectedModuleDevice?.last_speed ?? 0) > 0 ? 'Running' : 'Parked'],
    ['GPS Signal', selected?.gps_signal_status || selectedModuleDevice?.gps_signal_status || '-'],
    ['Battery', selected?.battery_voltage || selectedModuleDevice?.battery_voltage || '-'],
    ['Device Status', selectedModuleDevice?.live_status || selectedModuleDevice?.status || '-'],
    ['Expiry Date', selectedModuleDevice?.expiry_date ? formatDate(selectedModuleDevice.expiry_date) : '-'],
    ['Protocol', selected?.gps_protocol || selectedModuleDevice?.protocol || 'Auto']
  ];

  const pushActionLog = (type, label) => {
    setActionLog((current) => [
      {
        id: `${Date.now()}-${label}`,
        type,
        label,
        vehicle: selected?.vehicle_no || selected?.name || 'Selected vehicle',
        time: formatDateTime(new Date())
      },
      ...current
    ].slice(0, 8));
  };

  const getGpsReportRows = () => {
    const fallbackRow = {
      event_time: selected?.last_data_time || selectedModuleDevice?.last_packet_time,
      latitude: selected?.gps_latitude ?? selectedModuleDevice?.last_latitude,
      longitude: selected?.gps_longitude ?? selectedModuleDevice?.last_longitude,
      speed: selected?.gps_speed ?? selectedModuleDevice?.last_speed,
      ignition: selected?.gps_ignition ?? selectedModuleDevice?.ignition,
      motion_status: selected?.gps_motion_status || selectedModuleDevice?.motion_status,
      address: selected?.gps_address || selectedModuleDevice?.current_address,
      direction: selected?.gps_direction,
      gps_signal_status: selected?.gps_signal_status || selectedModuleDevice?.gps_signal_status,
      satellites: selected?.gps_satellites,
      gsm_signal: selected?.gsm_signal,
      battery_voltage: selected?.battery_voltage || selectedModuleDevice?.battery_voltage
    };
    return gpsHistory.length ? gpsHistory : [fallbackRow];
  };

  const getGpsReportPayload = (reportName) => {
    const today = getLocalDateKey();
    const firstPoint = historyPoints[0]?.event_time ? String(historyPoints[0].event_time).slice(0, 10) : today;
    const numericDeviceId = Number(selectedDevice?.id || selectedModuleDevice?.id || 0);
    return {
      customer_id: selected?.id || selectedModuleDevice?.customer_id || null,
      device_id: Number.isFinite(numericDeviceId) && numericDeviceId > 0 ? numericDeviceId : null,
      report_type: reportName,
      date_from: firstPoint,
      date_to: today
    };
  };

  const getBackendGpsReport = async (reportName, persist = false) => {
    const payload = getGpsReportPayload(reportName);
    if (persist) {
      const res = await axios.post(`${API_URL}/api/gps/reports`, { ...payload, metadata: { source: 'gps-tracking-center' } }, { headers: getHeaders() });
      return res.data || {};
    }
    const res = await axios.get(`${API_URL}/api/gps/reports/preview`, { headers: getHeaders(), params: payload });
    return res.data || {};
  };

  const showGpsReport = async (reportName) => {
    if (!selected && !selectedModuleDevice) return alert('Please select a customer');
    setTrackingSideTab('reports');
    setReportPreviewLoading(true);
    let rows = getGpsReportRows();
    let summary = null;
    try {
      const report = await getBackendGpsReport(reportName, false);
      rows = Array.isArray(report.rows) ? report.rows : rows;
      summary = report.summary || null;
    } catch (err) {
      console.log(err);
    }
    const latestRow = rows[rows.length - 1] || {};
    setReportPreview({
      name: reportName,
      generatedAt: formatDateTime(new Date()),
      vehicle: selected?.vehicle_no || selectedModuleDevice?.device_name || selected?.name || 'GPS Vehicle',
      imei: selected?.imei || selectedModuleDevice?.imei || latestRow.imei || '-',
      rows: rows.slice(-5).reverse(),
      totalPoints: summary?.result_rows ?? summary?.points ?? rows.length,
      routeDistance: Number(summary?.route_km ?? routeDistance).toFixed(2),
      maxSpeed: Number(summary?.max_speed ?? Math.max(maxSpeed, Number(latestRow.speed || selectedModuleDevice?.last_speed || 0))).toFixed(0),
      latestUpdate: formatDateTime(latestRow.event_time || selected?.last_data_time || selectedModuleDevice?.last_packet_time, '')
    });
    setReportPreviewLoading(false);
    pushActionLog('Report Show', reportName);
  };

  useEffect(() => {
    if (selectedGpsSidebarOption?.type !== 'report') return;
    if (selected || selectedModuleDevice) showGpsReport(selectedGpsSidebarOption.label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGpsSidebarOption?.key, selected?.id, selectedModuleDevice?.id]);

  useEffect(() => {
    if (!selectedGpsSidebarOption || selectedGpsSidebarOption.type === 'report') return;
    pushActionLog(selectedGpsSidebarOption.type === 'alert' ? 'Alert Selected' : 'Command Selected', selectedGpsSidebarOption.label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGpsSidebarOption?.key]);

  const downloadGpsReport = async (reportName) => {
    if (!selected && !selectedModuleDevice) return alert('Please select a customer');
    setTrackingSideTab('reports');
    let rows = getGpsReportRows();
    let generatedReport = null;
    try {
      generatedReport = await getBackendGpsReport(reportName, true);
      rows = Array.isArray(generatedReport.rows) ? generatedReport.rows : rows;
    } catch (err) {
      console.log(err);
    }
    const reportVehicle = selected?.vehicle_no || selectedModuleDevice?.device_name || selected?.name || 'gps';
    const reportImei = selected?.imei || selectedModuleDevice?.imei || '';
    const baseHeader = ['report', 'vehicle', 'imei', 'event_time', 'latitude', 'longitude', 'speed', 'ignition', 'motion_status', 'address', 'direction', 'gps_signal_status', 'satellites', 'gsm_signal', 'battery_voltage'];
    const extraKeys = Array.from(new Set(rows.flatMap((row) => Object.keys(row || {})))).filter((key) => !baseHeader.includes(key));
    const header = [...baseHeader, ...extraKeys];
    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csvRows = rows.map((row) => {
      const eventTime = row.event_time || selected?.last_data_time || selectedModuleDevice?.last_packet_time || '';
      const normalized = {
        report: reportName,
        vehicle: reportVehicle,
        imei: reportImei || row.imei || '',
        event_time: formatDateTime(eventTime, '') || row.event_time || '',
        latitude: row.latitude || selected?.gps_latitude || selectedModuleDevice?.last_latitude || '',
        longitude: row.longitude || selected?.gps_longitude || selectedModuleDevice?.last_longitude || '',
        speed: row.speed ?? selected?.gps_speed ?? selectedModuleDevice?.last_speed ?? '',
        ignition: row.ignition ?? selected?.gps_ignition ?? selectedModuleDevice?.ignition ?? '',
        motion_status: row.motion_status || selected?.gps_motion_status || selectedModuleDevice?.motion_status || '',
        address: row.address || selected?.gps_address || selectedModuleDevice?.current_address || '',
        direction: row.direction || selected?.gps_direction || '',
        gps_signal_status: row.gps_signal_status || selected?.gps_signal_status || selectedModuleDevice?.gps_signal_status || '',
        satellites: row.satellites || selected?.gps_satellites || '',
        gsm_signal: row.gsm_signal || selected?.gsm_signal || '',
        battery_voltage: row.battery_voltage || selected?.battery_voltage || selectedModuleDevice?.battery_voltage || '',
        ...row
      };
      return header.map((key) => escapeCsv(normalized[key])).join(',');
    });
    const blob = new Blob([[header.join(','), ...csvRows].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reportVehicle.replace(/[^a-z0-9]+/gi, '-')}-${reportName.replace(/[^a-z0-9]+/gi, '-')}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    pushActionLog('Report', `${reportName} - ${generatedReport?.summary?.result_rows ?? rows.length} rows`);
    try {
      const reportRes = await axios.get(`${API_URL}/api/gps/reports`, { headers: getHeaders() });
      setGpsReports(reportRes.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  const copyTrackingLink = (url) => {
    if (!url) return;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => alert('Live location link copied')).catch(() => alert(url));
      return;
    }
    alert(url);
  };

  const runTrackingSideAction = async (section, label) => {
    if (!selected) return alert('Please select a customer');
    setTrackingSideTab(section);
    if (section === 'reports') {
      showGpsReport(label);
      return;
    }
    if (section === 'history') {
      if (label === 'Live Tracking') setTrackingView('live');
      else if (label.includes('Playback')) setTrackingView('playback');
      else setTrackingView('history');
      if (label.includes('Download CSV')) {
        downloadGpsReport('GPS History');
        return;
      }
      if (label.includes('Share Live') && openTrackingUrl) {
        copyTrackingLink(openTrackingUrl);
      }
      fetchGpsHistory(selected.id);
      pushActionLog('History', label);
      return;
    }
    if (label === 'Get Current Location' || label === 'Check Device Status') fetchGpsHistory(selected.id);
    await sendDeviceCommand(label);
  };

  const runGpsAction = async (type, label) => {
    if (!selected) return alert('Please select a customer');
    if (label === 'Live map') setTrackingView('live');
    if (label === 'Route playback' || label === 'Route Playback' || label === 'Playback / Route History') setTrackingView('playback');
    if (label === 'Reports download') {
      setTrackingSideTab('reports');
      downloadGpsReport('Customer GPS Report');
      return;
    }
    if (label === 'Share live location' && openTrackingUrl) copyTrackingLink(openTrackingUrl);
    if (label === 'Get Current Location') fetchGpsHistory(selected.id);
    if (type === 'Command' || label.includes('Engine Lock') || label.includes('Engine Unlock')) {
      await sendDeviceCommand(label);
      return;
    }
    pushActionLog(type, label);
  };

  const protocolMetricCards = [
    { label: 'IMEI Seen', value: protocolSummary.total_imei || 0, icon: MapPin, tone: '#0ea5e9' },
    { label: 'Live Protocols', value: protocolSummary.live || 0, icon: Activity, tone: '#10b981' },
    { label: 'Today Packets', value: protocolSummary.today_packets || 0, icon: Zap, tone: '#f59e0b' },
    { label: 'Protocol Types', value: protocolSummary.protocol_count || 0, icon: Globe2, tone: '#8b5cf6' }
  ];

  if (gpsView === 'gps-protocol-monitor' && user?.role === 'superadmin') {
    return (
      <motion.div {...pageMotion} className="gps-protocol-monitor">
        <section className="gps-protocol-header glass-panel">
          <div>
            <span className="eyebrow"><Activity size={14} /> Super Admin GPS Diagnostics</span>
            <h2>Protocol Monitor</h2>
            <p>IMEI-wise received protocol, packet source, live status, raw payload sample and customer mapping.</p>
          </div>
          <div className="gps-protocol-refresh">
            <span>{protocolLoading ? 'Refreshing...' : 'Auto refresh 5 sec'}</span>
            <ShellButton onClick={fetchProtocolMonitor}><RefreshCw size={16} /> Refresh</ShellButton>
          </div>
        </section>

        <div className="gps-protocol-metrics">
          {protocolMetricCards.map((item) => (
            <div className="gps-protocol-metric glass-panel" style={{ '--protocol-tone': item.tone }} key={item.label}>
              <item.icon size={22} />
              <span>{item.label}</span>
              <strong>{Number(item.value || 0).toLocaleString('en-IN')}</strong>
            </div>
          ))}
        </div>

        <section className="gps-protocol-table-card glass-panel">
          <div className="gps-protocol-tools">
            <label className="gps-protocol-search">
              <Search size={16} />
              <input
                value={protocolSearch}
                onChange={(event) => setProtocolSearch(event.target.value)}
                placeholder="Search IMEI, protocol, admin, customer, IP..."
              />
            </label>
            <div className="gps-protocol-pills">
              {(protocolSummary.protocols || []).slice(0, 6).map((item) => <span key={item}>{item}</span>)}
              {!(protocolSummary.protocols || []).length && <span>Waiting for protocol packets</span>}
            </div>
          </div>

          <div className="gps-protocol-table-wrap">
            <table className="gps-protocol-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>IMEI / Protocol</th>
                  <th>Packets</th>
                  <th>Source</th>
                  <th>Mapped Account</th>
                  <th>Last Location</th>
                  <th>Raw Sample</th>
                </tr>
              </thead>
              <tbody>
                {protocolRows.map((row, index) => (
                  <tr key={row.imei || index}>
                    <td>{index + 1}</td>
                    <td>
                      <strong>{row.imei || '-'}</strong>
                      <span className={`gps-protocol-state ${row.live_state || 'waiting'}`}>{row.live_state || 'waiting'}</span>
                      <span className={`gps-protocol-packet ${row.packet_status || 'accepted'}`}>{row.packet_status || 'accepted'}</span>
                      <small>{row.detected_protocol || 'Auto Detect'}</small>
                      {row.received_protocols && row.received_protocols !== row.detected_protocol && <em>{row.received_protocols}</em>}
                    </td>
                    <td>
                      <b>{Number(row.total_packets || 0).toLocaleString('en-IN')}</b>
                      <small>Today {Number(row.today_packets || 0).toLocaleString('en-IN')}</small>
                      <small>Last {formatDateTime(row.last_seen, '-')}</small>
                    </td>
                    <td>
                      <strong>{row.source_ip || '-'}</strong>
                      <small>Port {row.source_port || '-'}</small>
                    </td>
                    <td>
                      <strong>{row.customer_name || row.device_name || 'Unmapped Device'}</strong>
                      <small>{row.customer_mobile || row.vehicle_no || row.admin_name || '-'}</small>
                      <small>{row.admin_name ? `Admin: ${row.admin_name}` : 'Super Admin visible'}</small>
                    </td>
                    <td>
                      <strong>{row.latitude && row.longitude ? `${Number(row.latitude).toFixed(6)}, ${Number(row.longitude).toFixed(6)}` : '-'}</strong>
                      <small>{Number(row.speed || 0)} km/h | {Number(row.ignition || 0) ? 'Ignition ON' : 'Ignition OFF'}</small>
                      <small>{row.motion_status || row.device_status || '-'}</small>
                    </td>
                    <td>
                      <code>{row.raw_payload_sample || 'No raw sample stored yet'}</code>
                      {row.error_message && <small className="gps-protocol-error">{row.error_message}</small>}
                    </td>
                  </tr>
                ))}
                {!protocolRows.length && (
                  <tr>
                    <td colSpan="7">
                      <div className="empty-state">No GPS protocol packets found yet. Incoming GPS packets will appear here automatically.</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </motion.div>
    );
  }

  return (
    <motion.div {...pageMotion}>
      <div className="ops-grid">
        {[
          { label: 'Total Devices', value: deviceModuleRows.length, icon: MapPin, color: '#059669' },
          { label: 'Online', value: deviceStatusCounts.active || 0, icon: Activity, color: '#10b981' },
          { label: 'Offline', value: (deviceStatusCounts.offline || 0) + (deviceStatusCounts.never_connected || 0), icon: Moon, color: '#64748b' },
          { label: 'Commands', value: `${sentCommandCount}/${pendingCommandCount}`, icon: Send, color: '#7c3aed' },
          { label: 'Reports', value: gpsReports.length, icon: BarChart3, color: '#f59e0b' },
          { label: 'Expired', value: deviceStatusCounts.expired || 0, icon: CalendarClock, color: '#ef4444' },
        ].map((item) => (
          <div className="ops-metric glass-panel" style={{ '--accent': item.color }} key={item.label}>
            <item.icon size={22} />
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </div>

      <div className="gps-health-ribbon">
        {[
          ['Scoped Live', 'Role-wise socket rooms', '#06b6d4'],
          ['Command Gateway', pendingCommandCount ? `${pendingCommandCount} queued` : 'live-ready', '#8b5cf6'],
          ['Report Engine', `${gpsReports.length} generated`, '#f97316'],
          ['Device Guard', 'registered IMEI first', '#10b981']
        ].map(([label, value, color]) => (
          <span style={{ '--gps-ribbon': color }} key={label}>
            <b>{label}</b>
            <small>{value}</small>
          </span>
        ))}
      </div>

      <section className="live-device-module workspace-card glass-panel">
        <div className="section-title-row">
          <h3>Device live tracking panel</h3>
          <small>{selectedModuleDevice?.imei || 'Select device'}</small>
        </div>
        <div className="live-device-shell">
          <aside className="live-device-list">
            <div className="live-device-list-head">
              <strong>Vehicle List</strong>
              <span>{deviceModuleRows.length} devices</span>
            </div>
            {deviceModuleRows.map((device) => {
              const isActive = (selectedModuleDevice?.id && String(selectedModuleDevice.id) === String(device.id))
                || (selected?.imei && String(selected.imei) === String(device.imei));
              const status = device.live_status || device.status || 'never_connected';
              return (
                <button type="button" className={`live-device-row ${isActive ? 'active' : ''} ${status}`} key={device.id || device.imei} onClick={() => selectTrackingDevice(device)}>
                  <span className="device-status-dot" />
                  <strong>{device.vehicle_no || device.device_name || device.customer_name || device.imei || 'GPS Device'}</strong>
                  <small>{device.customer_name || device.customer_mobile || 'No customer'} | {device.imei || 'No IMEI'}</small>
                  <em>{status.replace(/_/g, ' ')}</em>
                </button>
              );
            })}
            {deviceModuleRows.length === 0 && <div className="empty-state">Assign a device/IMEI in Sales. The device will appear here automatically.</div>}
          </aside>

          <main className="live-device-main">
            <div className="live-device-toolbar">
              <div>
                <h3>{selected?.vehicle_no || selectedModuleDevice?.device_name || 'Live Map'}</h3>
                <p>{selected?.name || selectedModuleDevice?.customer_name || 'Customer'} | {selectedModuleDevice?.imei || selected?.imei || 'No IMEI'}</p>
              </div>
              <div className="live-map-toolbar">
                <button type="button" className={trackingView === 'live' ? 'active' : ''} onClick={() => setTrackingView('live')}><Navigation size={16} /> Live</button>
                <button type="button" className={trackingView === 'playback' ? 'active' : ''} onClick={() => setTrackingView('playback')}><RefreshCw size={16} /> Playback</button>
                <button type="button" className={trackingView === 'history' ? 'active' : ''} onClick={() => { setTrackingView('history'); setTrackingSideTab('history'); }}><Eye size={16} /> History</button>
                <button type="button" onClick={() => setMapTheme((current) => current === 'light' ? 'dark' : 'light')}><Moon size={16} /> {mapTheme === 'light' ? 'Dark' : 'Light'}</button>
                <button type="button" onClick={() => setMapFullscreen((current) => !current)}><Globe2 size={16} /> Fullscreen</button>
              </div>
            </div>
            <LiveTrackingMap
              vehicle={selected || selectedModuleDevice}
              points={selectedModulePoints}
              mode={trackingView}
              fleetVehicles={fleetMapVehicles}
              mapTheme={mapTheme}
              fullscreen={mapFullscreen}
            />
            <div className="live-device-stat-grid">
              {deviceModuleStats.map(([label, value]) => (
                <div className="gps-feature-card" key={label}>
                  <span>{label}</span>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>
          </main>

          <aside className="live-device-actions">
            <div className="tracking-side-tabs">
              {trackingSideTabs.map((item) => (
                <button type="button" className={trackingSideTab === item.key ? 'active' : ''} onClick={() => setTrackingSideTab(item.key)} key={item.key}>
                  <span>{item.label}</span>
                  <b>{item.count}</b>
                </button>
              ))}
            </div>
            <div className="tracking-side-summary">
              <strong>{selectedModuleDevice?.device_name || selected?.vehicle_no || 'GPS Device'}</strong>
              <span>{selectedModuleDevice?.live_status || selectedModuleDevice?.status || selected?.gps_online_status || 'never connected'}</span>
              <small>Packet: {selectedModuleDevice?.last_packet_time ? formatDateTime(selectedModuleDevice.last_packet_time) : 'Never received'}</small>
              {selectedGpsSidebarOption && <small>Sidebar selected: {selectedGpsSidebarOption.label}</small>}
            </div>
            <div className="tracking-option-list">
              {trackingSideTab === 'commands' && ['Current Location', 'Device Restart', 'Engine Lock', 'Engine Unlock', 'SOS Command', 'Sleep Mode', 'Overspeed Setting', 'Geofence Setting'].map((item) => (
                <button type="button" className={`command ${/engine/i.test(item) ? 'danger-command' : ''}`} disabled={commandSending === item} onClick={() => sendDeviceCommand(item, selectedModuleDevice)} key={item}>
                  {commandSending === item ? 'Sending...' : item}
                </button>
              ))}
              {trackingSideTab === 'history' && historyOptions.slice(0, 10).map((item) => (
                <button type="button" onClick={() => runTrackingSideAction('history', item)} key={item}>{item}</button>
              ))}
              {trackingSideTab === 'reports' && reportOptions.map((item) => (
                <div className="report-option-row" key={item}>
                  <span>{item}</span>
                  <div className="report-option-actions">
                    <button type="button" onClick={() => showGpsReport(item)}><Eye size={14} /> Show</button>
                    <button type="button" className="download" onClick={() => downloadGpsReport(item)}><Download size={14} /> Download</button>
                  </div>
                </div>
              ))}
            </div>
            {trackingSideTab === 'commands' && (
              <div className="history-timeline side">
                {commandHistory.slice(0, 6).map((item) => (
                  <div className="history-event" key={item.id || `${item.command_name}-${item.created_at}`}>
                    <span>{item.created_at ? formatDateTime(item.created_at) : 'Just now'}</span>
                    <strong>{item.command_name || item.command_key}</strong>
                    <small>{item.imei || selectedModuleDevice?.imei || '-'} | <b className={`gps-command-status ${String(item.status || 'pending').toLowerCase()}`}>{item.status || 'pending'}</b></small>
                  </div>
                ))}
                {commandHistory.length === 0 && <div className="empty-state">Command history will appear here after a command is sent.</div>}
              </div>
            )}
            {trackingSideTab === 'history' && (
              <div className="history-timeline side">
                {historyPoints.slice(-6).reverse().map((point, index) => (
                  <div className="history-event" key={point.id || `${point.event_time}-${index}`}>
                    <span>{point.event_time ? formatDateTime(point.event_time) : '-'}</span>
                    <strong>{Number(point.speed || 0)} km/h | {point.motion_status || 'Stop'}</strong>
                    <small>{point.address || `${point.latitude}, ${point.longitude}`}</small>
                  </div>
                ))}
                {historyPoints.length === 0 && <div className="empty-state">Route history will be created after GPS packets are received.</div>}
              </div>
            )}
            {trackingSideTab === 'reports' && (
              <div className={`report-preview-card ${reportPreview ? '' : 'empty'}`}>
                {reportPreviewLoading ? (
                  <span>Generating secure backend report...</span>
                ) : reportPreview ? (
                  <>
                    <div className="report-preview-head">
                      <span>Report Preview</span>
                      <strong>{reportPreview.name}</strong>
                      <small>{reportPreview.vehicle} | {reportPreview.imei}</small>
                    </div>
                    <div className="report-preview-stats">
                      <span><b>{reportPreview.totalPoints}</b> Points</span>
                      <span><b>{reportPreview.routeDistance}</b> KM</span>
                      <span><b>{reportPreview.maxSpeed}</b> Max speed</span>
                    </div>
                    <div className="report-preview-rows">
                      {reportPreview.rows.map((point, index) => (
                        <div className="report-preview-row" key={point.id || `${point.event_time}-${index}`}>
                          <strong>{point.event_time ? formatDateTime(point.event_time) : 'Latest packet'}</strong>
                          <span>{Number(point.speed || 0)} km/h | {point.motion_status || 'Stop'}</span>
                          <small>{point.report_note || point.address || `${point.latitude || '-'}, ${point.longitude || '-'}`}</small>
                        </div>
                      ))}
                    </div>
                    <button type="button" className="report-download-button" onClick={() => downloadGpsReport(reportPreview.name)}><Download size={15} /> Download CSV</button>
                  </>
                ) : (
                  <span>The preview will appear here after the report is generated.</span>
                )}
              </div>
            )}
          </aside>
        </div>
      </section>

      {canManage && (
        <section className="workspace-card glass-panel gps-device-management">
          <div className="section-title-row"><h3>Add / edit tracking device</h3><small>Auto-mapped from sales data with manual correction allowed</small></div>
          <div className="device-management-grid">
            <div className="tracking-form compact">
              {canFetchInventory && (
                <label>
                  Fetch From Inventory
                  <select value={deviceForm.inventory_id || ''} onChange={(event) => applyInventoryToDeviceForm(event.target.value)}>
                    <option value="">Select inventory device</option>
                    {inventoryStock.map((item) => (
                      <option key={item.id} value={item.id}>{inventoryDeviceLabel(item)}</option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                Customer ID
                <input value={deviceForm.customer_id || ''} onChange={(event) => setDeviceForm((current) => ({ ...current, customer_id: event.target.value }))} placeholder="Customer ID" />
              </label>
              <label>
                IMEI
                <input value={deviceForm.imei || ''} onChange={(event) => setDeviceForm((current) => ({ ...current, imei: event.target.value }))} placeholder="Device IMEI" />
              </label>
              <label>
                SIM
                <input value={deviceForm.sim_no || ''} onChange={(event) => setDeviceForm((current) => ({ ...current, sim_no: event.target.value }))} placeholder="SIM number" />
              </label>
              <label>
                Device Name
                <input value={deviceForm.device_name || ''} onChange={(event) => setDeviceForm((current) => ({ ...current, device_name: event.target.value }))} placeholder="Vehicle or device name" />
              </label>
              <label>
                Model
                <input value={deviceForm.device_model || ''} onChange={(event) => setDeviceForm((current) => ({ ...current, device_model: event.target.value }))} placeholder="Model" />
              </label>
              <label>
                Device Type
                <input value={deviceForm.device_type || 'GPS'} onChange={(event) => setDeviceForm((current) => ({ ...current, device_type: event.target.value }))} placeholder="GPS / AIS140" />
              </label>
              <label>
                Expiry Date
                <input type="date" value={deviceForm.expiry_date || ''} onChange={(event) => setDeviceForm((current) => ({ ...current, expiry_date: event.target.value }))} />
              </label>
              <ShellButton onClick={saveGpsDevice} disabled={saving}><Settings size={16} /> {deviceForm.id ? 'Update Device' : 'Add Device'}</ShellButton>
              <button type="button" className="ghost-action-button" onClick={resetDeviceForm}>Clear</button>
            </div>
            <div className="device-list-panel">
              {deviceModuleRows.map((device) => (
                <div className={`device-list-item ${device.live_status || device.status}`} key={`manage-${device.id || device.imei}`}>
                  <div>
                    <strong>{device.device_name || device.vehicle_no || device.imei}</strong>
                    <span>{device.customer_name || 'Unassigned'} | {device.imei}</span>
                    <small>Last packet: {device.last_packet_time ? formatDateTime(device.last_packet_time) : 'Never'} | Expiry: {device.expiry_date ? formatDate(device.expiry_date) : '-'}</small>
                  </div>
                  <b>{(device.live_status || device.status || 'never_connected').replace(/_/g, ' ')}</b>
                  {canModifyGps && !String(device.id || '').startsWith('customer-') && <button type="button" onClick={() => editDevice(device)}>Edit</button>}
                  {canModifyGps && !String(device.id || '').startsWith('customer-') && <button type="button" onClick={() => deleteGpsDevice(device.id)}>Delete</button>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {actionLog.length > 0 && (
        <section className="workspace-card glass-panel gps-action-log">
          <div className="section-title-row"><h3>Recent GPS actions</h3><small>{actionLog.length} latest</small></div>
          <div>
            {actionLog.map((item) => (
              <div className="gps-action-log-item" key={item.id}>
                <span>{item.type}</span>
                <strong>{item.label}</strong>
                <small>{item.vehicle} | {item.time}</small>
              </div>
            ))}
          </div>
        </section>
      )}

      {false && (
      <>
      <WorkspaceHero icon={MapPin} kicker="gps tracking" title="GPS Tracking" desc="Give customers tracking access for vehicles already added in the CRM.">
        <ShellButton onClick={() => { fetchTracking(); fetchGpsHistory(selected?.id); }}><RefreshCw size={16} /> Refresh</ShellButton>
      </WorkspaceHero>
      <div className="ops-grid">
        {[
          { label: 'Tracked Customers', value: activeCount, icon: Navigation, color: '#0ea5e9' },
          { label: 'Total Devices', value: deviceModuleRows.length, icon: MapPin, color: '#059669' },
          { label: 'Online Devices', value: deviceStatusCounts.active || 0, icon: Activity, color: '#10b981' },
          { label: 'Offline Devices', value: (deviceStatusCounts.offline || 0) + (deviceStatusCounts.never_connected || 0), icon: Moon, color: '#64748b' },
          { label: 'Expired Devices', value: deviceStatusCounts.expired || 0, icon: CalendarClock, color: '#ef4444' },
          { label: 'Tracking Access', value: canModifyGps ? 'Manage' : canManage ? 'Add Only' : 'View Only', icon: Lock, color: '#7c3aed' },
        ].map((item) => (
          <div className="ops-metric glass-panel" style={{ '--accent': item.color }} key={item.label}>
            <item.icon size={22} />
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </div>

      <section className="tracking-layout">
        <div className="workspace-card glass-panel">
          <div className="section-title-row">
            <h3>{canManage ? 'Customer tracking devices' : 'My tracking access'}</h3>
            <small>{customers.length} records</small>
          </div>
          <div className="mini-table tracking-table">
            {customers.map((customer) => (
              <button type="button" className={`tracking-row ${selected?.id === customer.id ? 'active' : ''}`} key={customer.id} onClick={() => startEdit(customer)}>
                <strong>{customer.name}</strong>
                <span>{customer.vehicle_no || customer.mobile || '-'}</span>
                <b>{customer.imei || 'No IMEI'}</b>
                <em>{customer.gps_tracking_enabled || hasGpsLocation(customer) || customer.imei ? 'Active' : 'Not enabled'}</em>
              </button>
            ))}
            {customers.length === 0 && <div className="empty-state">No customer vehicles found for tracking.</div>}
          </div>
        </div>

        <div className="workspace-card glass-panel">
          <div className="section-title-row">
            <h3>{selected?.vehicle_no || 'Tracking console'}</h3>
            <small>{selected?.gps_provider || selected?.device_type || 'GPS device'}</small>
          </div>
          {selected ? (
            <>
              <div className="tracking-detail-grid">
                <span><b>Customer</b>{selected.name || '-'}</span>
                <span><b>Driver</b>{selected.driver_name || '-'}</span>
                <span><b>Mobile</b>{selected.mobile || '-'}</span>
                <span><b>Vehicle</b>{selected.vehicle_no || '-'}</span>
                <span><b>IMEI</b>{selected.imei || '-'}</span>
                <span><b>SIM Number</b>{selected.sim_no || '-'}</span>
                <span><b>Device Model</b>{selected.device_model || '-'}</span>
                <span><b>Protocol</b>{selected.gps_protocol || '-'}</span>
                <span><b>Device IP</b>{selected.gps_device_ip || '-'}</span>
                <span><b>Online Status</b>{selected.gps_online_status || 'offline'}</span>
                <span><b>Latitude</b>{selected.gps_latitude || '-'}</span>
                <span><b>Longitude</b>{selected.gps_longitude || '-'}</span>
                <span><b>Device Status</b>{selectedDevice?.live_status || selectedDevice?.status || '-'}</span>
                <span><b>Last Packet</b>{selectedDevice?.last_packet_time ? formatDateTime(selectedDevice.last_packet_time) : '-'}</span>
                <span><b>Expiry Date</b>{selectedDevice?.expiry_date ? formatDate(selectedDevice.expiry_date) : (selected.renewal_date ? formatDate(selected.renewal_date) : '-')}</span>
                <span><b>Tracking Source</b>{selected.tracking_source === 'sales' || selectedDevice?.sale_id ? 'Sales device' : 'Customer IMEI'}</span>
              </div>
              {canModifyGps && (
                <div className="tracking-form">
                  <label>
                    Last Data Time
                    <input type="datetime-local" value={trackingForm.last_data_time || ''} onChange={(event) => setTrackingForm((current) => ({ ...current, last_data_time: event.target.value }))} />
                  </label>
                  <label>
                    Online Status
                    <select value={trackingForm.gps_online_status || 'offline'} onChange={(event) => setTrackingForm((current) => ({ ...current, gps_online_status: event.target.value }))}>
                      <option value="offline">Offline</option>
                      <option value="online">Online</option>
                    </select>
                  </label>
                  <label>
                    Speed
                    <input type="number" value={trackingForm.gps_speed || 0} onChange={(event) => setTrackingForm((current) => ({ ...current, gps_speed: event.target.value }))} placeholder="0" />
                  </label>
                  <label>
                    Vehicle Status
                    <select value={trackingForm.gps_motion_status || 'Stop'} onChange={(event) => setTrackingForm((current) => ({ ...current, gps_motion_status: event.target.value }))}>
                      <option value="Moving">Moving</option>
                      <option value="Stop">Stop</option>
                      <option value="Idle">Idle</option>
                    </select>
                  </label>
                  <label>
                    Address
                    <input value={trackingForm.gps_address || ''} onChange={(event) => setTrackingForm((current) => ({ ...current, gps_address: event.target.value }))} placeholder="Current address" />
                  </label>
                  <label>
                    Direction
                    <input value={trackingForm.gps_direction || ''} onChange={(event) => setTrackingForm((current) => ({ ...current, gps_direction: event.target.value }))} placeholder="North East" />
                  </label>
                  <label>
                    GPS Signal
                    <input value={trackingForm.gps_signal_status || ''} onChange={(event) => setTrackingForm((current) => ({ ...current, gps_signal_status: event.target.value }))} placeholder="Good / Weak" />
                  </label>
                  <label>
                    GSM Signal
                    <input value={trackingForm.gsm_signal || ''} onChange={(event) => setTrackingForm((current) => ({ ...current, gsm_signal: event.target.value }))} placeholder="4G / Good" />
                  </label>
                  <label>
                    Battery Voltage
                    <input value={trackingForm.battery_voltage || ''} onChange={(event) => setTrackingForm((current) => ({ ...current, battery_voltage: event.target.value }))} placeholder="12.4V" />
                  </label>
                  <label className="toggle-row">
                    <input type="checkbox" checked={trackingForm.gps_tracking_enabled} onChange={(event) => setTrackingForm((current) => ({ ...current, gps_tracking_enabled: event.target.checked }))} />
                    Enable customer tracking access
                  </label>
                  <label className="toggle-row">
                    <input type="checkbox" checked={trackingForm.gps_ignition || false} onChange={(event) => setTrackingForm((current) => ({ ...current, gps_ignition: event.target.checked }))} />
                    Ignition ON
                  </label>
                  <ShellButton onClick={saveTracking} disabled={saving}><Settings size={16} /> Save Tracking</ShellButton>
                </div>
              )}
              <div className="gps-feature-section">
                <div className="section-title-row">
                  <h3>Live tracking</h3>
                  <small>GPS Tracking Device only</small>
                </div>
                <div className="tracking-mode-tabs">
                  {[
                    { key: 'live', label: 'Live Map' },
                    { key: 'playback', label: 'Playback' },
                    { key: 'history', label: 'History' },
                  ].map((item) => (
                    <button type="button" className={trackingView === item.key ? 'active' : ''} onClick={() => setTrackingView(item.key)} key={item.key}>
                      {item.label}
                    </button>
                  ))}
                </div>
                <div className="gps-feature-grid live">
                  {liveTrackingItems.map(([label, value]) => (
                    <div className="gps-feature-card" key={label}>
                      <span>{label}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}
                </div>
                <div className="gps-feature-grid live tracking-history-strip">
                  {historySummaryItems.map(([label, value]) => (
                    <div className="gps-feature-card" key={label}>
                      <span>{label}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}
                </div>
              </div>
              <div className="tracking-map-workspace">
                <div className="tracking-map-stage">
                  <div className="tracking-preview">
                    {liveMapPoints.length > 0 ? (
                      <>
                        <div className="live-map-toolbar">
                          {openTrackingUrl ? <a className="open-link-button" href={openTrackingUrl} target="_blank" rel="noreferrer"><Navigation size={16} /> Open Map</a> : null}
                          <button type="button" onClick={() => setMapTheme((current) => current === 'light' ? 'dark' : 'light')}><Moon size={16} /> {mapTheme === 'light' ? 'Dark Map' : 'Light Map'}</button>
                          <button type="button" onClick={() => setMapFullscreen((current) => !current)}><Globe2 size={16} /> {mapFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</button>
                        </div>
                        <LiveTrackingMap vehicle={selected} points={liveMapPoints} mode={trackingView} fleetVehicles={fleetMapVehicles} mapTheme={mapTheme} fullscreen={mapFullscreen} />
                      </>
                    ) : (
                      <div className="empty-state">Waiting for GPS data from this device IMEI.</div>
                    )}
                  </div>
                </div>
                <aside className="tracking-side-panel">
                  <div className="tracking-side-tabs">
                    {trackingSideTabs.map((item) => (
                      <button type="button" className={trackingSideTab === item.key ? 'active' : ''} onClick={() => setTrackingSideTab(item.key)} key={item.key}>
                        <span>{item.label}</span>
                        <b>{item.count}</b>
                      </button>
                    ))}
                  </div>
                  <div className="tracking-side-summary">
                    <strong>{selected.vehicle_no || selected.name || 'Selected vehicle'}</strong>
                    <span>{selected.imei || 'No IMEI'} | {selected.gps_online_status || 'offline'}</span>
                    <small>{selected.gps_protocol || selected.gps_provider || 'Auto protocol'} | {selected.gps_device_ip || 'Waiting for IP packet'}</small>
                  </div>
                  <div className="tracking-option-list">
                    {trackingSideTab === 'reports' && reportOptions.map((item) => (
                      <button type="button" onClick={() => runTrackingSideAction('reports', item)} key={item}>{item}</button>
                    ))}
                    {trackingSideTab === 'commands' && commandOptions.map((item) => (
                      <button type="button" className="command" onClick={() => runTrackingSideAction('commands', item)} key={item}>{item}</button>
                    ))}
                    {trackingSideTab === 'history' && historyOptions.map((item) => (
                      <button type="button" onClick={() => runTrackingSideAction('history', item)} key={item}>{item}</button>
                    ))}
                  </div>
                  {trackingSideTab === 'history' && (
                    <div className="history-timeline side">
                      {historyLoading ? (
                        <div className="empty-state">Loading GPS history...</div>
                      ) : historyPoints.length === 0 ? (
                        <div className="empty-state">No GPS history received yet.</div>
                      ) : historyPoints.slice(-8).reverse().map((point, index) => (
                        <div className="history-event" key={point.id || `${point.event_time}-${index}`}>
                          <span>{point.event_time ? formatDateTime(point.event_time) : '-'}</span>
                          <strong>{Number(point.speed || 0)} km/h | {point.motion_status || 'Stop'}</strong>
                          <small>{point.address || `${point.latitude}, ${point.longitude}`}</small>
                        </div>
                      ))}
                    </div>
                  )}
                </aside>
              </div>
              <div className="gps-feature-section">
                <div className="section-title-row">
                  <h3>GPS software options</h3>
                  <small>Important live tracking controls</small>
                </div>
                <div className="gps-feature-grid">
                  {['Live Tracking', 'Playback / Route History', 'Distance / KM Report', 'Stop Report', 'Idle Report', 'Overspeed Alert', 'Ignition Report', 'Engine Lock / Unlock'].map((item) => (
                    <button type="button" className="gps-feature-card priority action-card" onClick={() => item.includes('Report') ? downloadGpsReport(item) : runGpsAction('GPS Option', item)} key={item}><strong>{item}</strong></button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state">Select a customer to view GPS tracking.</div>
          )}
        </div>
      </section>

      <section className="gps-command-layout">
        <div className="workspace-card glass-panel">
          <div className="section-title-row"><h3>Basic reports</h3><small>Download-ready GPS reports</small></div>
          <div className="gps-feature-grid">{reportOptions.map((item) => <button type="button" className="gps-action-button" onClick={() => downloadGpsReport(item)} key={item}>{item}</button>)}</div>
        </div>
        <div className="workspace-card glass-panel">
          <div className="section-title-row"><h3>Alerts</h3><small>Customer notification options</small></div>
          <div className="gps-feature-grid">{alertOptions.map((item) => <button type="button" className="gps-action-button" onClick={() => runGpsAction('Alert', item)} key={item}>{item}</button>)}</div>
        </div>
        <div className="workspace-card glass-panel">
          <div className="section-title-row"><h3>Commands</h3><small>Device command center</small></div>
          <div className="gps-feature-grid">{commandOptions.map((item) => <button type="button" className="gps-action-button command" onClick={() => runGpsAction('Command', item)} key={item}>{item}</button>)}</div>
        </div>
        <div className="workspace-card glass-panel">
          <div className="section-title-row"><h3>Customer app options</h3><small>Enabled when tracking access is selected</small></div>
          <div className="gps-feature-grid">{customerAppOptions.map((item) => <button type="button" className="gps-action-button" onClick={() => runGpsAction('Customer App', item)} key={item}>{item}</button>)}</div>
        </div>
      </section>

      {canManage && (
        <section className="workspace-card glass-panel gps-device-management">
          <div className="section-title-row"><h3>Device management</h3><small>Sales mapped GPS devices</small></div>
          <div className="device-management-grid">
            <div className="tracking-form compact">
              <label>
                Customer ID
                <input value={deviceForm.customer_id || ''} onChange={(event) => setDeviceForm((current) => ({ ...current, customer_id: event.target.value }))} placeholder="Customer ID" />
              </label>
              <label>
                IMEI
                <input value={deviceForm.imei || ''} onChange={(event) => setDeviceForm((current) => ({ ...current, imei: event.target.value }))} placeholder="Device IMEI" />
              </label>
              <label>
                SIM
                <input value={deviceForm.sim_no || ''} onChange={(event) => setDeviceForm((current) => ({ ...current, sim_no: event.target.value }))} placeholder="SIM number" />
              </label>
              <label>
                Device Name
                <input value={deviceForm.device_name || ''} onChange={(event) => setDeviceForm((current) => ({ ...current, device_name: event.target.value }))} placeholder="Vehicle or device name" />
              </label>
              <label>
                Model
                <input value={deviceForm.device_model || ''} onChange={(event) => setDeviceForm((current) => ({ ...current, device_model: event.target.value }))} placeholder="Model" />
              </label>
              <label>
                Protocol
                <input value={deviceForm.protocol || ''} onChange={(event) => setDeviceForm((current) => ({ ...current, protocol: event.target.value }))} placeholder="Auto / GT06 / AIS140" />
              </label>
              <label>
                Status
                <select value={deviceForm.status || 'never_connected'} onChange={(event) => setDeviceForm((current) => ({ ...current, status: event.target.value }))}>
                  <option value="active">Active</option>
                  <option value="offline">Offline</option>
                  <option value="expired">Expired</option>
                  <option value="never_connected">Never Connected</option>
                </select>
              </label>
              <label>
                Expiry Date
                <input type="date" value={deviceForm.expiry_date || ''} onChange={(event) => setDeviceForm((current) => ({ ...current, expiry_date: event.target.value }))} />
              </label>
              <ShellButton onClick={saveGpsDevice} disabled={saving}><Settings size={16} /> {deviceForm.id ? 'Update Device' : 'Add Device'}</ShellButton>
              <button type="button" className="ghost-action-button" onClick={resetDeviceForm}>Clear</button>
            </div>
            <div className="device-list-panel">
              {gpsDevices.length === 0 ? (
                <div className="empty-state">No GPS devices mapped yet.</div>
              ) : gpsDevices.map((device) => (
                <div className={`device-list-item ${device.live_status || device.status}`} key={device.id}>
                  <div>
                    <strong>{device.device_name || device.vehicle_no || device.imei}</strong>
                    <span>{device.customer_name || 'Unassigned'} | {device.imei}</span>
                    <small>Packet: {device.last_packet_time ? formatDateTime(device.last_packet_time) : 'Never'} | Expiry: {device.expiry_date ? formatDate(device.expiry_date) : '-'}</small>
                  </div>
                  <b>{device.live_status || device.status}</b>
                  {canModifyGps && <button type="button" onClick={() => editDevice(device)}>Edit</button>}
                  {canModifyGps && <button type="button" onClick={() => deleteGpsDevice(device.id)}>Delete</button>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="gps-command-layout">
        <div className="workspace-card glass-panel">
          <div className="section-title-row"><h3>Report history</h3><small>{gpsReports.length} generated</small></div>
          <div className="history-timeline side">
            {gpsReports.slice(0, 8).map((item) => (
              <div className="history-event" key={item.id}>
                <span>{item.created_at ? formatDateTime(item.created_at) : '-'}</span>
                <strong>{item.report_type}</strong>
                <small>{item.customer_name || '-'} | {item.imei || '-'}</small>
              </div>
            ))}
            {gpsReports.length === 0 && <div className="empty-state">No GPS reports generated yet.</div>}
          </div>
        </div>
        <div className="workspace-card glass-panel">
          <div className="section-title-row"><h3>Command history</h3><small>{commandHistory.length} commands</small></div>
          <div className="history-timeline side">
            {commandHistory.slice(0, 8).map((item) => (
              <div className="history-event" key={item.id || `${item.command_name}-${item.created_at}`}>
                <span>{item.created_at ? formatDateTime(item.created_at) : '-'}</span>
                <strong>{item.command_name || item.command_key}</strong>
                <small>{item.imei || '-'} | {item.status || 'pending'}</small>
              </div>
            ))}
            {commandHistory.length === 0 && <div className="empty-state">No GPS commands sent yet.</div>}
          </div>
        </div>
      </section>

      {actionLog.length > 0 && (
        <section className="workspace-card glass-panel gps-action-log">
          <div className="section-title-row"><h3>Recent GPS actions</h3><small>{actionLog.length} latest</small></div>
          <div>
            {actionLog.map((item) => (
              <div className="gps-action-log-item" key={item.id}>
                <span>{item.type}</span>
                <strong>{item.label}</strong>
                <small>{item.vehicle} | {item.time}</small>
              </div>
            ))}
          </div>
        </section>
      )}
      </>
      )}
    </motion.div>
  );
}

function AccountSection({ onOpenPayments, accountView = '' }) {
  const [summary, setSummary] = useState({ total_revenue: 0, paid_amount: 0, pending_amount: 0, today_sales: 0, device_revenue: 0, service_revenue: 0 });
  const [ledger, setLedger] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountError, setAccountError] = useState('');
  const [accountSearch, setAccountSearch] = useState('');
  const [accountType, setAccountType] = useState('all');
  const [dateScope, setDateScope] = useState('all');
  const [ledgerPage, setLedgerPage] = useState(1);
  const [paymentPage, setPaymentPage] = useState(1);
  const summaryRef = useRef(null);
  const ledgerRef = useRef(null);
  const paymentsRef = useRef(null);
  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);
  const accountPageSize = 5;
  const activeAccountView = accountView || 'accounts-summary';

  const fetchAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    setAccountError('');
    try {
      const [summaryRes, ledgerRes, paymentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/sales/summary`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/payments/ledger`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/payments`, { headers: getHeaders() }),
      ]);
      setSummary(summaryRes.data || {});
      setLedger(ledgerRes.data || []);
      setPayments(paymentsRes.data || []);
    } catch (err) {
      console.log(err);
      setAccountError('Account data load failed');
    } finally {
      setLoadingAccounts(false);
    }
  }, [getHeaders]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  useEffect(() => {
    if (!accountView) return;
    const targetMap = {
      'accounts-revenue': summaryRef,
      'accounts-dues': ledgerRef,
      'accounts-credit-debit-ledger': ledgerRef,
      'accounts-payment-flow': paymentsRef
    };
    const target = targetMap[accountView];
    if (target) window.setTimeout(() => revealPanel(target), 0);
    if (accountView === 'accounts-payment-flow') {
      fetchAccounts();
    }
  }, [accountView, fetchAccounts]);

  useEffect(() => {
    setLedgerPage(1);
    setPaymentPage(1);
  }, [accountSearch, accountType, dateScope, activeAccountView]);

  const accountLabel = useCallback((type = '') => (({
    customer: 'Customer',
    dealer: 'Dealer',
    vendor: 'Vendor',
    purchase: 'Vendor',
    admin: 'Admin',
    whitelabel_admin: 'White Label',
    employee: 'Employee'
  })[type] || formatRole(type || 'account')), []);

  const dateKey = useCallback((value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return [
      parsed.getFullYear(),
      String(parsed.getMonth() + 1).padStart(2, '0'),
      String(parsed.getDate()).padStart(2, '0')
    ].join('-');
  }, []);
  const todayKey = getLocalDateKey();
  const currentMonthKey = todayKey.slice(0, 7);
  const inDateScope = useCallback((value) => {
    if (dateScope === 'all') return true;
    const key = dateKey(value);
    if (!key) return false;
    if (dateScope === 'today') return key === todayKey;
    if (dateScope === 'month') return key.slice(0, 7) === currentMonthKey;
    return true;
  }, [currentMonthKey, dateKey, dateScope, todayKey]);
  const matchesSearch = useCallback((values) => {
    const query = accountSearch.trim().toLowerCase();
    if (!query) return true;
    return values.some((value) => String(value || '').toLowerCase().includes(query));
  }, [accountSearch]);
  const ledgerAmount = useCallback((item) => {
    const debit = Number(item.total_debit ?? item.total_purchase ?? 0);
    const credit = Number(item.total_credit ?? item.total_paid ?? 0);
    const balance = Number(item.pending ?? (debit - credit));
    return { debit, credit, balance };
  }, []);
  const accountTypeOptions = useMemo(() => {
    const types = [...new Set(ledger.map((item) => item.account_type).filter(Boolean))];
    return [{ value: 'all', label: 'All Accounts' }, ...types.map((type) => ({ value: type, label: accountLabel(type) }))];
  }, [ledger, accountLabel]);
  const filteredLedger = useMemo(() => {
    const pendingOnly = activeAccountView === 'accounts-dues';
    return ledger
      .filter((item) => accountType === 'all' || item.account_type === accountType)
      .filter((item) => !pendingOnly || ledgerAmount(item).balance > 0)
      .filter((item) => inDateScope(item.last_payment_date))
      .filter((item) => matchesSearch([
        item.account_name,
        item.dealer_name,
        item.account_mobile,
        item.dealer_mobile,
        item.vehicle_no,
        item.account_type
      ]))
      .sort((a, b) => (activeAccountView === 'accounts-dues' ? ledgerAmount(b).balance - ledgerAmount(a).balance : 0));
  }, [ledger, accountType, activeAccountView, inDateScope, matchesSearch, ledgerAmount]);
  const filteredPayments = useMemo(() => (
    payments
      .filter((item) => accountType === 'all' || item.from_type === accountType || (accountType === 'vendor' && item.from_type === 'purchase'))
      .filter((item) => inDateScope(item.created_at))
      .filter((item) => matchesSearch([
        item.from_name,
        item.from_mobile,
        item.vehicle_no,
        item.invoice_no,
        item.payment_mode,
        item.note
      ]))
  ), [payments, accountType, inDateScope, matchesSearch]);
  const pendingLedger = useMemo(() => (
    ledger
      .filter((item) => ledgerAmount(item).balance > 0)
      .sort((a, b) => ledgerAmount(b).balance - ledgerAmount(a).balance)
  ), [ledger, ledgerAmount]);
  const ledgerTotalPages = Math.max(1, Math.ceil(filteredLedger.length / accountPageSize));
  const paymentTotalPages = Math.max(1, Math.ceil(filteredPayments.length / accountPageSize));
  const safeLedgerPage = Math.min(ledgerPage, ledgerTotalPages);
  const safePaymentPage = Math.min(paymentPage, paymentTotalPages);
  const visibleLedger = filteredLedger.slice((safeLedgerPage - 1) * accountPageSize, safeLedgerPage * accountPageSize);
  const visiblePayments = filteredPayments.slice((safePaymentPage - 1) * accountPageSize, safePaymentPage * accountPageSize);
  const showLedgerPanel = activeAccountView !== 'accounts-payment-flow' && activeAccountView !== 'accounts-revenue';
  const showPaymentsPanel = activeAccountView === 'accounts-payment-flow' || !accountView;
  const exportCsv = (rows, columns, filename) => {
    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csv = [
      columns.map((column) => escapeCsv(column.label)).join(','),
      ...rows.map((row) => columns.map((column) => escapeCsv(column.value(row))).join(','))
    ].join('\n');
    const blobUrl = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  };
  const exportLedgerCsv = () => exportCsv(filteredLedger, [
    { label: 'Account', value: (row) => row.account_name || row.dealer_name || '-' },
    { label: 'Type', value: (row) => accountLabel(row.account_type) },
    { label: 'Mobile', value: (row) => row.account_mobile || row.dealer_mobile || '-' },
    { label: 'Vehicle', value: (row) => row.vehicle_no || '-' },
    { label: 'Debit', value: (row) => ledgerAmount(row).debit },
    { label: 'Credit', value: (row) => ledgerAmount(row).credit },
    { label: 'Balance', value: (row) => ledgerAmount(row).balance },
    { label: 'Cash', value: (row) => row.cash_amount || 0 },
    { label: 'Online', value: (row) => Number(row.online_amount || 0) + Number(row.gpay_amount || 0) + Number(row.paytm_amount || 0) + Number(row.phonepe_amount || 0) },
    { label: 'Last Payment', value: (row) => row.last_payment_date ? formatDate(row.last_payment_date) : '-' }
  ], activeAccountView === 'accounts-dues' ? 'pending-dues.csv' : 'account-ledger.csv');
  const exportPaymentsCsv = () => exportCsv(filteredPayments, [
    { label: 'From', value: (row) => row.from_name || accountLabel(row.from_type) },
    { label: 'Type', value: (row) => accountLabel(row.from_type) },
    { label: 'Mobile', value: (row) => row.from_mobile || '-' },
    { label: 'Vehicle', value: (row) => row.vehicle_no || '-' },
    { label: 'Invoice', value: (row) => row.invoice_no || '-' },
    { label: 'Amount', value: (row) => row.amount || 0 },
    { label: 'Mode', value: (row) => formatRole(row.payment_mode || '') },
    { label: 'Note', value: (row) => row.note || '-' },
    { label: 'Date', value: (row) => row.created_at ? formatDate(row.created_at) : '-' }
  ], 'daily-payment-flow.csv');

  const summaryItems = [
    { label: 'Total Revenue', value: formatMoney(summary.total_revenue), icon: BarChart3, color: '#10b981' },
    { label: 'Paid Amount', value: formatMoney(summary.paid_amount), icon: WalletCards, color: '#0891b2' },
    { label: 'Pending Amount', value: formatMoney(summary.pending_amount), icon: CreditCard, color: '#7c3aed' },
    { label: 'Device Revenue', value: formatMoney(summary.device_revenue), icon: ShoppingCart, color: '#0b66ae' },
    { label: 'Service Revenue', value: formatMoney(summary.service_revenue), icon: ReceiptText, color: '#f59e0b' },
    { label: "Today's Sales", value: summary.today_sales || 0, icon: ShoppingCart, color: '#0b66ae' },
  ];

  return (
    <motion.div {...pageMotion} className="account-section-compact">
      <WorkspaceHero icon={WalletCards} kicker="accounts" title="Account Section" desc="Focused account workspace for payments, dues, dealer ledgers, and revenue snapshots.">
        {onOpenPayments && <ShellButton onClick={onOpenPayments}><CreditCard size={16} /> Payments</ShellButton>}
        <ShellButton onClick={fetchAccounts}><RefreshCw size={16} /> Refresh</ShellButton>
      </WorkspaceHero>
      <div className="account-control-bar glass-panel">
        <label className="account-search-field">
          <Search size={16} />
          <input value={accountSearch} onChange={(event) => setAccountSearch(event.target.value)} placeholder="Search account, mobile, vehicle, invoice..." />
        </label>
        <select value={accountType} onChange={(event) => setAccountType(event.target.value)}>
          {accountTypeOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
        </select>
        <select value={dateScope} onChange={(event) => setDateScope(event.target.value)}>
          <option value="all">All Dates</option>
          <option value="today">Today</option>
          <option value="month">This Month</option>
        </select>
        {accountSearch && <button type="button" className="account-clear-btn" onClick={() => setAccountSearch('')}><X size={15} /> Clear</button>}
        <span className="account-result-pill">{filteredLedger.length} ledger | {filteredPayments.length} payments</span>
      </div>
      {accountError && <div className="empty-state account-error-state">{accountError}</div>}
      <div className="ops-grid" ref={summaryRef}>
        {summaryItems.map((item) => (
          <div className="ops-metric glass-panel" style={{ '--accent': item.color }} key={item.label}>
            <item.icon size={22} />
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </div>
      {activeAccountView === 'accounts-revenue' && (
        <section className="workspace-card glass-panel account-focus-panel">
          <div className="section-title-row">
            <h3>Revenue snapshot</h3>
            <small>{loadingAccounts ? 'Refreshing...' : `${summary.total_sales || 0} sales records`}</small>
          </div>
          <div className="account-breakdown-grid">
            <div><span>Device Revenue</span><strong>{formatMoney(summary.device_revenue)}</strong><small>Paid {formatMoney(summary.device_paid_amount)}</small></div>
            <div><span>Service Revenue</span><strong>{formatMoney(summary.service_revenue)}</strong><small>Paid {formatMoney(summary.service_paid_amount)}</small></div>
            <div><span>Pending Balance</span><strong>{formatMoney(summary.pending_amount)}</strong><small>{pendingLedger.length} pending accounts</small></div>
          </div>
        </section>
      )}
      {showLedgerPanel && (
      <section className="workspace-card glass-panel" ref={ledgerRef}>
        <div className="section-title-row account-section-title">
          <div>
            <h3>{activeAccountView === 'accounts-dues' ? 'Pending dues' : 'Credit debit ledger'}</h3>
            <small>{filteredLedger.length} filtered records</small>
          </div>
          <button type="button" className="account-export-btn" onClick={exportLedgerCsv}><Download size={15} /> Export</button>
        </div>
        <div className="account-data-shell">
          <table className="account-data-table">
            <thead>
              <tr>
                {['#', 'Account', 'Type', 'Mobile', 'Vehicle', 'Debit', 'Credit', 'Balance', 'Cash', 'Online', 'Last Payment'].map((head) => <th key={head}>{head}</th>)}
              </tr>
            </thead>
            <tbody>
          {visibleLedger.map((item, index) => {
            const debit = Number(item.total_debit ?? item.total_purchase ?? 0);
            const credit = Number(item.total_credit ?? item.total_paid ?? 0);
            const balance = Number(item.pending ?? (debit - credit));
            return (
              <tr key={`${item.account_type}-${item.id}`}>
                <td>{((safeLedgerPage - 1) * accountPageSize) + index + 1}</td>
                <td><strong>{item.account_name || item.dealer_name || '-'}</strong></td>
                <td>{accountLabel(item.account_type)}</td>
                <td>{item.account_mobile || item.dealer_mobile || '-'}</td>
                <td>{item.vehicle_no || '-'}</td>
                <td>{formatMoney(debit)}</td>
                <td className="account-positive">{formatMoney(credit)}</td>
                <td className={balance > 0 ? 'account-negative' : 'account-positive'}>{formatMoney(balance)}</td>
                <td>{formatMoney(item.cash_amount)}</td>
                <td>{formatMoney(Number(item.online_amount || 0) + Number(item.gpay_amount || 0) + Number(item.paytm_amount || 0) + Number(item.phonepe_amount || 0))}</td>
                <td>{item.last_payment_date ? formatDate(item.last_payment_date) : '-'}</td>
              </tr>
            );
          })}
              {visibleLedger.length === 0 && <tr><td colSpan="11"><div className="empty-state">No ledger records found.</div></td></tr>}
            </tbody>
          </table>
        </div>
        <div className="account-pagination">
          <span>{filteredLedger.length === 0 ? '0' : ((safeLedgerPage - 1) * accountPageSize) + 1}-{Math.min(safeLedgerPage * accountPageSize, filteredLedger.length)} of {filteredLedger.length}</span>
          <button type="button" disabled={safeLedgerPage <= 1} onClick={() => setLedgerPage((page) => Math.max(1, page - 1))}>Previous</button>
          <strong>Page {safeLedgerPage} / {ledgerTotalPages}</strong>
          <button type="button" disabled={safeLedgerPage >= ledgerTotalPages} onClick={() => setLedgerPage((page) => Math.min(ledgerTotalPages, page + 1))}>Next</button>
        </div>
      </section>
      )}
      {showPaymentsPanel && (
      <section className="workspace-card glass-panel" ref={paymentsRef}>
        <div className="section-title-row account-section-title">
          <div>
            <h3>Daily payment flow</h3>
            <small>{filteredPayments.length} filtered records</small>
          </div>
          <button type="button" className="account-export-btn" onClick={exportPaymentsCsv}><Download size={15} /> Export</button>
        </div>
        <div className="account-data-shell">
          <table className="account-data-table">
            <thead>
              <tr>
                {['#', 'From', 'Type', 'Mobile', 'Vehicle', 'Invoice', 'Amount', 'Mode', 'Note', 'Date'].map((head) => <th key={head}>{head}</th>)}
              </tr>
            </thead>
            <tbody>
              {visiblePayments.map((item, index) => (
                <tr key={item.id}>
                  <td>{((safePaymentPage - 1) * accountPageSize) + index + 1}</td>
                  <td><strong>{item.from_name || accountLabel(item.from_type)}</strong></td>
                  <td>{accountLabel(item.from_type)}</td>
                  <td>{item.from_mobile || '-'}</td>
                  <td>{item.vehicle_no || '-'}</td>
                  <td>{item.invoice_no || '-'}</td>
                  <td className="account-positive">{formatMoney(item.amount)}</td>
                  <td>{formatRole(item.payment_mode || '')}</td>
                  <td>{item.note || '-'}</td>
                  <td>{item.created_at ? formatDate(item.created_at) : '-'}</td>
                </tr>
              ))}
              {visiblePayments.length === 0 && <tr><td colSpan="10"><div className="empty-state">No payment records found.</div></td></tr>}
            </tbody>
          </table>
        </div>
        <div className="account-pagination">
          <span>{filteredPayments.length === 0 ? '0' : ((safePaymentPage - 1) * accountPageSize) + 1}-{Math.min(safePaymentPage * accountPageSize, filteredPayments.length)} of {filteredPayments.length}</span>
          <button type="button" disabled={safePaymentPage <= 1} onClick={() => setPaymentPage((page) => Math.max(1, page - 1))}>Previous</button>
          <strong>Page {safePaymentPage} / {paymentTotalPages}</strong>
          <button type="button" disabled={safePaymentPage >= paymentTotalPages} onClick={() => setPaymentPage((page) => Math.min(paymentTotalPages, page + 1))}>Next</button>
        </div>
      </section>
      )}
    </motion.div>
  );
}

function WhatsAppConfig({ user, whatsAppView = '' }) {
  const { settings, setSettings, saveSettings, users } = useWorkspaceSettings();
  const [quickNumber, setQuickNumber] = useState('');
  const [quickName, setQuickName] = useState('');
  const [workItems, setWorkItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [savingWork, setSavingWork] = useState(false);
  const numberConfigRef = useRef(null);
  const quickEntryRef = useRef(null);
  const workQueueRef = useRef(null);
  const canEdit = isWorkspaceAdmin(user?.role);
  const canManageWorkItems = isWorkspaceAdmin(user?.role);
  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);
  const messageTemplates = [
    'Hello {name}, your renewal is due soon. Please contact our support team.',
    'Hello {name}, payment is pending on your CRM account. Please clear it today.',
    'Hello {name}, your device installation update is ready in BizTrack CRM.',
  ];
  const targetNumber = String(quickNumber || settings.whatsapp_number || '').replace(/\D/g, '');
  const previewName = quickName || 'Customer';
  const preparedMessage = (settings.whatsapp_message || '')
    .replace(/\{\{name\}\}/g, previewName)
    .replace(/\{name\}/g, previewName);

  const fetchWorkItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/whatsapp-work`, { headers: getHeaders() });
      setWorkItems(res.data || []);
    } catch (err) {
      console.log(err);
    }
  }, [getHeaders]);

  useEffect(() => { fetchWorkItems(); }, [fetchWorkItems]);

  useEffect(() => {
    const filterMap = {
      'whatsapp-work-all': 'all',
      'whatsapp-work-draft': 'draft',
      'whatsapp-work-ready': 'ready',
      'whatsapp-work-sent': 'sent',
      'whatsapp-work-cancelled': 'cancelled'
    };
    if (filterMap[whatsAppView]) setStatusFilter(filterMap[whatsAppView]);
    const targetMap = {
      'whatsapp-number-config': { ref: numberConfigRef, focus: 'input' },
      'whatsapp-template': { ref: numberConfigRef, focus: 'textarea' },
      'whatsapp-quick-entry': { ref: quickEntryRef, focus: 'input' },
      'whatsapp-work-all': { ref: workQueueRef },
      'whatsapp-work-draft': { ref: workQueueRef },
      'whatsapp-work-ready': { ref: workQueueRef },
      'whatsapp-work-sent': { ref: workQueueRef },
      'whatsapp-work-cancelled': { ref: workQueueRef }
    };
    const target = targetMap[whatsAppView];
    if (target) window.setTimeout(() => revealPanel(target.ref, target.focus), 0);
  }, [whatsAppView]);

  const fillQuickContact = (contact) => {
    setQuickNumber(contact.mobile || '');
    setQuickName(contact.name || '');
  };
  const quickContacts = (users || []).filter((item) => item.mobile).slice(0, 8);
  const statusCounts = workItems.reduce((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1;
    return acc;
  }, {});
  const visibleWorkItems = statusFilter === 'all' ? workItems : workItems.filter((item) => item.status === statusFilter);

  const saveWhatsApp = async () => {
    try {
      await saveSettings({
        whatsapp_number: settings.whatsapp_number,
        whatsapp_message: settings.whatsapp_message
      });
      alert('WhatsApp configuration saved');
    } catch (err) {
      alert(err.response?.data?.message || 'Could not save WhatsApp config');
    }
  };

  const addWorkItem = async ({ number = targetNumber, name = previewName, message = preparedMessage, source = 'manual' } = {}) => {
    const cleanNumber = String(number || '').replace(/\D/g, '');
    if (!cleanNumber) return alert('WhatsApp mobile number required');
    if (!String(message || '').trim()) return alert('Message required');
    setSavingWork(true);
    try {
      await axios.post(`${API_URL}/api/whatsapp-work`, {
        contact_name: name,
        mobile: cleanNumber,
        message,
        source
      }, { headers: getHeaders() });
      alert('WhatsApp item added to the work panel');
      fetchWorkItems();
    } catch (err) {
      alert(err.response?.data?.message || 'Could not add WhatsApp work');
    } finally {
      setSavingWork(false);
    }
  };

  const updateWorkStatus = async (item, status) => {
    try {
      const res = await axios.patch(`${API_URL}/api/whatsapp-work/${item.id}`, { status }, { headers: getHeaders() });
      setWorkItems((current) => current.map((row) => row.id === item.id ? res.data.item : row));
    } catch (err) {
      alert(err.response?.data?.message || 'Could not update WhatsApp work');
    }
  };

  const removeWorkItem = async (item) => {
    if (!window.confirm('Remove this WhatsApp work item from panel?')) return;
    try {
      await axios.delete(`${API_URL}/api/whatsapp-work/${item.id}`, { headers: getHeaders() });
      setWorkItems((current) => current.filter((row) => row.id !== item.id));
    } catch (err) {
      alert(err.response?.data?.message || 'Could not remove WhatsApp work');
    }
  };

  const copyMessage = async (item) => {
    try {
      await navigator.clipboard.writeText(item.message || '');
      alert('Message copied from CRM panel');
    } catch (err) {
      alert('Could not copy message');
    }
  };

  return (
    <motion.div {...pageMotion}>
      <WorkspaceHero icon={MessageCircle} kicker="whatsapp work" title="WhatsApp Work Panel" desc="Prepare, queue, copy, and manage sent status for WhatsApp messages inside CRM.">
        {canEdit && <ShellButton onClick={saveWhatsApp}><Settings size={16} /> Save Config</ShellButton>}
        <ShellButton onClick={fetchWorkItems}><RefreshCw size={16} /> Refresh Panel</ShellButton>
      </WorkspaceHero>
      <section className="config-grid">
        <div className="workspace-card glass-panel" ref={numberConfigRef}>
          <div className="section-title-row">
            <h3>WhatsApp number config</h3>
            <small>Save the number with the country code</small>
          </div>
          <label>WhatsApp Number</label>
          <input value={settings.whatsapp_number || ''} onChange={(event) => setSettings((current) => ({ ...current, whatsapp_number: event.target.value }))} placeholder="919999999999" disabled={!canEdit} />
          <label>Default Message</label>
          <textarea value={settings.whatsapp_message || ''} onChange={(event) => setSettings((current) => ({ ...current, whatsapp_message: event.target.value }))} placeholder="Message template" disabled={!canEdit} />
          <div className="template-chip-row">
            {messageTemplates.map((template) => (
              <button type="button" key={template} onClick={() => canEdit && setSettings((current) => ({ ...current, whatsapp_message: template }))}>{template.split('.')[0]}</button>
            ))}
          </div>
          <div className="message-preview">
            {preparedMessage}
          </div>
          <button type="button" className="open-link-button" disabled={savingWork} onClick={() => addWorkItem({ number: settings.whatsapp_number, name: previewName, message: preparedMessage, source: 'saved_template' })}><ClipboardList size={16} /> Add Saved Message To Panel</button>
        </div>
        <div className="whatsapp-console glass-panel">
          <div className="wa-header">
            <Phone size={18} />
            <strong>Internal WhatsApp Panel Active</strong>
          </div>
          <div className="wa-status-panel">
            <span><CheckCircle2 size={18} /> External launch disabled</span>
            <span><MessageCircle size={18} /> {workItems.length} CRM work items</span>
          </div>
          <div className="wa-quick-send" ref={quickEntryRef}>
            <div className="section-title-row">
              <h3>Quick panel entry</h3>
              <small>{targetNumber ? `To ${targetNumber}` : 'No number selected'}</small>
            </div>
            <label>Customer / User Name</label>
            <input value={quickName} onChange={(event) => setQuickName(event.target.value)} placeholder="Customer name" />
            <label>Mobile Number</label>
            <input value={quickNumber} onChange={(event) => setQuickNumber(event.target.value)} placeholder="919999999999" />
            <button type="button" className="open-link-button" disabled={savingWork} onClick={() => addWorkItem({ number: targetNumber, name: previewName, message: preparedMessage, source: 'quick_panel' })}><Send size={16} /> Add To Work Panel</button>
            {quickContacts.length > 0 && (
              <div className="wa-contact-pills">
                {quickContacts.map((contact) => (
                  <button type="button" key={contact.id} onClick={() => fillQuickContact(contact)}>
                    {contact.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      <section className="workspace-card glass-panel" ref={workQueueRef}>
        <div className="section-title-row">
          <h3>WhatsApp work queue</h3>
          <small>Draft, ready and sent work stays inside CRM panel</small>
        </div>
        <div className="wa-filter-row">
          {[
            { key: 'all', label: `All ${workItems.length}` },
            { key: 'draft', label: `Draft ${statusCounts.draft || 0}` },
            { key: 'ready', label: `Ready ${statusCounts.ready || 0}` },
            { key: 'sent', label: `Sent ${statusCounts.sent || 0}` },
            { key: 'cancelled', label: `Cancelled ${statusCounts.cancelled || 0}` },
          ].map((item) => (
            <button type="button" key={item.key} className={statusFilter === item.key ? 'active' : ''} onClick={() => setStatusFilter(item.key)}>{item.label}</button>
          ))}
        </div>
        <div className="whatsapp-work-list">
          {visibleWorkItems.map((item) => (
            <div className="whatsapp-work-item" key={item.id}>
              <div>
                <strong>{item.contact_name || 'WhatsApp Contact'}</strong>
                <span>{item.mobile} | {formatRole(item.source || 'manual')}</span>
              </div>
              <p>{item.message}</p>
              <em>{formatRole(item.status)}</em>
              <div className="whatsapp-work-actions">
                <button type="button" onClick={() => copyMessage(item)}><ClipboardList size={14} /> Copy</button>
                {canManageWorkItems && <button type="button" onClick={() => updateWorkStatus(item, 'ready')} disabled={item.status === 'ready'}>Ready</button>}
                {canManageWorkItems && <button type="button" onClick={() => updateWorkStatus(item, 'sent')} disabled={item.status === 'sent'}><CheckCircle2 size={14} /> Sent</button>}
                {canManageWorkItems && <button type="button" onClick={() => removeWorkItem(item)}><X size={14} /></button>}
              </div>
            </div>
          ))}
          {visibleWorkItems.length === 0 && <div className="empty-state">No WhatsApp work items in this panel.</div>}
        </div>
      </section>
    </motion.div>
  );
}

function TeamChatCenter({ user, teamChatView = '' }) {
  const [chatData, setChatData] = useState({ users: [], requests: [], settings: workspaceDefaultSettings, allowed_user_ids: [], can_chat: true, retention_days: 30 });
  const [allowedIds, setAllowedIds] = useState([]);
  const [receiverId, setReceiverId] = useState('');
  const [activeRequest, setActiveRequest] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);
  const canAdminEdit = isWorkspaceAdmin(user?.role);
  const canDirectChat = isWorkspaceAdmin(user?.role);

  const fetchChat = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/workspace/chat`, { headers: getHeaders() });
      setChatData(res.data);
      const allUserIds = (res.data.users || []).map((item) => item.id);
      setAllowedIds((res.data.allowed_user_ids || []).length > 0 ? res.data.allowed_user_ids : allUserIds);
    } catch (err) {
      console.log(err);
    }
  }, [getHeaders]);

  useEffect(() => { fetchChat(); }, [fetchChat]);

  useEffect(() => {
    if (!activeRequest) return;
    const freshRequest = (chatData.requests || []).find((request) => Number(request.id) === Number(activeRequest.id));
    if (!freshRequest) {
      setActiveRequest(null);
      setMessages([]);
      return;
    }
    if (freshRequest.status !== activeRequest.status) setActiveRequest(freshRequest);
  }, [chatData.requests, activeRequest]);

  const saveChatSettings = async () => {
    const allUserIds = chatData.users.map((item) => item.id);
    try {
      await axios.post(`${API_URL}/api/workspace/settings`, {
        team_chat_enabled: '1',
        allowed_user_ids: allUserIds
      }, { headers: getHeaders() });
      setAllowedIds(allUserIds);
      alert('Team chat permission granted to all users');
      fetchChat();
    } catch (err) {
      alert(err.response?.data?.message || 'Could not save chat settings');
    }
  };

  const requestChat = async () => {
    if (!receiverId) return alert('Please select a user');
    const selectedReceiver = chatData.users.find((item) => Number(item.id) === Number(receiverId));
    try {
      const res = await axios.post(`${API_URL}/api/workspace/chat/request`, { receiver_id: receiverId }, { headers: getHeaders() });
      setReceiverId('');
      await fetchChat();
      if (canDirectChat && res.data?.request) {
        await loadMessages({
          ...res.data.request,
          requester_name: user?.name || 'Admin',
          receiver_name: selectedReceiver?.name || 'User'
        });
        return;
      }
      alert('Chat request sent');
    } catch (err) {
      alert(err.response?.data?.message || (canDirectChat ? 'Could not open chat' : 'Could not send request'));
    }
  };

  const updateRequest = async (request, status) => {
    try {
      await axios.patch(`${API_URL}/api/workspace/chat/request/${request.id}`, { status }, { headers: getHeaders() });
      await fetchChat();
      if (status === 'accepted') {
        await loadMessages({ ...request, status });
      } else if (Number(activeRequest?.id) === Number(request.id)) {
        setActiveRequest(null);
        setMessages([]);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Could not update request');
    }
  };

  const loadMessages = async (request) => {
    setActiveRequest(request);
    try {
      const res = await axios.get(`${API_URL}/api/workspace/chat/messages/${request.id}`, { headers: getHeaders() });
      setMessages(res.data || []);
    } catch (err) {
      alert(err.response?.data?.message || 'Could not load messages');
    }
  };

  const sendMessage = async () => {
    if (!activeRequest || !message.trim()) return;
    try {
      await axios.post(`${API_URL}/api/workspace/chat/messages`, {
        request_id: activeRequest.id,
        message
      }, { headers: getHeaders() });
      setMessage('');
      await loadMessages(activeRequest);
      await fetchChat();
    } catch (err) {
      alert(err.response?.data?.message || 'Could not send message');
    }
  };

  const pendingForMe = chatData.requests.filter((request) => request.status === 'pending' && Number(request.receiver_id) === Number(user?.id));
  const acceptedChats = chatData.requests.filter((request) => request.status === 'accepted');
  const requestList = chatData.requests.filter((request) => (
    request.status !== 'accepted'
    && !(request.status === 'pending' && Number(request.receiver_id) === Number(user?.id))
  ));
  const permittedUserCount = Math.max(allowedIds.length, chatData.users.length);

  useEffect(() => {
    if (teamChatView === 'team-chat-refresh') fetchChat();
    if (teamChatView === 'team-chat-accepted' && acceptedChats[0] && !activeRequest) {
      loadMessages(acceptedChats[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamChatView]);

  return (
    <motion.div {...pageMotion}>
      <WorkspaceHero icon={MessageSquare} kicker="team chat" title="Team Chat" desc="Admins and super admins can chat directly with anyone. Other users can send requests.">
        <ShellButton onClick={fetchChat}><RefreshCw size={16} /> Refresh Chat</ShellButton>
      </WorkspaceHero>

      {canAdminEdit && (
        <section className="workspace-card glass-panel">
          <div className="section-title-row">
            <h3>Admin chat permission</h3>
            <small>{permittedUserCount} users allowed</small>
          </div>
          <div className="empty-state">All active users are allowed for team chat. An admin approval list is not required.</div>
          <div className="permission-grid">
            {chatData.users.map((item) => (
              <label className="permission-chip" key={item.id}>
                <input type="checkbox" checked readOnly />
                <span>{item.name}</span>
                <small>{displayRole(item.role, user?.role)}</small>
              </label>
            ))}
          </div>
          <ShellButton onClick={saveChatSettings}><Settings size={16} /> Sync All Permissions</ShellButton>
        </section>
      )}

      <section className="chat-shell glass-panel">
        <div className="chat-sidebar">
          <div className="section-title-row">
            <h3>{canDirectChat ? 'Direct Chat' : 'Requests'}</h3>
            <small>{chatData.retention_days} days backup</small>
          </div>
          {chatData.can_chat ? (
            <div className="chat-request-box">
              <select value={receiverId} onChange={(event) => setReceiverId(event.target.value)}>
                <option value="">{canDirectChat ? 'Select user to open chat' : 'Select user to request chat'}</option>
                {chatData.users.map((item) => (
                  <option key={item.id} value={item.id}>{item.name} - {displayRole(item.role, user?.role)}</option>
                ))}
              </select>
              <button type="button" onClick={requestChat}><Send size={16} /> {canDirectChat ? 'Open Chat' : 'Send Request'}</button>
            </div>
          ) : (
            <div className="empty-state">Chat requests are available for active users. Refresh and try again.</div>
          )}

          {pendingForMe.map((request) => (
            <div className="chat-request-card" key={request.id}>
              <strong>{request.requester_name}</strong>
              <span>Chat request pending</span>
              <div>
                <button type="button" onClick={() => updateRequest(request, 'accepted')}>Accept</button>
                <button type="button" onClick={() => updateRequest(request, 'rejected')}>Reject</button>
              </div>
            </div>
          ))}

          {requestList.map((request) => (
            <div className="chat-request-card muted" key={request.id}>
              <strong>{request.requester_name} to {request.receiver_name}</strong>
              <span>{request.status}</span>
            </div>
          ))}
        </div>

        <div className="chat-window">
          <div className="chat-tabs">
            {acceptedChats.map((request) => (
              <button type="button" className={activeRequest?.id === request.id ? 'active' : ''} onClick={() => loadMessages(request)} key={request.id}>
                {request.requester_name} / {request.receiver_name}
              </button>
            ))}
            {acceptedChats.length === 0 && <span>No accepted chats yet</span>}
          </div>
          <div className="message-stream">
            {messages.map((item) => (
              <div className={`message-bubble ${Number(item.sender_id) === Number(user?.id) ? 'mine' : ''}`} key={item.id}>
                <small>{item.sender_name}</small>
                <span>{item.message}</span>
              </div>
            ))}
            {!activeRequest && <div className="empty-state">Select an accepted chat.</div>}
            {activeRequest && messages.length === 0 && <div className="empty-state">No messages yet.</div>}
          </div>
          <div className="message-compose">
            <input
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Type message..."
              disabled={!activeRequest}
            />
            <button type="button" onClick={sendMessage} disabled={!activeRequest}><Send size={16} /></button>
          </div>
        </div>
      </section>
    </motion.div>
  );
}

export default function Dashboard() {
  const [user, setUser] = useState(() => getCurrentUser());
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState('dashboard');
  const [activeSubmenu, setActiveSubmenu] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('credyou-sidebar-collapsed') === 'true');
  const [theme, setTheme] = useState(() => localStorage.getItem('credyou-theme') || 'light');
  const [search, setSearch] = useState('');
  const [profilePanelOpen, setProfilePanelOpen] = useState(false);
  const [profileBusy, setProfileBusy] = useState(false);
  const [credentialsForm, setCredentialsForm] = useState({ username: user?.username || '', mobile: user?.mobile || '', new_password: '' });
  const [renewalPopup, setRenewalPopup] = useState({ open: false, items: [], storageKey: '' });
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalSales: 0,
    todaySales: 0,
    todayDeviceSales: 0,
    todayServiceSales: 0,
    deviceSales: 0,
    serviceSales: 0,
    deviceRevenue: 0,
    serviceRevenue: 0,
    totalRevenue: 0,
    totalInstallation: 0,
    paidAmount: 0,
    pendingPayments: 0,
    renewalsDue: 0,
    availableStock: 0,
  });
  const [liveRefreshKey, setLiveRefreshKey] = useState(0);
  const [customerVehicles, setCustomerVehicles] = useState([]);

  // Customer Profile Edit Modal State
  const [customerProfileModalOpen, setCustomerProfileModalOpen] = useState(false);
  const [customerProfileActiveTab, setCustomerProfileActiveTab] = useState('profile'); // 'profile' | 'drivers' | 'logs'
  const [customerProfileForm, setCustomerProfileForm] = useState({
    name: user?.name || '',
    mobile: user?.mobile || '',
    email: user?.email || '',
    address: user?.address || '',
    new_password: ''
  });
  const [customerVehiclesDrivers, setCustomerVehiclesDrivers] = useState([]);
  const [driverLogs, setDriverLogs] = useState([]);
  const [driverLogsLoading, setDriverLogsLoading] = useState(false);
  const [customerProfileBusy, setCustomerProfileBusy] = useState(false);
  const [customerProfileMsg, setCustomerProfileMsg] = useState(null);
  const [customerPicUploading, setCustomerPicUploading] = useState(false);

  const fetchDriverLogs = useCallback(async () => {
    setDriverLogsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/profile/driver-logs`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setDriverLogs(res.data || []);
    } catch (err) {
      console.log('Failed to fetch driver logs:', err);
    } finally {
      setDriverLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (customerProfileModalOpen && customerProfileActiveTab === 'logs') {
      fetchDriverLogs();
    }
  }, [customerProfileModalOpen, customerProfileActiveTab, fetchDriverLogs]);

  useEffect(() => {
    if (user) {
      setCustomerProfileForm((prev) => ({
        ...prev,
        name: user.name || '',
        mobile: user.mobile || '',
        email: user.email || '',
        address: user.address || ''
      }));
    }
  }, [user]);

  useEffect(() => {
    if (customerVehicles && customerVehicles.length > 0) {
      setCustomerVehiclesDrivers(customerVehicles.map(v => ({
        id: v.id,
        vehicle_no: v.vehicle_no,
        device_model: v.device_model || v.device_type || 'GPS Device',
        imei: v.imei || '-',
        driver_name: v.driver_name || ''
      })));
    }
  }, [customerVehicles]);

  const handleUploadCustomerProfilePic = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCustomerPicUploading(true);
    setCustomerProfileMsg(null);
    const formData = new FormData();
    formData.append('profile_pic', file);
    try {
      const res = await axios.post(`${API_URL}/api/profile/upload`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      if (res.data.filename) {
        updateSessionUser({ profile_pic: res.data.filename });
        setCustomerProfileMsg({ type: 'success', text: 'Profile picture updated successfully!' });
      }
    } catch (err) {
      setCustomerProfileMsg({ type: 'error', text: err.response?.data?.message || 'Failed to upload photo.' });
    } finally {
      setCustomerPicUploading(false);
    }
  };

  const handleUpdateVehicleDriver = (vehicleId, newDriverName) => {
    setCustomerVehiclesDrivers(prev => prev.map(v => v.id === vehicleId ? { ...v, driver_name: newDriverName } : v));
  };

  const handleUpdateCustomerProfile = async (e) => {
    e.preventDefault();
    setCustomerProfileBusy(true);
    setCustomerProfileMsg(null);
    try {
      const res = await axios.patch(
        `${API_URL}/api/profile/credentials`,
        {
          name: customerProfileForm.name,
          username: customerProfileForm.mobile,
          mobile: customerProfileForm.mobile,
          email: customerProfileForm.email,
          address: customerProfileForm.address,
          new_password: customerProfileForm.new_password,
          vehicles: customerVehiclesDrivers
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.data.token) localStorage.setItem('token', res.data.token);
      if (res.data.user) {
        updateSessionUser(res.data.user);
      }
      setCustomerVehicles(prev => prev.map(v => {
        const found = customerVehiclesDrivers.find(d => d.id === v.id);
        return found ? { ...v, driver_name: found.driver_name } : v;
      }));

      setCustomerProfileMsg({ type: 'success', text: 'Profile & Vehicle Drivers updated successfully!' });
      setTimeout(() => {
        setCustomerProfileModalOpen(false);
        setCustomerProfileMsg(null);
        setCustomerProfileForm((prev) => ({ ...prev, new_password: '' }));
      }, 1400);
    } catch (err) {
      setCustomerProfileMsg({
        type: 'error',
        text: err.response?.data?.message || 'Failed to update profile. Please try again.'
      });
    } finally {
      setCustomerProfileBusy(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'customer') {
      axios.get(`${API_URL}/api/customers`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
        .then((res) => {
          const list = res.data || [];
          setCustomerVehicles(list);
          if (list.length > 0 && !activeSubmenu) {
            setActiveSubmenu(String(list[0].id));
          }
        })
        .catch((err) => console.log(err));
    }
  }, [user?.role, activeSubmenu]);
  const [liveSync, setLiveSync] = useState({ status: 'connecting', label: 'Connecting' });
  const [pendingLiveRefresh, setPendingLiveRefresh] = useState(false);
  const [menuVisibility, setMenuVisibility] = useState({ hidden_menu_keys: [], hidden_submenu_keys: [], hidden_report_types: [] });
  const liveRefreshTimerRef = useRef(null);
  const liveStatusTimerRef = useRef(null);

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);
  const userId = user?.id;
  const userRole = user?.role;
  const updateSessionUser = useCallback((patch) => {
    setUser((current) => {
      const nextUser = { ...(current || {}), ...(patch || {}) };
      localStorage.setItem('user', JSON.stringify(nextUser));
      return nextUser;
    });
  }, []);

  useEffect(() => {
    if (!user) navigate('/login', { replace: true });
  }, [navigate, user]);

  useEffect(() => {
    const handleSessionChange = (event) => {
      const nextUser = event.detail?.user || getCurrentUser();
      setUser(nextUser);
      setSelectedCompany(null);
      setSidebarOpen(false);
      setActiveSubmenu('');
      setProfilePanelOpen(false);
      setActivePage('dashboard');
    };

    window.addEventListener(AUTH_SESSION_CHANGED_EVENT, handleSessionChange);
    return () => window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, handleSessionChange);
  }, []);

  useEffect(() => {
    if (!userId) return;
    let mounted = true;

    axios.get(`${API_URL}/api/profile/me`, {
      headers: getHeaders(),
      params: { host: window.location.host }
    })
      .then((res) => {
        if (!mounted) return;
        updateSessionUser({
          ...(res.data || {}),
          admin_id: res.data?.admin_id ?? user?.admin_id ?? null
        });
      })
      .catch((err) => console.log(err));

    return () => {
      mounted = false;
    };
  }, [getHeaders, updateSessionUser, user?.admin_id, userId, userRole]);

  const fetchMenuVisibility = useCallback(async () => {
    if (!userId || userRole === 'superadmin') {
      setMenuVisibility({ hidden_menu_keys: [], hidden_submenu_keys: [], hidden_report_types: [] });
      return;
    }

    try {
      const res = await axios.get(`${API_URL}/api/workspace/menu-permissions`, { headers: getHeaders() });
      setMenuVisibility({
        hidden_menu_keys: Array.isArray(res.data?.hidden_menu_keys) ? res.data.hidden_menu_keys : [],
        hidden_submenu_keys: Array.isArray(res.data?.hidden_submenu_keys) ? res.data.hidden_submenu_keys : [],
        hidden_report_types: Array.isArray(res.data?.hidden_report_types) ? res.data.hidden_report_types : []
      });
    } catch (err) {
      setMenuVisibility({ hidden_menu_keys: [], hidden_submenu_keys: [], hidden_report_types: [] });
    }
  }, [getHeaders, userId, userRole]);

  useEffect(() => {
    fetchMenuVisibility();
  }, [fetchMenuVisibility]);

  useEffect(() => {
    const refreshPermissions = () => fetchMenuVisibility();
    const handleDataChange = (event) => {
      const path = String(event.detail?.path || '');
      if (path.includes('/api/workspace/menu-permissions')) refreshPermissions();
    };

    window.addEventListener('focus', refreshPermissions);
    window.addEventListener(DATA_CHANGE_EVENT, handleDataChange);
    return () => {
      window.removeEventListener('focus', refreshPermissions);
      window.removeEventListener(DATA_CHANGE_EVENT, handleDataChange);
    };
  }, [fetchMenuVisibility]);

  const fetchStats = useCallback(async () => {
    try {
      const [custRes, salesRes, stockRes, renewalRes] = await Promise.all([
        axios.get(`${API_URL}/api/customers`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/sales/summary`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/inventory/stock`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/customers/expiring`, { headers: getHeaders() }),
      ]);
      setStats({
        totalCustomers: custRes.data.length,
        totalSales: salesRes.data.total_sales || 0,
        todaySales: salesRes.data.today_sales || 0,
        todayDeviceSales: salesRes.data.today_device_sales || 0,
        todayServiceSales: salesRes.data.today_service_sales || 0,
        deviceSales: salesRes.data.device_sales || 0,
        serviceSales: salesRes.data.service_sales || 0,
        deviceRevenue: salesRes.data.device_revenue || 0,
        serviceRevenue: salesRes.data.service_revenue || 0,
        totalRevenue: salesRes.data.total_revenue || 0,
        totalInstallation: salesRes.data.total_installation || 0,
        paidAmount: salesRes.data.paid_amount || 0,
        pendingPayments: salesRes.data.pending_amount || 0,
        renewalsDue: renewalRes.data.length,
        availableStock: stockRes.data.filter((s) => s.status === 'available').length,
      });
    } catch (err) {
      console.log(err);
    }
  }, [getHeaders]);

  useEffect(() => {
    if (isCompanyAdmin(user?.role)) fetchStats();
  }, [fetchStats, user?.role]);

  const applyLiveRefresh = useCallback(() => {
    setPendingLiveRefresh(false);
    setLiveRefreshKey((current) => current + 1);
    if (isCompanyAdmin(user?.role)) fetchStats();

    setLiveSync({ status: 'syncing', label: 'Syncing' });
    window.clearTimeout(liveStatusTimerRef.current);
    liveStatusTimerRef.current = window.setTimeout(() => {
      setLiveSync({ status: 'online', label: 'Live' });
    }, 1400);
  }, [fetchStats, user?.role]);

  useEffect(() => {
    if (!user?.id) return undefined;

    const socket = io(API_URL, {
      auth: { token: localStorage.getItem('token') },
      transports: ['websocket', 'polling']
    });

    socket.on('connect', () => {
      setLiveSync({ status: 'online', label: 'Live' });
    });

    socket.on('connect_error', () => {
      setLiveSync({ status: 'offline', label: 'Offline' });
    });

    socket.on('disconnect', () => {
      setLiveSync({ status: 'offline', label: 'Offline' });
    });

    socket.on('app:data-changed', (change) => {
      window.dispatchEvent(new CustomEvent(DATA_CHANGE_EVENT, { detail: change }));
      setLiveSync({ status: 'syncing', label: 'Updating' });
      window.clearTimeout(liveRefreshTimerRef.current);
      liveRefreshTimerRef.current = window.setTimeout(() => {
        if (isFormFieldFocused()) {
          setPendingLiveRefresh(true);
          setLiveSync({ status: 'pending', label: 'Tap to sync' });
          if (isCompanyAdmin(user?.role)) fetchStats();
          return;
        }

        applyLiveRefresh();
      }, 650);
    });

    return () => {
      window.clearTimeout(liveRefreshTimerRef.current);
      window.clearTimeout(liveStatusTimerRef.current);
      socket.disconnect();
    };
  }, [applyLiveRefresh, fetchStats, user?.id, user?.role]);

  useEffect(() => {
    if (!user) return undefined;

    let cancelled = false;
    const loadDailyRenewalPopup = async () => {
      try {
        const storageKey = `credyou-renewal-popup-${user.role || 'user'}-${user.id || user.mobile || 'account'}-${getLocalDateKey()}`;
        if (localStorage.getItem(storageKey) === 'done') return;

        const res = await axios.get(`${API_URL}/api/customers/expiring`, { headers: getHeaders() });
        const rows = res.data || [];
        if (!cancelled && rows.length > 0) {
          setRenewalPopup({ open: true, items: rows, storageKey });
        }
      } catch (err) {
        console.log(err);
      }
    };

    loadDailyRenewalPopup();
    const timer = setInterval(loadDailyRenewalPopup, 60 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [getHeaders, user]);

  useEffect(() => {
    localStorage.setItem('credyou-theme', theme);
  }, [theme]);

  const hiddenMenuKeys = useMemo(() => new Set(menuVisibility.hidden_menu_keys || []), [menuVisibility.hidden_menu_keys]);
  const hiddenSubmenuKeys = useMemo(() => new Set(menuVisibility.hidden_submenu_keys || []), [menuVisibility.hidden_submenu_keys]);

  const roleAllowedModules = useMemo(() => (
    moduleCatalog
      .filter((item) => {
        const roleAllowed = item.strictRoles ? item.strictRoles.includes(user?.role) : user?.role === 'superadmin' || item.roles.includes(user?.role);
        if (!roleAllowed) return false;
        if (user?.role !== 'superadmin' && item.key !== 'dashboard' && hiddenMenuKeys.has(item.key)) return false;
        return true;
      })
      .map((item) => {
        if (!Array.isArray(item.subItems)) return item;
        return {
          ...item,
          subItems: filterSubmenuItems(item.subItems, item.key, user?.role, hiddenSubmenuKeys, item.roles)
        };
      })
  ), [hiddenMenuKeys, hiddenSubmenuKeys, user?.role]);

  const availableModules = useMemo(() => (
    roleAllowedModules
      .filter((item) => moduleMatchesSearch(item, user?.role, search.trim().toLowerCase()))
  ), [roleAllowedModules, search, user?.role]);

  useEffect(() => {
    if (!user || activePage === 'dashboard') return;
    if (user.role === 'superadmin' && activePage === 'company-detail') return;
    if (!roleAllowedModules.some((item) => item.key === activePage)) {
      setSelectedCompany(null);
      setActiveSubmenu('');
      setActivePage('dashboard');
    }
  }, [activePage, roleAllowedModules, user]);

  useEffect(() => {
    if (!activeSubmenu || user?.role === 'superadmin' || user?.role === 'customer') return;
    const activeModule = roleAllowedModules.find((item) => item.key === activePage);
    if (!submenuContainsKey(activeModule?.subItems || [], activeSubmenu)) setActiveSubmenu('');
  }, [activePage, activeSubmenu, roleAllowedModules, user?.role]);

  const openPage = (key, submenu = '') => {
    setActivePage(key);
    setActiveSubmenu(user?.role !== 'superadmin' && user?.role !== 'customer' && hiddenSubmenuKeys.has(`${key}:${submenu}`) ? '' : submenu);
    setSelectedCompany(null);
    setSidebarOpen(false);
  };

  const closeRenewalPopup = () => {
    if (renewalPopup.storageKey) {
      localStorage.setItem(renewalPopup.storageKey, 'done');
    }
    setRenewalPopup((current) => ({ ...current, open: false }));
  };

  const openRenewalDetails = () => {
    closeRenewalPopup();
    if (user?.role === 'dealer') {
      openPage('dashboard');
    } else if (user?.role === 'customer') {
      openPage('gps-tracking');
    } else if (user?.role === 'employee') {
      openPage('customers');
    } else {
      openPage('renewals');
    }
  };

  const logout = () => {
    clearAuthSession();
    navigate('/login');
  };

  const returnToImpersonator = () => {
    const previousSession = popImpersonationSession();
    if (!previousSession?.token || !previousSession?.user) {
      logout();
      return;
    }

    try {
      const previousUser = typeof previousSession.user === 'string'
        ? JSON.parse(previousSession.user)
        : previousSession.user;
      const restoredUser = setAuthSession({ token: previousSession.token, user: previousUser });
      if (!restoredUser) throw new Error('Could not restore previous session');
      setUser(restoredUser);
      setProfilePanelOpen(false);
      setActiveSubmenu('');
      setActivePage('dashboard');
    } catch (err) {
      logout();
    }
  };

  const canEditOwnProfile = isCompanyAdmin(user?.role) || user?.role === 'superadmin';
  const returnUser = getImpersonationReturnUser();
  const isImpersonating = Boolean(returnUser || user?.impersonated_by_id);
  const hasHostBrand = Boolean(user?.brand_context_active && user?.brand_logo_url);
  const userPhotoSrc = user?.profile_pic ? getProfileImageUrl(user.profile_pic) : '';
  const brandLogoCandidate = user?.brand_logo_url && user.brand_logo_url !== '/assets/BizTrack_Logo.png' ? getBrandImageUrl(user.brand_logo_url) : '';
  const adminPhotoSrc = user?.admin_profile_pic ? getProfileImageUrl(user.admin_profile_pic) : '';
  const activeFaviconSrc = userPhotoSrc || brandLogoCandidate || adminPhotoSrc || (user?.role === 'superadmin' ? '/assets/BizTrack_Logo.png' : '');

  const displayProfilePic = user?.role === 'superadmin'
    ? user?.profile_pic
    : (isCompanyAdmin(user?.role) ? user?.profile_pic : user?.admin_profile_pic || user?.profile_pic);
  const profileImageSrc = getProfileImageUrl(displayProfilePic);
  const ownProfileImageSrc = getProfileImageUrl(user?.profile_pic);
  const brandLogoSrc = user?.role === 'superadmin' && !hasHostBrand ? '/assets/BizTrack_Logo.png' : getBrandImageUrl(user?.brand_logo_url);
  const browserBrandIconSrc = activeFaviconSrc;
  const shellBrandPrimary = hasHostBrand ? (user?.brand_primary_color || '#0b66ae') : '#0b66ae';
  const shellBrandSecondary = hasHostBrand ? (user?.brand_secondary_color || '#8cc63f') : '#8cc63f';
  const topbarModule = presentModule(moduleCatalog.find((m) => m.key === activePage) || { title: 'Workspace' }, user?.role);
  const topbarTitle = user?.role === 'customer'
    ? 'My Devices'
    : user?.role === 'superadmin'
      ? 'White Label Command Center'
      : user?.role === 'whitelabel_admin'
        ? 'Admin Command Center'
        : 'AI Business Dashboard';

  const uploadOwnProfilePicture = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setProfileBusy(true);
    try {
      const formData = new FormData();
      formData.append('profile_pic', file);
      const res = await axios.post(`${API_URL}/api/profile/upload`, formData, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      updateSessionUser({
        profile_pic: res.data.filename,
        admin_profile_pic: isCompanyAdmin(user?.role) ? res.data.filename : user?.admin_profile_pic
      });
      alert('Profile picture updated');
    } catch (err) {
      alert(err.response?.data?.message || 'Profile picture upload failed');
    } finally {
      setProfileBusy(false);
      event.target.value = '';
    }
  };

  const updateOwnCredentials = async (event) => {
    event.preventDefault();
    setProfileBusy(true);
    try {
      const res = await axios.patch(`${API_URL}/api/profile/credentials`, {
        username: credentialsForm.username,
        mobile: credentialsForm.mobile,
        new_password: credentialsForm.new_password
      }, { headers: getHeaders() });
      if (res.data.token) {
        localStorage.setItem('token', res.data.token);
      }
      if (res.data.user) {
        updateSessionUser(res.data.user);
      }
      setCredentialsForm({ username: res.data.user?.username || credentialsForm.username, mobile: res.data.user?.mobile || credentialsForm.mobile, new_password: '' });
      alert('Profile updated successfully');
    } catch (err) {
      alert(err.response?.data?.message || 'Profile update failed');
    } finally {
      setProfileBusy(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    const userName = user.name || 'User';
    const roleName = displayRole(userRole, userRole) || 'Dashboard';
    const brandTitle = user.brand_name || (hasHostBrand ? user.brand_name : userName);
    const pageTitle = userRole === 'superadmin'
      ? `${userName} | Super Admin`
      : userRole === 'whitelabel_admin'
        ? `${brandTitle || userName} | White Label Admin`
        : `${userName} | ${roleName}`;

    setBrowserBranding({
      title: pageTitle,
      icon: activeFaviconSrc,
      fallbackText: userName || brandTitle,
      themeColor: user.brand_primary_color || (theme === 'dark' ? '#07111f' : '#0b66ae')
    });
  }, [activeFaviconSrc, hasHostBrand, theme, user, userRole]);

  const BackBtn = ({ label = 'Back' }) => (
    <ShellButton className="back-button" onClick={() => openPage('dashboard')}>
      <ChevronRight size={16} /> {label}
    </ShellButton>
  );

  const renderAdminDashboard = () => (
    <motion.div {...pageMotion}>
      <div className="stat-grid">
        <StatCard label="Total Sales" value={stats.totalSales} color="#3b82f6" icon={ShoppingCart} />
        <StatCard label="CRM Customers" value={stats.totalCustomers} color="#0ea5e9" icon={Users} />
        <StatCard label="Total Revenue" value={formatMoney(stats.totalRevenue)} color="#10b981" icon={BarChart3} />
        <StatCard label="Total Installation" value={formatMoney(stats.totalInstallation)} color="#f97316" icon={ClipboardList} />
        <StatCard label="Today's Sales" value={stats.todaySales} color="#06b6d4" icon={Activity} />
        <StatCard label="Paid Amount" value={formatMoney(stats.paidAmount)} color="#8b5cf6" icon={WalletCards} />
        <StatCard label="Pending Amount" value={formatMoney(stats.pendingPayments)} color="#ef4444" icon={CreditCard} />
      </div>
    </motion.div>
  );

  const renderContent = () => {
    if (user?.role === 'superadmin' && activePage === 'dashboard' && !selectedCompany) {
      return (
        <motion.div {...pageMotion}>
          <section className="content-card glass-panel">
            <SuperAdminDashboard onSelectCompany={(company) => {
              setSelectedCompany(company);
              setActiveSubmenu('');
              setActivePage('company-detail');
            }} />
          </section>
        </motion.div>
      );
    }

    if (user?.role === 'superadmin' && activePage === 'company-detail' && selectedCompany) {
      return (
        <motion.div {...pageMotion}>
          <BackBtn label="Back to Accounts" />
          <section className="company-detail glass-panel">
            <div>
              <span className="eyebrow"><Building2 size={14} /> Company detail</span>
              <h2>{selectedCompany.company_name}</h2>
              <p>Workspace performance overview</p>
            </div>
            <strong>{selectedCompany.total_sales || 0}</strong>
          </section>
          <div className="stat-grid">
            {[
              { label: 'Total Customers', value: selectedCompany.total_customers, color: '#2563eb', icon: Users },
              { label: 'Total Revenue', value: formatMoney(selectedCompany.total_revenue), color: '#10b981', icon: BarChart3 },
              { label: 'Pending Amount', value: formatMoney(selectedCompany.pending_amount), color: '#7c3aed', icon: CreditCard },
              { label: 'Renewals Due', value: selectedCompany.renewals_due, color: '#06b6d4', icon: RefreshCw },
              { label: 'Total Sales', value: selectedCompany.total_sales, color: '#4f46e5', icon: ShoppingCart },
              { label: 'Device Sales', value: selectedCompany.device_sales || 0, color: '#0b66ae', icon: ShoppingCart },
              { label: 'Service Sales', value: selectedCompany.service_sales || 0, color: '#f59e0b', icon: ReceiptText },
              { label: "Today's Sales", value: selectedCompany.today_sales, color: '#0891b2', icon: Activity },
              { label: 'Employees', value: selectedCompany.total_employees, color: '#0f766e', icon: Users },
              { label: 'Dealers', value: selectedCompany.total_dealers, color: '#d97706', icon: Building2 },
            ].map((card) => <StatCard key={card.label} {...card} />)}
          </div>
        </motion.div>
      );
    }

    if (activePage === 'dashboard' && isCompanyAdmin(user?.role)) return renderAdminDashboard();
    if (activePage === 'dashboard' && user?.role === 'dealer') return <motion.div {...pageMotion}><section className="content-card glass-panel"><DealerPanel /></section></motion.div>;
    if (activePage === 'dashboard' && user?.role === 'employee') return <motion.div {...pageMotion}><section className="content-card glass-panel"><EmployeePanel /></section></motion.div>;
    if (user?.role === 'customer') return <CustomerPanel />;

    if (activePage === 'sales') return (
      <motion.div {...pageMotion}>
        <BackBtn />
        <section className="content-card glass-panel">
          <Sales salesView={activeSubmenu || 'device-sale'} />
        </section>
      </motion.div>
    );
    if (activePage === 'gps-tracking') return <GPSTrackingCenter user={user} gpsView={activeSubmenu} />;
    if (activePage === 'inventory') return <motion.div {...pageMotion}><section className="content-card glass-panel"><Inventory inventoryView={activeSubmenu || 'inventory-dashboard'} /></section></motion.div>;
    if (activePage === 'purchase') return <><BackBtn /><PurchaseCenter purchaseView={activeSubmenu} /></>;
    if (activePage === 'reminders') return <><BackBtn /><ReminderCenter reminderView={activeSubmenu} onOpenWhatsAppPanel={() => openPage('whatsapp')} /></>;
    if (activePage === 'vehicle-services') return <motion.div {...pageMotion}><BackBtn /><section className="content-card glass-panel"><Services serviceView={activeSubmenu} onOpenServiceSale={() => openPage('sales', 'service-sale')} /></section></motion.div>;
    if (activePage === 'accounts') return <><BackBtn /><AccountSection accountView={activeSubmenu} onOpenPayments={() => openPage('payments')} /></>;
    if (activePage === 'payments') return <motion.div {...pageMotion}><BackBtn /><section className="content-card glass-panel"><Payments paymentView={activeSubmenu} /></section></motion.div>;
    if (activePage === 'renewals') return <motion.div {...pageMotion}><BackBtn /><section className="content-card glass-panel"><Renewals renewalView={activeSubmenu} /></section></motion.div>;
    if (activePage === 'reports') return <motion.div {...pageMotion}><BackBtn /><section className="content-card glass-panel"><Reports initialReport={activeSubmenu} onSelectReport={(key) => openPage('reports', key)} hiddenReportTypes={menuVisibility.hidden_report_types || []} /></section></motion.div>;
    if (activePage === 'activity-history') return <motion.div {...pageMotion}><BackBtn /><section className="content-card glass-panel"><ActivityHistory activityView={activeSubmenu} /></section></motion.div>;
    if (activePage === 'delete-history') return <motion.div {...pageMotion}><BackBtn /><section className="content-card glass-panel"><DeleteHistory deleteView={activeSubmenu} /></section></motion.div>;
    if (activePage === 'correction-requests') return <motion.div {...pageMotion}><BackBtn /><section className="content-card glass-panel"><CorrectionRequests /></section></motion.div>;
    if (activePage === 'menu-permissions') return <motion.div {...pageMotion}><BackBtn /><section className="content-card glass-panel"><MenuPermissions permissionView={activeSubmenu} /></section></motion.div>;
    if (activePage === 'users') return <motion.div {...pageMotion}><section className="content-card glass-panel"><UserManagement userView={activeSubmenu} /></section></motion.div>;
    if (activePage === 'notifications') return <motion.div {...pageMotion}><BackBtn /><section className="content-card glass-panel"><NotificationManagement notificationView={activeSubmenu} /></section></motion.div>;
    if (activePage === 'whatsapp') return <><BackBtn /><WhatsAppConfig user={user} whatsAppView={activeSubmenu} /></>;
    if (activePage === 'team-chat') return <><BackBtn /><TeamChatCenter user={user} teamChatView={activeSubmenu} /></>;
    if (activePage === 'user-chatbot') return <><BackBtn /><UserChatbotCenter /></>;
    if (activePage === 'white-label') return <><BackBtn /><WhiteLabelBuilder user={user} whiteLabelView={activeSubmenu} onBrandUpdate={updateSessionUser} /></>;
    if (activePage === 'support') return <><BackBtn /><SupportCenter user={user} supportView={activeSubmenu} /></>;
    if (activePage === 'system-deploy') return <><BackBtn /><SystemDeploymentCenter user={user} /></>;
    if (activePage === 'system-master-logs') return <motion.div {...pageMotion}><BackBtn /><section className="content-card glass-panel"><MasterLogs masterLogView={activeSubmenu} /></section></motion.div>;
    if (featureDetails[activePage]) return <FeaturePage pageKey={activePage} />;

    if (user?.role === 'customer') {
      return (
        <CustomerPanel
          selectedVehicleIdProp={activeSubmenu}
          onSelectVehicleFromPanel={(id) => setActiveSubmenu(String(id))}
        />
      );
    }
    return renderAdminDashboard();
  };

  return (
    <div
      className={`premium-crm-shell ${theme === 'dark' ? 'dark-mode' : 'light-mode'} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}
      style={{ '--brand-primary': shellBrandPrimary, '--brand-secondary': shellBrandSecondary }}
    >
      <Sidebar
        modules={availableModules}
        activePage={activePage}
        activeSubmenu={activeSubmenu}
        onOpen={openPage}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        user={user}
        brandLogoSrc={brandLogoSrc}
        isCollapsed={sidebarCollapsed}
        customerVehicles={customerVehicles}
        onOpenCustomerProfile={(tab = 'profile') => {
          setCustomerProfileActiveTab(tab);
          setCustomerProfileModalOpen(true);
        }}
        onToggleCollapse={() => setSidebarCollapsed(prev => {
          const val = !prev;
          localStorage.setItem('credyou-sidebar-collapsed', String(val));
          return val;
        })}
      />

      <main className="crm-main">
        <header className="topbar">
          <div className="topbar-left">
            <IconButton className="mobile-menu" onClick={() => setSidebarOpen(true)} aria-label="Open menu"><Menu size={18} /></IconButton>
            <div className="topbar-title-block">
              <span>{user?.role === 'customer' ? 'Customer Panel' : (activePage === 'dashboard' ? 'Dashboard' : topbarModule.title || 'Workspace')}</span>
              <h1>{topbarTitle}</h1>
              <button
                type="button"
                className={`live-sync-pill ${liveSync.status} ${pendingLiveRefresh ? 'actionable' : ''}`}
                onClick={pendingLiveRefresh ? applyLiveRefresh : undefined}
                disabled={!pendingLiveRefresh}
                title={pendingLiveRefresh ? 'Apply pending live update' : 'Realtime connection status'}
              >
                <i />
                {liveSync.label}
              </button>
            </div>
          </div>

          <div className="global-search">
            <Search size={18} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={user?.role === 'customer' ? 'Search your devices and services...' : 'Search modules, customers, devices, payments, reports...'}
            />
          </div>

          <div className="topbar-actions">
            <NotificationCenter />
            <IconButton onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} aria-label="Toggle theme">
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </IconButton>
            {isImpersonating && (
              <ShellButton className="soft impersonation-button" onClick={returnToImpersonator}>
                Back to {returnUser?.name || user?.impersonated_by_name || 'Admin'}
              </ShellButton>
            )}
            <div className="profile-menu-root">
              <button type="button" className={`user-chip ${canEditOwnProfile ? 'clickable' : ''}`} onClick={() => canEditOwnProfile && setProfilePanelOpen((open) => !open)}>
                <span>
                  {profileImageSrc ? (
                    <img src={profileImageSrc} alt={user?.name || 'User'} />
                  ) : (
                    user?.name?.charAt(0)?.toUpperCase() || 'U'
                  )}
                </span>
                <div>
                  <strong>{user?.name || 'User'}</strong>
                  <small>{[displayRole(user?.role, user?.role), user?.mobile].filter(Boolean).join(' - ')}</small>
                </div>
              </button>
              {canEditOwnProfile && profilePanelOpen && (
                <div className="profile-panel glass-panel">
                  <div className="profile-panel-head">
                    <span>
                      {ownProfileImageSrc ? <img src={ownProfileImageSrc} alt={user?.name || 'Admin'} /> : user?.name?.charAt(0)?.toUpperCase() || 'A'}
                    </span>
                    <div>
                      <strong>{user?.name || 'Admin'}</strong>
                      <small>Admin profile</small>
                    </div>
                  </div>
                  <label className="profile-upload-button">
                    <Upload size={15} />
                    Upload Profile Picture
                    <input type="file" accept="image/*" onChange={uploadOwnProfilePicture} disabled={profileBusy} />
                  </label>
                  <form onSubmit={updateOwnCredentials} className="profile-password-form">
                    <label>Username<input type="text" value={credentialsForm.username} onChange={(event) => setCredentialsForm((current) => ({ ...current, username: event.target.value }))} required /></label>
                    <label>Mobile Number<input type="text" value={credentialsForm.mobile} onChange={(event) => setCredentialsForm((current) => ({ ...current, mobile: event.target.value }))} required /></label>
                    <label>New Password <small>(optional)</small><input type="password" value={credentialsForm.new_password} onChange={(event) => setCredentialsForm((current) => ({ ...current, new_password: event.target.value }))} minLength={6} placeholder="Leave blank to keep current" /></label>
                    <ShellButton type="submit" disabled={profileBusy}><Lock size={15} /> {profileBusy ? 'Saving...' : 'Update Credentials'}</ShellButton>
                  </form>
                </div>
              )}
            </div>
            <IconButton className="danger" onClick={logout} aria-label="Logout"><LogOut size={18} /></IconButton>
          </div>
        </header>

        <div className="content-wrap">
          <AnimatePresence mode="wait">
            <div className="content-page-slot" key={`${activePage}-${selectedCompany?.admin_id || 'none'}-${liveRefreshKey}`}>
              {renderContent()}
            </div>
          </AnimatePresence>
        </div>
      </main>

      <LicenseFooter className="crm-footer" />

      {!['customer', 'dealer'].includes(user?.role) && (
        <RenewalDailyPopup
          open={renewalPopup.open}
          items={renewalPopup.items}
          onDismiss={closeRenewalPopup}
          onOpenDetails={openRenewalDetails}
        />
      )}
      {user?.role !== 'customer' && <FloatingAssistant />}

      {/* CUSTOMER PROFILE & DRIVERS EDIT MODAL */}
      {customerProfileModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.7)',
            backdropFilter: 'blur(8px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setCustomerProfileModalOpen(false);
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '520px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
              overflow: 'hidden',
              border: '1px solid #cbd5e1'
            }}
          >
            {/* Modal Header */}
            <div style={{
              padding: '16px 20px',
              background: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '38px',
                  height: '38px',
                  borderRadius: '10px',
                  background: '#e0f2fe',
                  color: '#0284c7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <User size={20} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 800, color: '#0f172a' }}>
                    My Profile & Vehicle Drivers
                  </h3>
                  <p style={{ margin: '2px 0 0', fontSize: '11.5px', color: '#64748b' }}>
                    Manage profile picture, personal details & vehicle drivers
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCustomerProfileModalOpen(false)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: '#94a3b8',
                  padding: '4px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Tabs */}
            <div style={{
              display: 'flex',
              background: '#f1f5f9',
              padding: '4px 6px',
              gap: '4px',
              borderBottom: '1px solid #e2e8f0',
              flexShrink: 0
            }}>
              <button
                type="button"
                onClick={() => setCustomerProfileActiveTab('profile')}
                style={{
                  flex: 1,
                  padding: '8px 8px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  background: customerProfileActiveTab === 'profile' ? '#ffffff' : 'transparent',
                  color: customerProfileActiveTab === 'profile' ? '#0284c7' : '#64748b',
                  boxShadow: customerProfileActiveTab === 'profile' ? '0 2px 5px rgba(0,0,0,0.06)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '5px',
                  transition: 'all 0.15s ease'
                }}
              >
                <User size={13} /> Personal Details
              </button>
              <button
                type="button"
                onClick={() => setCustomerProfileActiveTab('drivers')}
                style={{
                  flex: 1,
                  padding: '8px 8px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  background: customerProfileActiveTab === 'drivers' ? '#ffffff' : 'transparent',
                  color: customerProfileActiveTab === 'drivers' ? '#0284c7' : '#64748b',
                  boxShadow: customerProfileActiveTab === 'drivers' ? '0 2px 5px rgba(0,0,0,0.06)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '5px',
                  transition: 'all 0.15s ease'
                }}
              >
                <Car size={13} /> Drivers ({customerVehiclesDrivers.length})
              </button>
              <button
                type="button"
                onClick={() => setCustomerProfileActiveTab('logs')}
                style={{
                  flex: 1,
                  padding: '8px 8px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  background: customerProfileActiveTab === 'logs' ? '#ffffff' : 'transparent',
                  color: customerProfileActiveTab === 'logs' ? '#0284c7' : '#64748b',
                  boxShadow: customerProfileActiveTab === 'logs' ? '0 2px 5px rgba(0,0,0,0.06)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '5px',
                  transition: 'all 0.15s ease'
                }}
              >
                <Clock size={13} /> Assignment Log
              </button>
            </div>

            {/* Modal Body / Form */}
            <form onSubmit={handleUpdateCustomerProfile} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
                {customerProfileMsg && (
                  <div style={{
                    marginBottom: '14px',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    fontSize: '12.5px',
                    fontWeight: 700,
                    background: customerProfileMsg.type === 'success' ? '#dcfce7' : '#fee2e2',
                    color: customerProfileMsg.type === 'success' ? '#15803d' : '#b91c1c',
                    border: `1px solid ${customerProfileMsg.type === 'success' ? '#bbf7d0' : '#fecaca'}`
                  }}>
                    {customerProfileMsg.text}
                  </div>
                )}

                {customerProfileActiveTab === 'profile' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {/* Profile Picture Upload Banner */}
                    <div style={{
                      padding: '12px 14px',
                      background: '#f8fafc',
                      borderRadius: '10px',
                      border: '1px solid #e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px'
                    }}>
                      <div style={{
                        width: '54px',
                        height: '54px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #0284c7, #0369a1)',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 800,
                        fontSize: '18px',
                        flexShrink: 0,
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(2, 132, 199, 0.3)'
                      }}>
                        {user?.profile_pic ? (
                          <img
                            src={`${API_URL}/api/profile/pic/${user.profile_pic}`}
                            alt="Profile"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <User size={24} />
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a', marginBottom: '2px' }}>
                          Profile Photo
                        </div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px' }}>
                          Upload JPG or PNG photo (Max 2MB)
                        </div>
                        <label style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '5px 10px',
                          background: '#e0f2fe',
                          color: '#0284c7',
                          borderRadius: '6px',
                          fontSize: '11.5px',
                          fontWeight: 800,
                          cursor: 'pointer',
                          border: '1px solid #bae6fd'
                        }}>
                          <Camera size={13} />
                          {customerPicUploading ? 'Uploading...' : 'Change Profile Picture'}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleUploadCustomerProfilePic}
                            disabled={customerPicUploading}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 800, color: '#334155', marginBottom: '4px' }}>
                        Full Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={customerProfileForm.name}
                        onChange={(e) => setCustomerProfileForm({ ...customerProfileForm, name: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '9px 12px',
                          borderRadius: '8px',
                          border: '1.5px solid #cbd5e1',
                          fontSize: '13px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        placeholder="Enter your full name"
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 800, color: '#334155', marginBottom: '4px' }}>
                        Registered Mobile / WhatsApp Number *
                      </label>
                      <input
                        type="text"
                        required
                        value={customerProfileForm.mobile}
                        onChange={(e) => setCustomerProfileForm({ ...customerProfileForm, mobile: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '9px 12px',
                          borderRadius: '8px',
                          border: '1.5px solid #cbd5e1',
                          fontSize: '13px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        placeholder="10-digit mobile number"
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 800, color: '#334155', marginBottom: '4px' }}>
                        Email Address (Optional)
                      </label>
                      <input
                        type="email"
                        value={customerProfileForm.email}
                        onChange={(e) => setCustomerProfileForm({ ...customerProfileForm, email: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '9px 12px',
                          borderRadius: '8px',
                          border: '1.5px solid #cbd5e1',
                          fontSize: '13px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        placeholder="name@example.com"
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 800, color: '#334155', marginBottom: '4px' }}>
                        Billing / City Address (Optional)
                      </label>
                      <input
                        type="text"
                        value={customerProfileForm.address}
                        onChange={(e) => setCustomerProfileForm({ ...customerProfileForm, address: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '9px 12px',
                          borderRadius: '8px',
                          border: '1.5px solid #cbd5e1',
                          fontSize: '13px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        placeholder="e.g. Rohini Sector 15, New Delhi"
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 800, color: '#334155', marginBottom: '4px' }}>
                        New Password <small style={{ color: '#64748b', fontWeight: 500 }}>(Leave blank to keep unchanged)</small>
                      </label>
                      <input
                        type="password"
                        minLength={6}
                        value={customerProfileForm.new_password}
                        onChange={(e) => setCustomerProfileForm({ ...customerProfileForm, new_password: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '9px 12px',
                          borderRadius: '8px',
                          border: '1.5px solid #cbd5e1',
                          fontSize: '13px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        placeholder="Min 6 characters"
                      />
                    </div>
                  </div>
                )}

                {customerProfileActiveTab === 'drivers' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ fontSize: '12px', color: '#475569', background: '#f8fafc', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      Assign or change the driver details (Name & Phone) for each of your registered vehicles below:
                    </div>

                    {customerVehiclesDrivers.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '30px 10px', color: '#94a3b8', fontSize: '13px' }}>
                        No vehicles registered under this account.
                      </div>
                    ) : (
                      customerVehiclesDrivers.map((v, idx) => (
                        <div
                          key={v.id || idx}
                          style={{
                            padding: '12px 14px',
                            background: '#ffffff',
                            borderRadius: '10px',
                            border: '1.5px solid #e2e8f0',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.03)'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{
                                padding: '3px 8px',
                                background: '#1e293b',
                                color: '#ffffff',
                                borderRadius: '5px',
                                fontSize: '12px',
                                fontWeight: 800,
                                letterSpacing: '0.4px'
                              }}>
                                {v.vehicle_no || 'Vehicle'}
                              </span>
                            </div>
                            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>
                              {v.device_model} • IMEI: {v.imei}
                            </span>
                          </div>

                          <div>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#334155', marginBottom: '4px' }}>
                              Assigned Driver (Name & Mobile)
                            </label>
                            <input
                              type="text"
                              value={v.driver_name || ''}
                              onChange={(e) => handleUpdateVehicleDriver(v.id, e.target.value)}
                              placeholder="e.g. Suresh Kumar (9876543210)"
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                borderRadius: '8px',
                                border: '1.5px solid #cbd5e1',
                                fontSize: '12.5px',
                                outline: 'none',
                                boxSizing: 'border-box'
                              }}
                            />
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {customerProfileActiveTab === 'logs' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '12px',
                      color: '#475569',
                      background: '#f8fafc',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <span>Driver Assignment Timeline:</span>
                      <button
                        type="button"
                        onClick={fetchDriverLogs}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#0284c7',
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontSize: '11.5px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px'
                        }}
                      >
                        <RefreshCw size={11} /> Refresh
                      </button>
                    </div>

                    {driverLogsLoading ? (
                      <div style={{ textAlign: 'center', padding: '30px 10px', color: '#64748b', fontSize: '12.5px' }}>
                        Loading driver assignment logs...
                      </div>
                    ) : driverLogs.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '30px 10px', color: '#94a3b8', fontSize: '13px' }}>
                        No driver assignments recorded yet.
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {driverLogs.map((log, idx) => (
                          <div
                            key={log.id || idx}
                            style={{
                              padding: '10px 12px',
                              background: '#ffffff',
                              borderRadius: '8px',
                              border: '1.5px solid #e2e8f0',
                              boxShadow: '0 2px 5px rgba(0,0,0,0.02)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '10px'
                            }}
                          >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{
                                  padding: '2px 6px',
                                  background: '#1e293b',
                                  color: '#ffffff',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: 800
                                }}>
                                  {log.vehicle_no}
                                </span>
                                <strong style={{ fontSize: '12.5px', color: '#0f172a' }}>
                                  {log.driver_name}
                                </strong>
                              </div>
                              <span style={{ fontSize: '10.5px', color: '#64748b' }}>
                                Assigned by: <strong>{log.assigned_by || 'Customer'}</strong>
                              </span>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <span style={{
                                fontSize: '10.5px',
                                fontWeight: 700,
                                color: '#0284c7',
                                background: '#f0f9ff',
                                padding: '3px 7px',
                                borderRadius: '5px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px'
                              }}>
                                <Clock size={10} />
                                {new Date(log.assigned_at).toLocaleString('en-IN', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true
                                })}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div style={{
                display: 'flex',
                gap: '10px',
                padding: '14px 20px',
                background: '#f8fafc',
                borderTop: '1px solid #e2e8f0',
                flexShrink: 0
              }}>
                <button
                  type="button"
                  onClick={() => setCustomerProfileModalOpen(false)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#475569',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={customerProfileBusy}
                  style={{
                    flex: 2,
                    padding: '10px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #0284c7, #0369a1)',
                    color: '#ffffff',
                    fontSize: '13px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(2, 132, 199, 0.3)'
                  }}
                >
                  {customerProfileBusy ? 'Saving...' : 'Save Profile & Drivers'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
